import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../../services/axios';
import CeipalImportModal from '../../components/custom/CeipalImportModal';
import ATSImportModal from '../../components/custom/ATSImportModal';
import CandidateReportModal from '../../components/custom/CandidateReportModal';
import GlobalPagination from '../../components/ui/GlobalPagination';
import { formatPhoneCompact, normalizePhoneNumber, arePhoneNumbersEquivalent, isValidPhoneNumber } from '../../utils/phoneUtils';
import { scoreColor, buildSkillScoreBreakdown, getDefaultSkillWeightagesForTitle } from '../../utils/stackRankingScores';
import { formatAppDate, formatAppDateTime } from '../../utils/dateFormat';
import { formatPositionStatusLabel } from '../../utils/positionStatus';
import { formatJdPreviewHtml, JD_PREVIEW_CONTENT_STYLE, JD_PREVIEW_INNER_CSS } from '../../utils/jdPreviewFormat';
import { findJdById, getJdSourceTypeLabel } from '../../utils/jdSourceType';
import JdPreview from '../Dashboard/JdPreview';
import {
  getRoleSkills,
  roleSkillsToWeightages,
  resolvePositionJobFamilyKey,
  setPositionJobFamilyKey,
  getPositionJobFamilyKey,
  isValidRoleKey,
  syncJobFamilySkillsToJd,
  ensurePositionSkillWeightages,
  setJobFamilyAiEnabled,
} from '../../data/jobFamilyMaster';
import { JobFamilyTripleSelect } from '../../components/custom/JobFamilyPanel';
import RecruiterSsoDropdown from '../../components/custom/RecruiterSsoDropdown';
import { uniqueResumeAttachers, attachResumeCreatedBy, matchesSsoRecruiterSet } from '../../utils/recruiterSsoFilter';
import { useAuth } from '../../context/AuthContext';
import { usePositionAccess } from '../../context/PermissionsContext';
import { AI_RANKING_OFF_MESSAGE, isAiRankingEnabled } from '../../utils/entitlements';

/** Grammatically correct upload success (avoid static "resume(s)" for all counts). */
function resumeFileUploadSuccessMessage(fileCount) {
  const n = Number(fileCount);
  if (!Number.isFinite(n) || n < 1) return 'Upload completed.';
  if (n === 1) return '1 resume uploaded successfully';
  return `${n} resumes uploaded successfully`;
}

function uploadedResumesLabel(fileCount) {
  const n = Number(fileCount);
  if (!Number.isFinite(n) || n < 1) return 'Uploaded resumes';
  if (n === 1) return '1 uploaded resume';
  return `${n} uploaded resumes`;
}

function resumeCountLabel(n) {
  const count = Number(n);
  if (!Number.isFinite(count)) return '0 resumes';
  return `${count} ${count === 1 ? 'resume' : 'resumes'}`;
}

/** Remove duplicate ranking rows for the same resume (e.g. when cached results are merged twice). */
function dedupeRankingCandidatesByResumeId(candidates) {
  const seen = new Set();
  return candidates.filter((candidate) => {
    const resumeId = candidate.resume_id ?? candidate.resumeId;
    if (resumeId == null || resumeId === '') return true;
    const key = String(resumeId);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

/** e.g. "1 resume was skipped…" / "2 resumes were skipped…" */
function skippedResumesBannerTitle(count) {
  const n = Number(count);
  if (!Number.isFinite(n) || n < 1) return 'Some resumes were skipped during analysis';
  if (n === 1) return '1 resume was skipped during analysis';
  return `${n} resumes were skipped during analysis`;
}

/** Load persisted JD skill weightages without triggering AI/job-family initialization. */
async function loadSavedJdSkillWeightages(apiClient, jdId) {
  if (!jdId) return null;
  try {
    const response = await apiClient.get(`/stack-ranking/skill-weightage/jd/${jdId}`, {
      params: { initialize: false },
    });
    const params = response.data?.scoring_parameters;
    if (!Array.isArray(params) || params.length === 0) return null;
    return normalizeSkillWeightsTo100(
      params
        .map((p) => ({
          skillName: String(p.parameter || p.skillName || '').trim(),
          weightage: Math.max(0, Number(p.weight ?? p.weightage) || 0),
        }))
        .filter((skill) => skill.skillName)
    );
  } catch (error) {
    console.warn('Could not load saved JD skill weightages:', error);
    return null;
  }
}

/** Keep the current on-screen skill order when the API returns scoring_parameters (often A–Z). */
function applyScoringParametersPreservingOrder(currentSkills, params) {
  const incoming = Array.isArray(params) ? params : [];
  const byName = new Map();
  incoming.forEach((p) => {
    const name = String(p.parameter || p.skillName || '').trim();
    if (!name) return;
    byName.set(name.toLowerCase(), {
      skillName: name,
      weightage: Math.max(0, Number(p.weight ?? p.weightage) || 0),
    });
  });
  const seen = new Set();
  const kept = [];
  (currentSkills || []).forEach((s) => {
    const key = String(s.skillName || '').toLowerCase();
    if (!byName.has(key)) return;
    kept.push({ ...s, ...byName.get(key) });
    seen.add(key);
  });
  incoming.forEach((p) => {
    const name = String(p.parameter || p.skillName || '').trim();
    const key = name.toLowerCase();
    if (!name || seen.has(key)) return;
    kept.push({
      skillName: name,
      weightage: Math.max(0, Number(p.weight ?? p.weightage) || 0),
    });
    seen.add(key);
  });
  return normalizeSkillWeightsTo100(kept);
}

/**
 * JD / API skill rows sometimes sum to e.g. 90% after AI extraction.
 * Scale proportionally to integer weights that total exactly 100 (largest-remainder).
 */
function normalizeSkillWeightsTo100(skills) {
  if (!Array.isArray(skills) || skills.length === 0) return skills;
  const raw = skills.map((s) => Math.max(0, Number(s.weightage) || 0));
  const sum = raw.reduce((a, b) => a + b, 0);
  if (sum <= 0) return skills.map((s, i) => ({ ...s, weightage: raw[i] }));
  if (Math.abs(sum - 100) < 0.01) {
    return skills.map((s, i) => ({ ...s, weightage: raw[i] }));
  }
  const scaled = raw.map((v) => (v * 100) / sum);
  const floors = scaled.map((x) => Math.floor(x));
  let rem = 100 - floors.reduce((a, b) => a + b, 0);
  const fracIdx = scaled
    .map((x, i) => ({ i, f: x - Math.floor(x) }))
    .sort((a, b) => b.f - a.f);
  const outWeights = [...floors];
  for (let k = 0; k < rem; k++) {
    outWeights[fracIdx[k % fracIdx.length].i] += 1;
  }
  return skills.map((s, i) => ({ ...s, skillName: s.skillName, weightage: outWeights[i] }));
}

/* ── Primary button tokens (match app-wide Feuji blue gradient) ─────────── */
const FEUJI_GRAD = 'linear-gradient(135deg,#0041C2,#1A5FD4)';
const FEUJI_GRAD_HOVER = 'linear-gradient(135deg,#003399,#0041C2)';
const FEUJI_BLUE = '#1A5FD4';

function primaryBtnStyle(extra = {}) {
  return {
    background: FEUJI_GRAD,
    border: 'none',
    color: '#fff',
    fontWeight: 600,
    transition: 'all 0.18s ease',
    boxShadow: '0 2px 8px rgba(0,65,194,0.25)',
    ...extra,
  };
}

function primaryBtnHoverHandlers(enabled = true) {
  if (!enabled) return {};
  return {
    onMouseEnter: (e) => {
      e.currentTarget.style.background = FEUJI_GRAD_HOVER;
      e.currentTarget.style.boxShadow = '0 4px 14px rgba(0,65,194,0.4)';
      e.currentTarget.style.transform = 'translateY(-1px)';
    },
    onMouseLeave: (e) => {
      e.currentTarget.style.background = FEUJI_GRAD;
      e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,65,194,0.25)';
      e.currentTarget.style.transform = 'translateY(0)';
    },
  };
}

function outlineBlueBtnHoverHandlers(enabled = true) {
  if (!enabled) return {};
  return {
    onMouseEnter: (e) => {
      e.currentTarget.style.background = 'rgba(0,65,194,0.08)';
      e.currentTarget.style.borderColor = FEUJI_BLUE;
      e.currentTarget.style.color = FEUJI_BLUE;
    },
    onMouseLeave: (e) => {
      e.currentTarget.style.background = 'transparent';
      e.currentTarget.style.borderColor = FEUJI_BLUE;
      e.currentTarget.style.color = FEUJI_BLUE;
    },
  };
}

function infoCardHoverHandlers(enabled = true) {
  if (!enabled) return {};
  return {
    onMouseEnter: (e) => {
      e.currentTarget.style.borderColor = FEUJI_BLUE;
      e.currentTarget.style.boxShadow = '0 4px 14px rgba(0,65,194,0.14)';
      e.currentTarget.style.transform = 'translateY(-2px)';
    },
    onMouseLeave: (e) => {
      e.currentTarget.style.borderColor = 'var(--ds-border)';
      e.currentTarget.style.boxShadow = 'none';
      e.currentTarget.style.transform = 'translateY(0)';
    },
  };
}

function infoCardStyle(enabled = true) {
  return {
    flex: '1 1 200px',
    display: 'flex',
    alignItems: 'center',
    padding: 12,
    background: 'var(--ds-surface)',
    borderRadius: 8,
    border: '1px solid var(--ds-border)',
    minHeight: 90,
    textAlign: 'left',
    fontFamily: 'inherit',
    cursor: enabled ? 'pointer' : 'not-allowed',
    opacity: enabled ? 1 : 0.55,
    transition: 'all 0.18s ease',
    color: 'inherit',
    width: 'auto',
  };
}

function HeaderInfoPopup({ title, icon, onClose, children, maxWidth = 520 }) {
  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1050, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ background: 'var(--ds-card)', borderRadius: 12, width: '100%', maxWidth, maxHeight: '85vh', boxShadow: 'var(--ds-shadow-md)', display: 'flex', flexDirection: 'column' }}
      >
        <div style={{ background: 'linear-gradient(135deg,#0041C2,#1A5FD4)', borderRadius: '12px 12px 0 0', padding: '14px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {icon ? <i className={`fas ${icon}`} style={{ color: 'white' }}></i> : null}
            <span style={{ fontWeight: 700, color: 'white', fontSize: 14 }}>{title}</span>
          </div>
          <button type="button" onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.85)', fontSize: 20, lineHeight: 1 }} aria-label="Close">×</button>
        </div>
        <div style={{ padding: '16px 20px', overflowY: 'auto', flex: 1, minHeight: 0 }}>{children}</div>
        <div style={{ padding: '12px 20px', borderTop: '1px solid var(--ds-border)', display: 'flex', justifyContent: 'flex-end', flexShrink: 0 }}>
          <button type="button" onClick={onClose} style={{ borderRadius: 6, padding: '6px 14px', border: '1px solid var(--ds-border)', background: 'transparent', color: 'var(--ds-text)', cursor: 'pointer', fontSize: 13 }}>Close</button>
        </div>
      </div>
    </div>
  );
}

function popupDetailRow(label, value) {
  return (
    <div key={label} style={{ display: 'flex', justifyContent: 'space-between', gap: 16, padding: '8px 0', borderBottom: '1px solid var(--ds-border)' }}>
      <span style={{ fontSize: 12, color: 'var(--ds-muted)', flexShrink: 0 }}>{label}</span>
      <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--ds-text)', textAlign: 'right' }}>{value || '—'}</span>
    </div>
  );
}

function isPlaceholderEmailValue(raw) {
  const t = String(raw || '').trim().toLowerCase();
  if (!t) return true;
  return (
    t === 'not specified' ||
    t === 'not provided' ||
    t === 'n/a' ||
    t === 'none' ||
    t === 'unknown' ||
    t === 'notprovided@example.com' ||
    t === 'not extracted' ||
    t === 'not processed' ||
    t === 'not analyzed'
  );
}

function extractEmailFromTextBlob(blob) {
  if (!blob || typeof blob !== 'string') return '';
  const m = blob.match(/\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}\b/);
  return m ? m[0] : '';
}

/** Prefer structured fields, then scan AI analysis / match summary text for an address. */
function resolveCandidateEmail(candidate, aiAnalysisData) {
  const direct = [
    candidate?.email,
    candidate?.candidate_email,
    candidate?.candidateEmail,
    aiAnalysisData?.email,
    aiAnalysisData?.Email,
    aiAnalysisData?.contact_email,
    aiAnalysisData?.contactEmail,
  ].find((e) => e && typeof e === 'string' && e.includes('@') && !isPlaceholderEmailValue(e));
  if (direct) return String(direct).trim();

  const textSources = [
    typeof candidate?.ai_analysis === 'string' ? candidate.ai_analysis : '',
    typeof candidate?.aiAnalysis === 'string' ? candidate.aiAnalysis : '',
    typeof candidate?.match_summary === 'string' ? candidate.match_summary : '',
    typeof candidate?.matchSummary === 'string' ? candidate.matchSummary : '',
  ];
  for (const part of textSources) {
    const e = extractEmailFromTextBlob(part);
    if (e && !isPlaceholderEmailValue(e)) return e;
  }
  try {
    const s = JSON.stringify(aiAnalysisData || {});
    const e = extractEmailFromTextBlob(s);
    if (e && !isPlaceholderEmailValue(e)) return e;
  } catch (e) {
    /* ignore */
  }
  return '';
}

function candidateDisplayEmail(candidate, aiAnalysisData) {
  const resolved = resolveCandidateEmail(candidate, aiAnalysisData);
  const raw =
    resolved ||
    candidate?.email ||
    candidate?.candidateEmail ||
    candidate?.candidate_email ||
    '';
  if (!isPlaceholderEmailValue(raw) && String(raw).includes('@')) return String(raw).trim();
  return 'N/A';
}

function parseStepQuery(value) {
  const v = String(value || '').toLowerCase();
  if (v === 'results' || v === '3') return 3;
  if (v === 'upload' || v === '2') return 2;
  if (v === 'skills' || v === '1') return 1;
  return 0;
}

function stepQueryValue(stepNumber) {
  if (stepNumber === 3) return 'results';
  if (stepNumber === 2) return 'upload';
  return 'skills';
}

function readSavedWizardStep(positionId) {
  if (!positionId) return 0;
  try {
    return parseStepQuery(sessionStorage.getItem(`sr_wizard_step_${positionId}`));
  } catch {
    return 0;
  }
}

function writeSavedWizardStep(positionId, stepNumber) {
  if (!positionId || !stepNumber) return;
  try {
    sessionStorage.setItem(`sr_wizard_step_${positionId}`, String(stepNumber));
  } catch { /* ignore */ }
}

function loggedInRecruiter(user) {
  const email = String(user?.email || '').trim();
  const name = [user?.firstName, user?.lastName].filter(Boolean).join(' ').trim() || email;
  return { name, email };
}

const StackRanking = () => {
  const { user, entitlements, unrestricted } = useAuth();
  // Stack Ranking off = no AI scoring; resumes still list with basic details so outreach continues.
  const aiRankingOn = isAiRankingEnabled(entitlements, { unrestricted });
  // Job family / skill weightage suggestions here follow Stack Ranking's AI switch.
  setJobFamilyAiEnabled(aiRankingOn);
  const { positionId } = useParams();
  const positionAccess = usePositionAccess(positionId);
  const canUploadResumes = positionAccess.can('resume.upload');
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const isProduction = process.env.NODE_ENV === 'production';
  const debugLog = (...args) => {
    if (!isProduction) console.log(...args);
  };

  // Prevent repeating the stack ranking completion toast across revisits/refreshes.
  // We reset this flag when a new ranking run starts.
  const STACK_RANKING_SUCCESS_TOAST_KEY = useMemo(
    () => `stack_ranking_success_toast_shown::position::${positionId || 'unknown'}`,
    [positionId]
  );

  // Step management
  const [currentStep, setCurrentStep] = useState(() => {
    const fromUrl = parseStepQuery(searchParams.get('step'));
    if (fromUrl) return fromUrl;
    const saved = readSavedWizardStep(positionId);
    return saved || 1;
  });
  // Set when an AI ranking run fails outright, so the unranked resumes are shown instead.
  const [aiRankingFailed, setAiRankingFailed] = useState(false);

  const goToStep = useCallback((stepNumber, { push = true } = {}) => {
    const n = Number(stepNumber);
    if (n < 1 || n > 3) return;
    setCurrentStep(n);
    writeSavedWizardStep(positionId, n);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('step', stepQueryValue(n));
      return next;
    }, { replace: !push });
  }, [positionId, setSearchParams]);

  const handleBackToPositions = useCallback(() => {
    const id = positionId ? String(positionId) : '';
    navigate(id ? `/stack-ranking?focus=${encodeURIComponent(id)}` : '/stack-ranking');
  }, [navigate, positionId]);
  const [loading, setLoading] = useState(true);

  // Data states
  const [position, setPosition] = useState(null);
  const [selectedJdId, setSelectedJdId] = useState(null);
  const [jdVersions, setJdVersions] = useState([]);
  const selectedJd = useMemo(
    () => findJdById(jdVersions, selectedJdId),
    [jdVersions, selectedJdId]
  );
  const [whatsappMessages, setWhatsappMessages] = useState([]);
  const [manualQuestions, setManualQuestions] = useState([]);
  const [manualQuestionDraft, setManualQuestionDraft] = useState('');
  const [manualQuestionType, setManualQuestionType] = useState('YES_NO');
  const [manualQuestionsSaving, setManualQuestionsSaving] = useState(false);
  const [manualQuestionsDirty, setManualQuestionsDirty] = useState(false);

  const withManualQuestionOrder = (questions) => questions.map((question, index) => ({
    ...question,
    sortOrder: index,
  }));

  useEffect(() => {
    if (!positionId) return undefined;
    let cancelled = false;
    api.get(`/positions/${positionId}/manual-screening-questions`)
      .then((response) => {
        if (cancelled) return;
        setManualQuestions(Array.isArray(response.data) ? response.data : []);
        setManualQuestionsDirty(false);
      })
      .catch(() => {
        if (cancelled) return;
        setManualQuestions([]);
        setManualQuestionsDirty(false);
      });
    return () => { cancelled = true; };
  }, [positionId]);

  const manualSaveLock = useRef(false);

  const persistManualQuestions = async (questions) => {
    if (!positionId || manualSaveLock.current) return false;
    const next = withManualQuestionOrder(questions);
    manualSaveLock.current = true;
    setManualQuestions(next);
    setManualQuestionsSaving(true);
    try {
      const response = await api.put(`/positions/${positionId}/manual-screening-questions`, next);
      setManualQuestions(Array.isArray(response.data) ? response.data : next);
      setManualQuestionsDirty(false);
      toast.success('Screening questions saved');
      return true;
    } catch (error) {
      const message = error?.response?.data?.message || 'Could not save screening questions';
      toast.error(message);
      setManualQuestionsDirty(true);
      return false;
    } finally {
      manualSaveLock.current = false;
      setManualQuestionsSaving(false);
    }
  };

  const saveManualQuestions = () => persistManualQuestions(manualQuestions);

  const addManualQuestion = () => {
    const text = manualQuestionDraft.trim();
    if (!text || manualSaveLock.current) return;
    if (/[\r\n\t]/.test(text)) {
      toast.error('Question must be a single line');
      return;
    }
    if (text.length > 200) {
      toast.error('Question must be 200 characters or fewer');
      return;
    }
    setManualQuestionDraft('');
    persistManualQuestions([
      ...manualQuestions,
      { text, enabled: true, answerType: manualQuestionType || 'YES_NO', sortOrder: manualQuestions.length },
    ]);
  };

  const updateManualQuestion = (index, patch) => {
    setManualQuestions((current) => withManualQuestionOrder(current.map((question, i) => (
      i === index ? { ...question, ...patch } : question
    ))));
    setManualQuestionsDirty(true);
  };

  const deleteManualQuestion = (index) => {
    if (manualSaveLock.current) return;
    persistManualQuestions(manualQuestions.filter((_, i) => i !== index));
  };

  const [showMessageModal, setShowMessageModal] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [skillWeightages, setSkillWeightages] = useState([]);
  const [resumes, setResumes] = useState([]);
  const [stackRankingResults, setStackRankingResults] = useState(null);

  // UI states
  const [rankingLoading, setRankingLoading] = useState(false);
  const rankingAbortRef = useRef(null);
  const processingIntervalRef = useRef(null);
  const [processingProgress, setProcessingProgress] = useState(0);
  const fileInputRef = useRef(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [showFileConfirmation, setShowFileConfirmation] = useState(false);
  const [uploadInProgress, setUploadInProgress] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [resumeToDelete, setResumeToDelete] = useState(null);
  const [selectedResumeIds, setSelectedResumeIds] = useState(new Set());
  const [bulkDeleteInProgress, setBulkDeleteInProgress] = useState(false);
  const [showBulkDeleteConfirmation, setShowBulkDeleteConfirmation] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [selectedCandidates, setSelectedCandidates] = useState([]);
  const canSendWhatsApp = selectedCandidates.length > 1
    ? positionAccess.can('whatsapp.bulk')
    : positionAccess.can('whatsapp.send');
  const whatsAppBlockedReason = positionAccess.locked
    ? 'This position is locked. Only managers and admins can send messages.'
    : (selectedCandidates.length > 1 && positionAccess.can('whatsapp.send'))
      ? 'Bulk WhatsApp sending is not allowed for you. Select one candidate at a time.'
      : 'You don\'t have permission to send WhatsApp on this position';
  const [whatsAppStatusOverrides, setWhatsAppStatusOverrides] = useState({});
  const [whatsAppSending, setWhatsAppSending] = useState(false);
  const [rankingFailures, setRankingFailures] = useState([]);
  const [whatsAppStatusFilter, setWhatsAppStatusFilter] = useState('all');
  const [selectedRecruiters, setSelectedRecruiters] = useState(() => new Set());
  const getCriteriaRedFlag = (candidate) => {
    const msg = getLatestWhatsappMessage(candidate?.mobile) || candidate?.whatsappMessage;
    return !!(candidate?.criteriaRedFlag || candidate?.criteria_red_flag
      || msg?.criteriaRedFlag || msg?.criteria_red_flag);
  };

  const normalizeToE16491 = (raw) => {
    // Use the phoneUtils for consistent normalization
    const normalized = normalizePhoneNumber(raw);
    // Return without the + prefix for API compatibility
    return normalized ? normalized.replace('+', '') : null;
  };

  const normalizePhone = (phone) => {
    // Use the phoneUtils for consistent normalization
    const normalized = normalizePhoneNumber(phone);
    // Return without the + prefix for comparison
    return normalized ? normalized.replace('+', '') : '';
  };

  const getLatestWhatsappMessage = (candidateMobile, sourceMessages = whatsappMessages) => {
    const candidatePhone = normalizePhone(candidateMobile);

    const matches = (sourceMessages || []).filter(msg => {
      const msgPhone = normalizePhone(msg.phoneNumber);
      return msgPhone === candidatePhone;
    });

    if (!matches.length) return null;

    // The "temp" row is the original campaign dispatch — it is the ONLY row that
    // accumulates the 6 answer fields (experience_years, current_location, etc.).
    // "yes2", "interested_two", "not_interested2" rows are outbound notifications
    // and have NULL answer fields even though they are also non-user_reply rows.
    const tempRows = matches.filter(msg => msg.messageType === 'temp');
    if (tempRows.length > 0) {
      return tempRows.sort(
        (a, b) => new Date(b.updatedAt || b.sentAt || 0) - new Date(a.updatedAt || a.sentAt || 0)
      )[0];
    }

    // Fallback: no 'temp' row — try any non-user_reply row
    const campaignRows = matches.filter(msg => msg.messageType !== 'user_reply');
    if (campaignRows.length > 0) {
      return campaignRows.sort(
        (a, b) => new Date(b.updatedAt || b.sentAt || 0) - new Date(a.updatedAt || a.sentAt || 0)
      )[0];
    }

    // Last resort: pick latest user_reply
    return matches.sort(
      (a, b) => new Date(b.updatedAt) - new Date(a.updatedAt)
    )[0];
  };


  const getSelectedNumbers = () => {
    const all = resultCandidates;
    const out = [];
    all.forEach((c) => {
      const id = c.resume_id ?? c.rank;
      if (selectedCandidates.includes(id)) {
        const phone = c.mobile || c.phone || c.candidate_phone || c.candidatePhone;
        const normalized = phone && isValidPhoneNumber(phone) ? normalizeToE16491(phone) : null;
        if (normalized) out.push(normalized);
      }
    });
    return out;
  };

  const getCandidateKey = (candidate) => {
    const id = candidate?.resume_id ?? candidate?.rank;
    if (id !== null && id !== undefined) return String(id);
    const phone = candidate?.mobile || candidate?.phone || candidate?.candidate_phone || candidate?.candidatePhone;
    return phone ? normalizePhone(phone) : null;
  };

  const getMissingCandidateDetails = (candidate) => {
    const missing = [];
    const phone = candidate?.mobile || candidate?.phone || candidate?.candidate_phone || candidate?.candidatePhone;
    const hasValidPhone = Boolean(phone && isValidPhoneNumber(phone));
    if (!hasValidPhone) missing.push('valid phone number');
    if (!candidate?.name || !String(candidate.name).trim()) missing.push('candidate name');
    return missing;
  };

  const formatIndiaDateTime = (value) => {
    if (!value) return 'N/A';
    const formatted = formatAppDateTime(value);
    return formatted || 'N/A';
  };

  const normalizeCandidateEmailForDisplay = (value, candidate = {}) => {
    const raw = String(value || '').trim().toLowerCase();
    const invalidValues = new Set([
      '',
      'not specified',
      'not provided',
      'not processed',
      'not extracted',
      'n/a',
      'na',
      'null',
      'none',
      'notprovided@example.com',
    ]);

    if (invalidValues.has(raw)) {
      return 'N/A';
    }

    const isValidEmail = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(raw);
    if (!isValidEmail) {
      console.warn('Invalid candidate email suppressed in Stack Ranking table:', {
        resume_id: candidate.resume_id || candidate.resumeId || candidate.id,
        candidate: candidate.name || candidate.candidateName || 'Unknown',
        email: value,
      });
      return 'N/A';
    }

    debugLog('Stack Ranking email mapped:', {
      resume_id: candidate.resume_id || candidate.resumeId || candidate.id,
      candidate: candidate.name || candidate.candidateName || 'Unknown',
      email: raw,
    });
    return raw;
  };

  // Pagination state for results table
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage, setRecordsPerPage] = useState(10);

  const [skillTotal, setSkillTotal] = useState(0);
  const [isValidTotal, setIsValidTotal] = useState(true);
  const [editingSkills, setEditingSkills] = useState(false);
  const [showAddSkillModal, setShowAddSkillModal] = useState(false);
  const [newSkillName, setNewSkillName] = useState('');
  const [newSkillWeightage, setNewSkillWeightage] = useState(0);
  const [invalidSkillIndexes, setInvalidSkillIndexes] = useState({});

  // JD preview states
  const [showJdPreviewModal, setShowJdPreviewModal] = useState(false);
  const [jdPreviewContent, setJdPreviewContent] = useState(null);
  const [showJdEditModal, setShowJdEditModal] = useState(false);
  const [headerInfoPopup, setHeaderInfoPopup] = useState(null);

  // CEIPAL import states
  const [showCeipalImport, setShowCeipalImport] = useState(false);
  const [prefetchedJobPostings, setPrefetchedJobPostings] = useState(null);

  // Workday import state
  const [atsImportType, setAtsImportType] = useState(null); // 'workday' | 'greenhouse' | null

  // Track if we've already shown the existing results alert (use ref to prevent race conditions)
  const hasShownExistingResultsAlert = useRef(false);
  // Ref for current step so polling interval always reads latest (avoids stale closure)
  const stepRef = useRef(searchParams.get('step'));

  const defaultSkills = useMemo(() => {
    const title = position?.positionName || position?.title || position?.name || '';
    return getDefaultSkillWeightagesForTitle(title);
  }, [position]);

  const [activeJobFamilyKey, setActiveJobFamilyKey] = useState('');

  useEffect(() => {
    if (!position) return;
    const title = position.positionName || position.title || position.name || '';
    const resolved = resolvePositionJobFamilyKey(positionId, title);
    setActiveJobFamilyKey(resolved);
  }, [position, positionId]);

  const hasLinkedJobFamily = Boolean(
    positionId && activeJobFamilyKey && getPositionJobFamilyKey(positionId) === activeJobFamilyKey && isValidRoleKey(activeJobFamilyKey)
  );

  const handleJobFamilyProfileChange = async (roleKey) => {
    if (!roleKey || !positionId) return;
    const jd = findJdById(jdVersions, selectedJdId);
    const posTitle = position?.positionName || position?.title || position?.name || '';
    const { roleKey: resolvedKey, skills } = await ensurePositionSkillWeightages({
      positionId,
      positionTitle: posTitle,
      jdText: jd?.jdText || jd?.content || jd?.generatedJd || '',
      experience: position?.experience || '',
      roleKey,
      forceRegenerate: true,
    });
    const finalKey = resolvedKey || roleKey;
    setPositionJobFamilyKey(positionId, finalKey);
    setActiveJobFamilyKey(finalKey);
    const normalized = normalizeSkillWeightsTo100(skills);
    setSkillWeightages(normalized);
    if (selectedJdId) await syncJobFamilySkillsToJd(api, selectedJdId, normalized);
  };

  const fetchData = useCallback(async () => {
    try {
      const [positionResponse, resumesResponse, jdVersionsResponse] = await Promise.all([
        api.get(`/positions/${positionId}`),
        api.get(`/resumes/position/${positionId}`),
        api.get(`/job-descriptions/position/${positionId}/versions`)
      ]);

      setPosition(positionResponse.data);
      console.log('Fetched position:', positionResponse.data);
      // If we already have results but job_role is just the placeholder, update it with the fetched position name
      setStackRankingResults(prev => {
        if (!prev) return prev;
        if (!prev.job_role || prev.job_role === 'Position') {
          return {
            ...prev,
            job_role: positionResponse.data?.positionName || positionResponse.data?.title || positionResponse.data?.name || prev.job_role
          };
        }
        return prev;
      });
      setResumes(Array.isArray(resumesResponse.data) ? resumesResponse.data : []);
      setJdVersions(jdVersionsResponse.data);

      console.log('Fetched resumes count:', resumesResponse.data?.length || 0);
      console.log('Fetched resumes data:', resumesResponse.data);

      // Set selected JD to the latest one
      if (jdVersionsResponse.data && jdVersionsResponse.data.length > 0) {
        const latestJd = jdVersionsResponse.data[jdVersionsResponse.data.length - 1];
        const jdIdToUse = latestJd.jdId;
        setSelectedJdId(jdIdToUse);
        await fetchSkillWeightageForJd(jdIdToUse, positionResponse.data, latestJd);

        // Check if we should go directly to results page
        const shouldGoToResults = searchParams.get('step') === 'results';

        // Only check for existing results once - pass JD ID explicitly
        await checkExistingResults(jdIdToUse, true);
      } else {
        // No JD yet — still resolve job family + AI skill weightages from position title
        await fetchSkillWeightageForJd(null, positionResponse.data, null);

        const shouldGoToResults = searchParams.get('step') === 'results';
        if (shouldGoToResults) {
          console.warn('No JD versions found, but navigating to results. Trying position-level results only...');
          await checkExistingResults(null, true);
        }
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [positionId, searchParams]);

  useEffect(() => {
    stepRef.current = currentStep;
  }, [currentStep]);

  useEffect(() => {
    setSelectedRecruiters(new Set());
  }, [positionId]);

  useEffect(() => {
    fetchData();
    fetchWhatsAppMessages();

    // Auto-refresh WhatsApp messages only when on results step, every 30 seconds (reduces API load)
    const whatsappRefreshInterval = setInterval(() => {
      if (stepRef.current === 3) {
        fetchWhatsAppMessages();
      }
    }, 30000);

    // Listen for JD regeneration events so we can refresh versions and select the new AI JD
    const onJdRegenerated = async (event) => {
      try {
        const regeneratedPositionId = event?.detail?.positionId;
        if (!regeneratedPositionId || Number(regeneratedPositionId) !== Number(positionId)) return;
        console.log('Received jd:regenerated for position', regeneratedPositionId, '- refreshing JD versions');
        const resp = await api.get(`/job-descriptions/position/${positionId}/versions`);
        setJdVersions(resp.data || []);
        if (resp.data && resp.data.length > 0) {
          const latest = resp.data[resp.data.length - 1];
          setSelectedJdId(latest.jdId);
          // Optionally open preview for the regenerated JD
          // setJdPreviewContent(latest);
          // setShowJdPreviewModal(true);
          console.log('Updated selected JD to latest id:', latest.jdId);
        }
      } catch (err) {
        console.error('Error handling jd:regenerated event', err);
      }
    };

    window.addEventListener('jd:regenerated', onJdRegenerated);

    return () => {
      window.removeEventListener('jd:regenerated', onJdRegenerated);
      clearInterval(whatsappRefreshInterval);
      rankingAbortRef.current?.abort();
      rankingAbortRef.current = null;
    };
  }, [positionId]); // Only depend on positionId to prevent duplicate calls

  useEffect(() => {
    calculateSkillTotal(skillWeightages);
  }, [skillWeightages]);

  useEffect(() => {
    const onJobFamilySkillsUpdated = (e) => {
      const updatedKey = e.detail?.roleKey;
      const title = position?.positionName || position?.title || position?.name || '';
      const currentKey = resolvePositionJobFamilyKey(positionId, title);
      if (!updatedKey || updatedKey !== currentKey) return;
      const skills = roleSkillsToWeightages(getRoleSkills(updatedKey));
      const normalized = normalizeSkillWeightsTo100(skills);
      setSkillWeightages(normalized);
      if (selectedJdId) syncJobFamilySkillsToJd(api, selectedJdId, normalized);
    };
    window.addEventListener('reachout:job-family-skills-updated', onJobFamilySkillsUpdated);
    return () => window.removeEventListener('reachout:job-family-skills-updated', onJobFamilySkillsUpdated);
  }, [positionId, position, selectedJdId]);

  // If every resume is removed, clear processing UI, selection state, and cancel any in-flight ranking request.
  useEffect(() => {
    if (resumes.length === 0) {
      rankingAbortRef.current?.abort();
      rankingAbortRef.current = null;
      setRankingLoading(false);
      setSelectedResumeIds(new Set());
      setShowBulkDeleteConfirmation(false);
      return;
    }

    const validIds = new Set(
      resumes
        .filter((r) => r?.resumeId != null && !String(r.resumeId).startsWith('temp-'))
        .map((r) => r.resumeId)
    );
    setSelectedResumeIds((prev) => {
      const next = new Set([...prev].filter((id) => validIds.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [resumes]);

  const rankedResumeIdSet = useMemo(() => {
    const ids = new Set();
    (stackRankingResults?.candidates || []).forEach((candidate) => {
      const id = candidate?.resume_id ?? candidate?.resumeId;
      if (id != null && id !== '') ids.add(String(id));
    });
    return ids;
  }, [stackRankingResults]);

  // Ranked candidates first, then every resume the AI hasn't ranked (Stack Ranking off, AI failed,
  // or uploaded after the last run) with the basic details read at upload — so none drop out of outreach.
  const resultCandidates = useMemo(() => {
    const ranked = stackRankingResults?.candidates || [];
    const basic = (resumes || [])
      .filter((r) => r?.resumeId != null && !String(r.resumeId).startsWith('temp-'))
      .filter((r) => !rankedResumeIdSet.has(String(r.resumeId)))
      .map((r, i) => ({
        rank: ranked.length + i + 1,
        basic_only: true,
        name: r.candidateName || 'Unknown',
        email: normalizeCandidateEmailForDisplay(r.email, r),
        mobile: r.mobile || 'Not provided',
        total_score: null,
        resume_id: r.resumeId,
        uploadedBy: r.uploadedBy,
        uploadedByEmail: r.uploadedByEmail,
        createdBy: r.uploadedBy,
        createdByEmail: r.uploadedByEmail,
      }));
    return basic.length ? [...ranked, ...basic] : ranked;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stackRankingResults, resumes, rankedResumeIdSet]);
  const basicCandidateCount = resultCandidates.length - (stackRankingResults?.candidates?.length || 0);

  const newUnrankedResumeCount = useMemo(() => {
    const persisted = (resumes || []).filter((r) => r?.resumeId != null && !String(r.resumeId).startsWith('temp-'));
    if (rankedResumeIdSet.size === 0) return persisted.length;
    return persisted.filter((r) => !rankedResumeIdSet.has(String(r.resumeId))).length;
  }, [resumes, rankedResumeIdSet]);

  const hasExistingRanking = rankedResumeIdSet.size > 0;

  // Track if we're actively checking for results to prevent premature step changes
  const isCheckingResults = useRef(false);



  const checkExistingResults = async (jdId, isInitialLoad = false) => {
    const shouldGoToResults = searchParams.get('step') === 'results';
    const resultsAlreadyLoaded =
      !!stackRankingResults &&
      ((Array.isArray(stackRankingResults.candidates) && stackRankingResults.candidates.length > 0) ||
        (Number(stackRankingResults.total_candidates) > 0));

    // Back/forward between steps can re-trigger this function even when results are already in memory.
    // Avoid showing "loading existing results" (and avoid refetching) in that case.
    if (shouldGoToResults && resultsAlreadyLoaded) {
      isCheckingResults.current = false;
      return;
    }

    // Reset alert flag when navigating to results page ONLY if we truly need to fetch.
    if (shouldGoToResults) {
      hasShownExistingResultsAlert.current = false;
      isCheckingResults.current = true; // Mark that we're checking
    }

    debugLog(`checkExistingResults called - jdId: ${jdId}, isInitialLoad: ${isInitialLoad}, hasShownAlert: ${hasShownExistingResultsAlert.current}, step=results: ${shouldGoToResults}`);

    // Prevent duplicate calls only for initial load when not navigating to results
    if (hasShownExistingResultsAlert.current && isInitialLoad && !shouldGoToResults) {
      debugLog('Already checked for existing results, skipping...');
      return;
    }

    try {
      // When navigating to results page, try position-level results first (more reliable)
      // Then try JD-specific if available
      let sourceList = null;

      // If navigating to results, check status first to see if results should exist
      if (shouldGoToResults) {
        try {
          debugLog('=== NAVIGATING TO RESULTS PAGE ===');
          debugLog(`Position ID: ${positionId}, JD ID: ${jdId || 'NOT SET YET'}`);
          debugLog('Checking status first...');
          const statusResponse = await api.get(`/stack-ranking/position/${positionId}/status`);
          debugLog('📊 FULL STATUS RESPONSE:', JSON.stringify(statusResponse.data, null, 2));

          const hasStackRanking = statusResponse.data?.hasStackRanking || false;
          const resultsCount = statusResponse.data?.resultsCount || 0;
          const oldRankingsCount = statusResponse.data?.oldRankingsCount || 0;
          const source = statusResponse.data?.source || 'none';

          debugLog(`📈 Status Summary: hasStackRanking=${hasStackRanking}, resultsCount=${resultsCount}, oldRankingsCount=${oldRankingsCount}, source=${source}`);

          // If status says results exist but we get empty array, there might be a cache validity issue
          if (hasStackRanking && (resultsCount > 0 || oldRankingsCount > 0)) {
            debugLog('Status indicates results exist, fetching them...');
            debugLog(`Expected results: ${resultsCount} from new table, ${oldRankingsCount} from old table, source: ${source}`);

            const positionResponse = await api.get(`/stack-ranking/position/${positionId}/results`);
            debugLog('Position-level results response:', positionResponse.data);
            debugLog('Position-level results count:', positionResponse.data?.length || 0);

            if (positionResponse.data && positionResponse.data.length > 0) {
              sourceList = positionResponse.data;
              debugLog('✅ Found position-level results, will use these');
            } else if (resultsCount > 0 || oldRankingsCount > 0) {
              // Status says results exist but results endpoint returns empty - try JD-specific immediately
              console.warn(`⚠️ Status indicates ${resultsCount} results exist but results endpoint returned empty.`);
              console.warn(`Source: ${source}, Position ID: ${positionId}`);
              console.warn('This might be a cache validity issue. Trying JD-specific endpoint as immediate fallback...');

              // If old rankings exist, log that specifically
              if (oldRankingsCount > 0) {
                console.warn(`Note: ${oldRankingsCount} results exist in old table format. Backend may need to convert them.`);
              }

              // Immediately try JD-specific endpoint if JD is available
              if (jdId) {
                try {
                  debugLog('Immediately trying JD-specific results...');
                  const jdResponse = await api.get(`/stack-ranking/position/${positionId}/jd/${jdId}/results`);
                  debugLog('JD-specific results response:', jdResponse.data);
                  debugLog('JD-specific results count:', jdResponse.data?.length || 0);
                  if (jdResponse.data && jdResponse.data.length > 0) {
                    sourceList = jdResponse.data;
                    debugLog('✅ Found JD-specific results via immediate fallback');
                  }
                } catch (jdError) {
                  console.warn('JD-specific endpoint also returned empty:', jdError.response?.status);
                }
              } else {
                console.warn('JD not available yet, will try later in the flow');
              }
            }
          } else {
            debugLog('Status indicates no results exist for this position');
          }
        } catch (statusError) {
          debugLog('Error checking status, will try results endpoint directly:', statusError.response?.status);
          // If status check fails, try results endpoint directly
          try {
            const positionResponse = await api.get(`/stack-ranking/position/${positionId}/results`);
            debugLog('Position-level results response:', positionResponse.data);
            debugLog('Position-level results count:', positionResponse.data?.length || 0);
            if (positionResponse.data && positionResponse.data.length > 0) {
              sourceList = positionResponse.data;
              debugLog('Found position-level results, will use these');
            }
          } catch (positionError) {
            debugLog('No position-level results found:', positionError.response?.status);
          }
        }
      }

      // If no position-level results found, try JD-specific (if JD is available)
      if ((!sourceList || sourceList.length === 0) && jdId) {
        try {
          debugLog(`🔍 Checking JD-specific results for JD ID: ${jdId}...`);
          const response = await api.get(`/stack-ranking/position/${positionId}/jd/${jdId}/results`);
          debugLog('🔍 JD-specific results FULL response:', JSON.stringify(response.data, null, 2));
          debugLog('📊 JD-specific results count:', response.data?.length || 0);
          debugLog('📊 JD-specific results type:', Array.isArray(response.data) ? 'Array' : typeof response.data);
          if (response.data && response.data.length > 0) {
            sourceList = response.data;
            debugLog('✅ Found JD-specific results, will use these');
          } else {
            console.warn('⚠️ JD-specific endpoint returned empty array or null');
          }
        } catch (jdError) {
          console.error('❌ JD-specific endpoint error:', jdError.response?.status, jdError.response?.data);
          console.error('Full error:', jdError);
        }
      } else if (!jdId) {
        console.warn('⚠️ JD ID not available yet, skipping JD-specific check');
      }

      // If still no results and we're navigating to results, try position-level one more time
      // (in case JD was checked first in the previous flow)
      if ((!sourceList || sourceList.length === 0) && shouldGoToResults) {
        try {
          debugLog('Final attempt - trying position-level results...');
          const positionResponse = await api.get(`/stack-ranking/position/${positionId}/results`);
          debugLog('Position-level results response:', positionResponse.data);
          debugLog('Position-level results count:', positionResponse.data?.length || 0);
          if (positionResponse.data && positionResponse.data.length > 0) {
            sourceList = positionResponse.data;
          }
        } catch (positionError) {
          debugLog('No position-level results found:', positionError.response?.status);
        }
      }

      // If still no results but we have JD, try one more time with a small delay
      // This handles cases where backend might need a moment to process fallback queries
      if ((!sourceList || sourceList.length === 0) && shouldGoToResults && jdId) {
        try {
          debugLog('Additional retry attempt - waiting 500ms then trying JD-specific again...');
          await new Promise(resolve => setTimeout(resolve, 500)); // Small delay
          const retryResponse = await api.get(`/stack-ranking/position/${positionId}/jd/${jdId}/results`);
          debugLog('Retry JD-specific results response:', retryResponse.data);
          debugLog('Retry JD-specific results count:', retryResponse.data?.length || 0);
          if (retryResponse.data && retryResponse.data.length > 0) {
            sourceList = retryResponse.data;
            debugLog('✅ Found results on retry attempt');
          }
        } catch (retryError) {
          debugLog('Retry also failed:', retryError.response?.status);
        }
      }

      if (sourceList && sourceList.length > 0) {
        // Convert and normalize ranks to be sequential 1..N (sorted by score desc)
        let candidates = sourceList.map(candidate => {
          // Parse AI analysis to extract detailed information
          const aiAnalysisData = parseAiAnalysis(candidate.aiAnalysis || candidate.ai_analysis);

          // Debug: Log the AI analysis parsing
          if (candidate.aiAnalysis || candidate.ai_analysis) {
            debugLog('AI Analysis for candidate:', candidate.name || candidate.candidateName);
            debugLog('Raw AI Analysis:', candidate.aiAnalysis || candidate.ai_analysis);
            debugLog('Parsed AI Analysis Data:', aiAnalysisData);
          }

          const rawEmail = candidate.email || candidate.candidateEmail || candidate.candidate_email || '';
          const safeEmail = normalizeCandidateEmailForDisplay(rawEmail, candidate);
          return {
            rank: candidate.rank,
            name: candidate.candidateName || candidate.name || 'Unknown',
            email: safeEmail,
            mobile: candidate.mobile || candidate.phone || candidate.candidatePhone || 'Not provided',
            total_score: candidate.totalScore || candidate.total_score || candidate.match_score || 0,
            technical_score: candidate.technicalScore || candidate.technical_score,
            communication_score: candidate.communicationScore || candidate.communication_score,
            experience_score: candidate.experienceScore || candidate.experience_score,
            skills_score: candidate.skillsScore || candidate.skills_score,
            parameter_scores:
              candidate.parameter_scores ||
              candidate.parameterScores ||
              aiAnalysisData.parameter_scores ||
              null,
            ai_analysis: candidate.aiAnalysis || candidate.ai_analysis,
            resume_id: candidate.resumeId || candidate.resume_id || candidate.id,
            uploadedBy: candidate.uploadedBy || candidate.uploaded_by,
            uploadedByEmail: candidate.uploadedByEmail || candidate.uploaded_by_email,
            createdBy: candidate.createdBy || candidate.uploadedBy || candidate.uploaded_by,
            createdByEmail: candidate.createdByEmail || candidate.uploadedByEmail || candidate.uploaded_by_email,
            parameter_scores: candidate.parameterScores || candidate.parameter_scores || null,
            // Extract detailed fields from AI analysis or use direct fields as fallback
            education: aiAnalysisData.education || candidate.education || candidate.educationDetails || candidate.education_info || 'Not provided',
            skills: aiAnalysisData.skills || candidate.skills || candidate.skillSet || candidate.skills_list || candidate.technical_skills || 'Not provided',
            matching_skills: aiAnalysisData.matching_skills || candidate.matching_skills || candidate.matchingSkills || candidate.matching_skills_list || 'Not provided',
            missing_skills: aiAnalysisData.missing_skills || candidate.missing_skills || candidate.missingSkills || candidate.missing_skills_list || 'Not provided',
            match_summary: aiAnalysisData.match_summary || candidate.match_summary || candidate.matchSummary || candidate.analysis || candidate.summary || 'Not provided',
            experience: candidate.experience || candidate.experienceYears || candidate.experience_years || candidate.years_of_experience || 'Not provided',
            current_position: candidate.current_position || candidate.currentPosition || candidate.current_role || candidate.position || 'Not provided',
            company: candidate.company || candidate.currentCompany || candidate.current_company || candidate.employer || 'Not provided',
            relevant_projects: candidate.relevant_projects || candidate.relevantProjects || candidate.projects || candidate.project_experience || 'Not provided'
          };
        });

        // sort by total score desc if available, otherwise by existing rank
        candidates.sort((a, b) => {
          const as = Number(a.total_score ?? 0);
          const bs = Number(b.total_score ?? 0);
          if (bs !== as) return bs - as;
          return (a.rank ?? 9999) - (b.rank ?? 9999);
        });
        // Remove duplicates - prioritize resume_id as it's the most reliable identifier
        // Only use email-based deduplication if resume_id is missing
        const uniqueCandidates = [];
        const seenResumeIds = new Set();
        const seenEmailResumePairs = new Set(); // Track email+resume_id pairs for cases where resume_id is missing

        for (const candidate of candidates) {
          const resumeId = candidate.resume_id || candidate.resumeId;
          const email = candidate.email || candidate.candidateEmail || candidate.candidate_email || '';
          const name = candidate.name || candidate.candidateName || '';

          // Primary deduplication: Use resume_id if available (most reliable)
          if (resumeId) {
            if (!seenResumeIds.has(resumeId)) {
              uniqueCandidates.push(candidate);
              seenResumeIds.add(resumeId);
              // Also track email+resume_id pair for secondary check
              if (email) {
                seenEmailResumePairs.add(`${email}-${resumeId}`);
              }
            } else {
              console.warn('Duplicate candidate found (same resume_id) and removed:', { name, email, resume_id: resumeId });
            }
          } else if (email) {
            // Secondary deduplication: Use email only if resume_id is missing
            // Only consider it a duplicate if we've seen this exact email+resume_id combination
            // OR if email is present and we haven't seen this email yet
            const emailKey = email.toLowerCase().trim();
            const emailResumePair = `${emailKey}-${resumeId || 'no-resume-id'}`;

            if (!seenEmailResumePairs.has(emailResumePair)) {
              // Check if we've seen this email with a different resume_id
              const emailSeenBefore = Array.from(seenEmailResumePairs).some(pair => pair.startsWith(`${emailKey}-`));

              if (!emailSeenBefore) {
                uniqueCandidates.push(candidate);
                seenEmailResumePairs.add(emailResumePair);
              } else {
                console.warn('Duplicate candidate found (same email, different resume_id) and removed:', { name, email, resume_id: resumeId });
              }
            } else {
              console.warn('Duplicate candidate found (same email+resume_id pair) and removed:', { name, email, resume_id: resumeId });
            }
          } else {
            // Fallback: If both resume_id and email are missing, use name+email combination
            // But be more lenient - only remove if name AND email are exactly the same
            const fallbackKey = `${name}-${email}`.toLowerCase().trim();
            const candidateKey = `${name}-${email || 'no-email'}`.toLowerCase().trim();

            if (!seenEmailResumePairs.has(candidateKey)) {
              uniqueCandidates.push(candidate);
              seenEmailResumePairs.add(candidateKey);
              console.warn('Candidate without resume_id or email, using fallback key:', { name, email, key: candidateKey });
            } else {
              console.warn('Duplicate candidate found (fallback key) and removed:', { name, email, key: candidateKey });
            }
          }
        }

        console.log(`Deduplication: Started with ${candidates.length} candidates, ended with ${uniqueCandidates.length} unique candidates`);

        // reindex ranks sequentially
        candidates = uniqueCandidates.map((c, idx) => ({ ...c, rank: idx + 1 }));

        const convertedResults = {
          job_role: position?.positionName || position?.title || position?.name || stackRankingResults?.job_title || 'Position',
          total_candidates: candidates.length,
          analysis_timestamp: sourceList[0]?.createdAt || new Date().toISOString(),
          candidates
        };
        setStackRankingResults(convertedResults);
        setCurrentPage(1);
        const urlWantsResults = parseStepQuery(searchParams.get('step')) === 3;
        if (urlWantsResults) {
          goToStep(3, { push: false });
        }

        // Mark checking as complete since we found results
        const shouldGoToResults = searchParams.get('step') === 'results';
        if (shouldGoToResults) {
          isCheckingResults.current = false;
        }

        // Check if there's a mismatch between uploaded resumes and existing results
        console.log('Uploaded resumes count:', resumes.length);
        console.log('Existing results count:', candidates.length);
        if (candidates.length !== resumes.length) {
          console.warn(`Mismatch: ${resumes.length} uploaded resumes but ${candidates.length} results found`);
        }

        // Pop-up/toast intentionally removed for existing results detection to avoid
        // noisy notifications on back/forward navigation.
        hasShownExistingResultsAlert.current = true;
        return; // Exit early if we found results
      }
      // No results found at any level
      if (shouldGoToResults && (!aiRankingOn || aiRankingFailed)) {
        // Nothing ranked is expected here; Results shows the basic list built from the resumes.
        isCheckingResults.current = false;
        hasShownExistingResultsAlert.current = true;
        return;
      }
      if (shouldGoToResults) {
        console.log('=== NO RESULTS FOUND AFTER ALL CHECKS ===');
        console.log(`Position ID: ${positionId}`);
        console.log(`JD ID: ${jdId || 'NOT AVAILABLE'}`);
        console.log(`SourceList: ${sourceList ? `Array with ${sourceList.length} items` : 'null/undefined'}`);

        // Before giving up, check status one more time to see if results should exist
        let statusIndicatesResultsExist = false;
        try {
          console.log('🔄 Final status check before giving up...');
          const finalStatusCheck = await api.get(`/stack-ranking/position/${positionId}/status`);
          console.log('📊 FINAL STATUS CHECK:', JSON.stringify(finalStatusCheck.data, null, 2));

          const finalHasStackRanking = finalStatusCheck.data?.hasStackRanking || false;
          const finalResultsCount = finalStatusCheck.data?.resultsCount || 0;
          const finalOldRankingsCount = finalStatusCheck.data?.oldRankingsCount || 0;

          statusIndicatesResultsExist = finalHasStackRanking && (finalResultsCount > 0 || finalOldRankingsCount > 0);

          console.log(`Final Status: hasStackRanking=${finalHasStackRanking}, resultsCount=${finalResultsCount}, oldRankingsCount=${finalOldRankingsCount}`);
          console.log(`Status indicates results exist: ${statusIndicatesResultsExist}`);

          if (statusIndicatesResultsExist) {
            console.error(`❌ CRITICAL: Status indicates ${finalResultsCount} results exist but we couldn't fetch them!`);
            console.error('This suggests a backend data inconsistency issue.');
            console.error('Backend fallback queries should have found these results.');
            console.error('Keeping step at 3 to show error message instead of redirecting.');

            // Don't redirect to step 1 if status says results exist - keep at step 3 to show the error
            isCheckingResults.current = false;
            if (!hasShownExistingResultsAlert.current) {
              toast.error(`Status indicates ${finalResultsCount} result(s) exist but unable to load them. This may be a backend data issue.`);
              hasShownExistingResultsAlert.current = true;
            }
            return; // Exit without changing step
          } else {
            console.log('✅ Final status check confirms no results exist - this is expected');
          }
        } catch (statusCheckError) {
          console.error('❌ Final status check failed:', statusCheckError);
          console.error('Error details:', statusCheckError.response?.data);
        }

        isCheckingResults.current = false; // Mark checking as complete
        if (!hasShownExistingResultsAlert.current) {
          console.error(`❌ No stack ranking results found for position ${positionId}`);
          console.error('Checked: JD-specific endpoint, position-level endpoint, and status endpoint');
          console.error('If results should exist, this might indicate:');
          console.error('  1. Cache validity issue (results exist but caches marked invalid)');
          console.error('  2. Data inconsistency (results in database but not linked properly)');
          console.error('  3. Backend fallback logic not working');
          toast.error('No stack ranking results found for this position. Please perform stack ranking first.');
          hasShownExistingResultsAlert.current = true;
        }
        // Only redirect to step 1 after we've confirmed no results exist
        console.log('No results found after complete check, redirecting to step 1');
        setCurrentStep(1);
      }

    } catch (error) {
      console.log('Error checking for existing results:', error.response?.status, error.message);

      // If we're trying to go to results but there's an error, show error
      if (shouldGoToResults) {
        isCheckingResults.current = false; // Mark checking as complete
        if (!hasShownExistingResultsAlert.current) {
          // Only show error if it's a real error (not just 404)
          if (error.response?.status !== 404) {
            console.error('Error fetching stack ranking results:', error);
            toast.error(`Failed to load stack ranking results: ${error.message || 'Unknown error'}`);
          } else {
            toast.error('No stack ranking results found for this position. Please perform stack ranking first.');
          }
          hasShownExistingResultsAlert.current = true;
        }
        // Only redirect to step 1 after we've confirmed there's an error
        setCurrentStep(1);
      }
    } finally {
      if (shouldGoToResults) {
        isCheckingResults.current = false; // Ensure flag is reset
      }
    }
  };

  const getWhatsAppStatus = (candidate, sourceMessages = whatsappMessages) => {
    const msg = getLatestWhatsappMessage(
      candidate?.mobile || candidate?.phone || candidate?.candidate_phone || candidate?.candidatePhone,
      sourceMessages
    );

    if (msg) {
      // Recruiter questions continue after notice period. Do not mark those chats completed
      // just because Q6 is already stored.
      if (/^asking-q(?:[7-9]|\d{2,})$/.test(msg.status || '')) return msg.status;

      // If all 6 answers are present the questionnaire is done — treat as completed
      // even if the DB status hasn't been refreshed yet from the 30-second cycle
      if (msg.noticePeriod) return 'completed';

      if (msg.status === 'completed') return 'completed';
      if (msg.status === 'awaiting-details') return 'awaiting-details';
      if (msg.status === 'interested' || msg.userResponse === 'Yes')
        return 'awaiting-details';
      if (msg.status === 'not-interested' || msg.userResponse === 'No')
        return 'not-interested';

      return msg.status || 'processing';
    }

    const overrideKey = getCandidateKey(candidate);
    const override = overrideKey ? whatsAppStatusOverrides[overrideKey] : null;
    return override?.status || 'not_sent';
  };

  const isWhatsAppOutreachLocked = (status) =>
    !['not_sent', 'failed', 'missing-details'].includes(status);

  const getWhatsAppStatusColor = (status) => {
    if (/^asking-q(?:[7-9]|\d{2,})$/.test(status || '')) return 'warning';
    switch (status) {
      case 'processing': return 'warning';
      case 'sent': return 'info';
      case 'delivered': return 'primary';
      case 'interested': return 'success';
      case 'not-interested':
      case 'not_interested': return 'danger';
      case 'awaiting-details': return 'warning';
      case 'completed': return 'success';
      case 'failed': return 'danger';
      case 'missing-details': return 'danger';
      case 'not_sent': return 'secondary';
      default: return 'dark';
    }
  };

  // Get status icon and text
  const getWhatsAppStatusDisplay = (status) => {
    if (/^asking-q(?:[7-9]|\d{2,})$/.test(status || '')) {
      return { icon: '⏳', text: 'SCREENING', color: '#8e9aaf' };
    }
    switch (status) {
      case 'processing':
        return { icon: '⏳', text: 'PROCESSING', color: '#8e9aaf' };
      case 'sent':
        return { icon: '📤', text: 'SENT', color: '#8e9aaf' };
      case 'delivered':
        return { icon: '✅', text: 'DELIVERED', color: '#8e9aaf' };
      case 'interested':
        return { icon: '🎯', text: 'INTERESTED', color: 'var(--feuji-green)' };
      case 'not-interested':
      case 'not_interested':
        return { icon: '🚫', text: 'NOT INTERESTED', color: '#8e9aaf' };
      case 'awaiting-details':
        return { icon: '⏳', text: 'AWAITING', color: '#8e9aaf' };
      case 'completed':
        return { icon: '🎉', text: 'COMPLETED', color: 'var(--feuji-green)' };
      case 'failed':
        return { icon: '❌', text: 'FAILED', color: '#8e9aaf' };
      case 'already-contacted':
        return { icon: '✅', text: 'CONTACTED', color: '#8e9aaf' };
      case 'missing-details':
        return { icon: '⚠️', text: 'MISSING DETAILS', color: '#8e9aaf' };
      case 'not_sent':
        return { icon: '📭', text: 'NOT SENT', color: '#8e9aaf' };
      default:
        return { icon: '❓', text: 'UNKNOWN', color: '#8e9aaf' };
    }
  };

  const fetchWhatsAppMessages = async () => {
    try {
      const response = await api.get(`/whatsapp-messages/position/${positionId}`);
      const messages = response.data || [];
      setWhatsappMessages(messages);

      // Build map: normalized phone -> the "temp" campaign row per candidate.
      // Only the "temp" row accumulates the 6 answer fields.
      // "yes2", "interested_two", "not_interested2" rows are outbound notifications
      // with NULL answer fields — they must never win over the "temp" row.
      const normalizePhoneDigits = (phone) => (phone || '').replace(/\D/g, '');
      const latestByPhone = new Map();
      messages.forEach(msg => {
        const key = normalizePhoneDigits(msg.phoneNumber);
        if (!key) return;
        const existing = latestByPhone.get(key);
        const isTemp     = msg.messageType === 'temp';
        const existingIsTemp = existing ? existing.messageType === 'temp' : false;
        const isCampaign = msg.messageType !== 'user_reply';
        const existingIsCampaign = existing ? existing.messageType !== 'user_reply' : false;
        const msgDate = new Date(msg.updatedAt || msg.sentAt || 0);
        const existingDate = existing ? new Date(existing.updatedAt || existing.sentAt || 0) : 0;

        const shouldReplace =
          !existing ||
          // Always prefer a 'temp' row over everything else
          (isTemp && !existingIsTemp) ||
          // Among 'temp' rows pick the latest (shouldn't be more than one, but safe)
          (isTemp && existingIsTemp && msgDate > existingDate) ||
          // Among non-temp campaign rows (if no temp found yet), prefer over user_reply
          (!isTemp && isCampaign && !existingIsTemp && !existingIsCampaign) ||
          // Among same tier, pick latest
          (!isTemp && isCampaign && !existingIsTemp && existingIsCampaign && msgDate > existingDate) ||
          (!isCampaign && !existingIsTemp && !existingIsCampaign && msgDate > existingDate);

        if (shouldReplace) {
          latestByPhone.set(key, msg);
        }
      });

      setStackRankingResults(prevResults => {
        if (!prevResults || !prevResults.candidates) return prevResults;

        const updatedCandidates = prevResults.candidates.map(candidate => {
          const rawPhone =
            candidate?.mobile
            || candidate?.phone
            || candidate?.candidate_phone
            || candidate?.candidatePhone;
          const key = normalizePhoneDigits(rawPhone);
          const latestMsg = key ? latestByPhone.get(key) : null;
          return {
            ...candidate,
            whatsappMessage: latestMsg || candidate.whatsappMessage
          };
        });

        return {
          ...prevResults,
          candidates: updatedCandidates
        };
      });

      return true;
    } catch (error) {
      console.error('Error fetching WhatsApp messages:', error);
      console.error('Error details:', error.response?.data);
      setWhatsappMessages([]);
      return false;
    }
  };

  const refreshWhatsAppMessages = async () => {
    // Only show success when there is something to refresh (matches enabled state of the button).
    const allowSuccessToast = resultCandidates.some((c) => getWhatsAppStatus(c) !== 'not_sent');
    try {
      await api.post(`/whatsapp-messages/refresh/${positionId}`);
      await fetchWhatsAppMessages();
      if (allowSuccessToast) {
        toast.success('WhatsApp messages refreshed');
      }
    } catch (error) {
      console.error('Error refreshing WhatsApp messages:', error);
      toast.error('Failed to refresh WhatsApp messages');
    }
  };

  const candidatesForWhatsApp = resultCandidates;
  const canRefreshWhatsAppStatus = Array.isArray(candidatesForWhatsApp)
    && candidatesForWhatsApp.some((c) => getWhatsAppStatus(c) !== 'not_sent');

  // Show Message Details Function
  const showMessageDetails = (message) => {
    // Get the actual candidate data from the table
    const actualCandidate = resultCandidates.find(c =>
      c.mobile === message.phoneNumber || c.phone === message.phoneNumber
    );

    // Create enhanced message with actual candidate data
    const enhancedMessage = {
      ...message,
      candidateName: actualCandidate?.name || message.candidateName,
      jobTitle: actualCandidate?.jobTitle || message.jobTitle
    };

    setSelectedMessage(enhancedMessage);
    setShowMessageModal(true);
  };

  // Handler to open WhatsApp details when badge is clicked; refreshes messages if needed
  const handleWhatsAppBadgeClick = async (candidate, status, messageFromList, candidateAttachedMessage) => {
    try {
      // not actionable if no message has been sent yet
      if (status === 'not_sent') return;

      console.log('WhatsApp badge clicked', { candidateName: candidate.name, status, hasListMessage: !!messageFromList, hasCandidateMessage: !!candidateAttachedMessage });

      let msg = messageFromList || candidateAttachedMessage;
      if (!msg) {
        // Try refreshing the shared list and pick latest
        await fetchWhatsAppMessages();
        msg = getLatestWhatsappMessage(candidate.mobile) || candidate.whatsappMessage;
        console.log('After refresh, found message:', msg);
      }

      if (msg) {
        showMessageDetails(msg);
      } else {
        // Create a minimal fallback message object so modal can open and show 'no details yet'
        const fallback = {
          candidateName: candidate.name,
          phoneNumber: candidate.mobile || candidate.phone,
          status: 'completed',
          jobTitle: candidate.jobTitle
        };
        console.log('Opening modal with fallback message:', fallback);
        showMessageDetails(fallback);
      }
    } catch (err) {
      console.error('Error opening WhatsApp details:', err);
      toast.error('Failed to open WhatsApp details');
    }
  };


  const fetchSkillWeightageForJd = async (jdId, positionData, jdRecord = null, { forceJobFamily = false } = {}) => {
    const pos = positionData || position;
    const title = pos?.positionName || pos?.title || pos?.name || '';
    const jd = jdRecord || (jdId ? findJdById(jdVersions, jdId) : null);
    const jdText = jd?.jdText || jd?.content || jd?.generatedJd || '';

    // Prefer skills already saved for this JD — never overwrite with job-family defaults on reload.
    if (jdId && !forceJobFamily) {
      const saved = await loadSavedJdSkillWeightages(api, jdId);
      if (saved?.length) {
        setSkillWeightages(saved);
        const resolvedKey = resolvePositionJobFamilyKey(positionId, title);
        if (resolvedKey && isValidRoleKey(resolvedKey)) {
          setActiveJobFamilyKey(resolvedKey);
        }
        return;
      }
    }

    const { roleKey, skills } = await ensurePositionSkillWeightages({
      positionId,
      positionTitle: title,
      jdText,
      experience: pos?.experience || '',
      forceRegenerate: forceJobFamily,
    });

    if (roleKey && isValidRoleKey(roleKey)) {
      setPositionJobFamilyKey(positionId, roleKey);
      setActiveJobFamilyKey(roleKey);
    }

    const normalized = normalizeSkillWeightsTo100(skills.length ? skills : defaultSkills);
    setSkillWeightages(normalized);
    if (jdId && normalized.length) {
      await syncJobFamilySkillsToJd(api, jdId, normalized);
    }
  };

  const handleJdChange = async (jdId) => {
    setSelectedJdId(jdId);
    fetchSkillWeightageForJd(jdId, position);

    // Clear any existing preview content when switching JDs
    setJdPreviewContent(null);

    // Check for existing results when switching JDs (not initial load)
    await checkExistingResults(jdId, false);
  };

  const handleFileSelect = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleAddResumeToExistingRanking = () => {
    goToStep(2);
    window.setTimeout(() => {
      fileInputRef.current?.click();
    }, 80);
  };

  /** Strip redundant labels from WhatsApp candidate detail values so UI shows only the value (e.g. "5.3 years" not "Total years of experience : 5.3 years") */
  const normalizeCandidateDetail = (value) => {
    if (!value || typeof value !== 'string') return value;
    let s = value.trim();
    const prefixes = [
      /^[-–—]\s*/,
      /^total\s*years?\s*of\s*experience\s*[:–-]\s*/i,
      /^total\s*experience\s*[:–-]?\s*/i,
      /^experience\s*[:–-]\s*/i,
      /^current\s*location\s*[:–-]?\s*/i,
      /^location\s*[:–-]\s*/i,
      /^willingness\s*to\s*relocate[^:]*[:–-]?\s*/i,
      /^willing\s*to\s*relocate[^:]*[:–-]?\s*/i,
      /^current\s*ctc\s*[:–-]?\s*/i,
      /^expected\s*ctc\s*[:–-]?\s*/i,
      /^expected\s*[:–-]?\s*/i,
      /^notice\s*period\s*[:–-]?\s*/i,
      /^notice\s*[:–-]\s*/i,
    ];
    for (const p of prefixes) {
      s = s.replace(p, '').trim();
    }
    return s || value;
  };

  const getShortFileTypeLabel = (file) => {
    const mime = (file.type || '').toLowerCase();
    if (mime.includes('pdf')) return 'PDF';
    if (mime.includes('wordprocessingml') || mime.includes('msword') || mime.includes('docx') || mime.includes('doc')) return 'DOCX';
    const ext = (file.name || '').split('.').pop();
    if (ext === 'pdf') return 'PDF';
    if (ext === 'docx' || ext === 'doc') return 'DOCX';
    return mime.split('/')[1]?.slice(0, 4).toUpperCase() || ext?.toUpperCase() || 'FILE';
  };

  const handleFileChange = (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    const allowedTypes = ['application/pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'application/msword'];
    const invalidFiles = files.filter(file => !allowedTypes.includes(file.type));

    if (invalidFiles.length > 0) {
      toast.error('Please select only PDF or Word document files');
      return;
    }

    setSelectedFiles(files);
    setShowFileConfirmation(true);
  };

  const handleConfirmUpload = async () => {
    if (uploadInProgress) return;
    if (!positionId) {
      toast.error('Position not found. Please go back and select a position.');
      return;
    }
    if (!selectedFiles || selectedFiles.length === 0) {
      toast.error('No files selected.');
      return;
    }
    const filesToUpload = [...selectedFiles];
    setShowFileConfirmation(false);
    setSelectedFiles([]);
    if (fileInputRef.current) fileInputRef.current.value = '';
    goToStep(2);
    setUploadInProgress(true);
    // Show new files in the list immediately (optimistic update)
    setResumes((prev) => [
      ...prev,
      ...filesToUpload.map((file, i) => ({
        resumeId: `temp-${Date.now()}-${i}`,
        fileName: file.name,
        candidateName: file.name.split('.')[0]
      }))
    ]);
    const UPLOAD_TIMEOUT_MS = 120000; // 2 min per file for slow connections / S3
    const DELAY_BETWEEN_FILES_MS = 400; // avoid connection abort when sending back-to-back
    const isNetworkError = (err) =>
      !err.response && (err.code === 'ECONNABORTED' || err.message?.includes('Network Error') || err.message?.includes('ABORTED'));

    try {
      for (let i = 0; i < filesToUpload.length; i++) {
        if (i > 0) await new Promise((r) => setTimeout(r, DELAY_BETWEEN_FILES_MS));
        const file = filesToUpload[i];
        let lastErr;
        for (let attempt = 0; attempt < 3; attempt++) {
          const fd = new FormData();
          const attacher = loggedInRecruiter(user);
          fd.append('candidateName', file.name.split('.')[0]);
          fd.append('email', 'notprovided@example.com');
          fd.append('mobile', 'Not provided');
          if (attacher.name) fd.append('attachedBy', attacher.name);
          if (attacher.email) fd.append('attachedByEmail', attacher.email);
          fd.append('file', file);
          try {
            await api.post(`/resumes/position/${positionId}/upload`, fd, { timeout: UPLOAD_TIMEOUT_MS });
            lastErr = null;
            break;
          } catch (err) {
            lastErr = err;
            if (attempt < 2 && isNetworkError(err)) {
              await new Promise((r) => setTimeout(r, 1000 * (attempt + 1)));
              continue;
            }
            throw err;
          }
        }
        if (lastErr) throw lastErr;
      }

      const uploadedCount = filesToUpload.length;
      toast.success(resumeFileUploadSuccessMessage(uploadedCount));
      // Replace with full list from server so we have real resumeIds for delete
      const resumesResponse = await api.get(`/resumes/position/${positionId}`);
      const raw = Array.isArray(resumesResponse.data) ? resumesResponse.data : (resumesResponse.data?.content ?? []);
      const list = (raw || []).map((r) => ({
        resumeId: r.resumeId ?? r.resume_id ?? r.id,
        fileName: r.fileName ?? r.file_name ?? r.name ?? 'Resume',
        ...r
      }));
      setResumes(list);
    } catch (error) {
      const message = error.response?.data?.message || error.response?.data?.error || error.message || 'Failed to upload resumes';
      toast.error(message);
      console.error('Error uploading resumes:', error.response?.data || error);
      // Remove optimistic entries on failure so list matches server
      setResumes((prev) => prev.filter((r) => !String(r.resumeId).startsWith('temp-')));
    } finally {
      setUploadInProgress(false);
    }
  };

  const handleCancelUpload = () => {
    if (uploadInProgress) return;
    setSelectedFiles([]);
    setShowFileConfirmation(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDeleteResume = (resumeId) => {
    setResumeToDelete(resumeId);
    setShowDeleteConfirmation(true);
  };

  const confirmDeleteResume = async () => {
    const deletedId = resumeToDelete;
    try {
      await api.delete(`/resumes/${resumeToDelete}`);
      toast.success('Resume deleted successfully');
      setShowDeleteConfirmation(false);
      setResumeToDelete(null);
      setSelectedResumeIds((prev) => {
        if (!deletedId) return prev;
        const next = new Set(prev);
        next.delete(deletedId);
        return next;
      });
      // Fetch latest resumes for the position
      const resumesResponse = await api.get(`/resumes/position/${positionId}`);
      const updatedResumes = resumesResponse.data || [];
      setResumes(updatedResumes);

      // If there are resumes left, immediately re-run stack ranking and show results
      // (without AI ranking the basic list simply updates from the refreshed resumes).
      if (!aiRankingOn || aiRankingFailed) {
        if (updatedResumes.length === 0) setCurrentStep(2);
      } else if (selectedJdId && updatedResumes.length > 0) {
        try {
          const customScoring = skillWeightages.map(skill => ({
            parameter: skill.skillName,
            weight: sanitizeWeightage(skill.weightage)
          }));
          const response = await api.post(
            `/stack-ranking/position/${positionId}/jd/${selectedJdId}/analyze`,
            { customScoring }
          );
          // Normalize and enforce sequential ranks
          const sourceList = response.data.candidates || response.data.rankings || [];
          let candidates = sourceList.map((candidate, index) => {
            // Parse AI analysis to extract detailed information
            const aiAnalysisData = parseAiAnalysis(candidate.ai_analysis || candidate.aiAnalysis);

            const rawEmail = candidate.email || candidate.candidate_email || candidate.candidateEmail || '';
            const safeEmail = normalizeCandidateEmailForDisplay(rawEmail, candidate);
            return {
              rank: candidate.rank || index + 1,
              name: candidate.name || candidate.candidateName || 'Unknown',
              email: safeEmail,
              mobile: candidate.mobile || candidate.phone || candidate.candidate_phone || 'Not provided',
              total_score: candidate.total_score || candidate.totalScore || candidate.match_score || 0,
              technical_score: candidate.technical_score || candidate.technicalScore,
              communication_score: candidate.communication_score || candidate.communicationScore,
              experience_score: candidate.experience_score || candidate.experienceScore,
              skills_score: candidate.skills_score || candidate.skillsScore,
              parameter_scores:
                candidate.parameter_scores ||
                candidate.parameterScores ||
                aiAnalysisData.parameter_scores ||
                null,
              ai_analysis: candidate.ai_analysis || candidate.aiAnalysis,
              resume_id: candidate.resume_id || candidate.resumeId || candidate.id,
              parameter_scores: candidate.parameterScores || candidate.parameter_scores || null,
              // Extract detailed fields from AI analysis or use direct fields as fallback
              education: aiAnalysisData.education || candidate.education || candidate.educationDetails || 'Not provided',
              skills: aiAnalysisData.skills || candidate.skills || candidate.skillSet || 'Not provided',
              matching_skills: aiAnalysisData.matching_skills || candidate.matching_skills || candidate.matchingSkills || 'Not provided',
              missing_skills: aiAnalysisData.missing_skills || candidate.missing_skills || candidate.missingSkills || 'Not provided',
              match_summary: aiAnalysisData.match_summary || candidate.match_summary || candidate.matchSummary || candidate.ai_analysis || 'Not provided',
              experience: candidate.experience || candidate.experienceYears || 'Not provided',
              current_position: candidate.current_position || candidate.currentPosition || 'Not provided',
              company: candidate.company || candidate.currentCompany || 'Not provided',
              relevant_projects: candidate.relevant_projects || candidate.relevantProjects || 'Not provided'
        };
      });
      candidates = dedupeRankingCandidatesByResumeId(candidates);
      candidates.sort((a, b) => Number(b.total_score ?? 0) - Number(a.total_score ?? 0));
      candidates = candidates.map((c, idx) => ({ ...c, rank: idx + 1 }));
      setStackRankingResults({
            job_role: position?.positionName || position?.title || position?.name || stackRankingResults?.job_title || 'Position',
            total_candidates: candidates.length,
            analysis_timestamp: new Date().toISOString(),
            candidates
          });
          setSelectedCandidates([]);
          goToStep(3);
        } catch (e) {
          // If analysis fails for any reason, fall back to upload step with refreshed list
          setStackRankingResults(null);
          setSelectedCandidates([]);
          setCurrentStep(2);
        }
      } else {
        // No resumes left – cancel ranking and reset processing UI
        rankingAbortRef.current?.abort();
        rankingAbortRef.current = null;
        setRankingLoading(false);
        setStackRankingResults(null);
        setSelectedCandidates([]);
        setCurrentStep(2);
      }
    } catch (error) {
      toast.error('Failed to delete resume');
      console.error('Error deleting resume:', error);
    }
  };

  const cancelDeleteResume = () => {
    setShowDeleteConfirmation(false);
    setResumeToDelete(null);
  };

  const handleBulkDeleteResumes = () => {
    if (selectedResumeIds.size === 0) return;
    setShowBulkDeleteConfirmation(true);
  };

  const confirmBulkDeleteResumes = async () => {
    const idsToDelete = Array.from(selectedResumeIds).filter(id => !String(id).startsWith('temp-'));
    setShowBulkDeleteConfirmation(false);
    if (idsToDelete.length === 0) {
      setSelectedResumeIds(new Set());
      return;
    }
    setBulkDeleteInProgress(true);
    try {
      await Promise.all(idsToDelete.map(id => api.delete(`/resumes/${id}`)));
      toast.success(`${idsToDelete.length} resume${idsToDelete.length > 1 ? 's' : ''} deleted successfully`);
      setSelectedResumeIds(new Set());
      const resumesResponse = await api.get(`/resumes/position/${positionId}`);
      setResumes(resumesResponse.data || []);
    } catch (error) {
      toast.error('Failed to delete some resumes');
      const resumesResponse = await api.get(`/resumes/position/${positionId}`);
      setResumes(resumesResponse.data || []);
      setSelectedResumeIds(new Set());
    } finally {
      setBulkDeleteInProgress(false);
    }
  };

  const cancelBulkDeleteResumes = () => {
    setShowBulkDeleteConfirmation(false);
  };

  const handlePerformRanking = async () => {
    if (!selectedJdId) {
      toast.error('Please select a job description');
      return;
    }

    if (resumes.length === 0) {
      toast.error('Please upload at least one resume');
      return;
    }

    if (!positionId) {
      toast.error('Position ID is missing');
      return;
    }

    if (!isValidTotal) {
      toast.warning(
        'Skill weightages must total 100% before you can run stack ranking. Go back to Skill Weightage to adjust them.'
      );
      return;
    }

    // New run: allow success toast to show again.
    try {
      sessionStorage.removeItem(STACK_RANKING_SUCCESS_TOAST_KEY);
    } catch (e) {
      // ignore storage errors
    }

    rankingAbortRef.current?.abort();
    const rankingController = new AbortController();
    rankingAbortRef.current = rankingController;

    setRankingLoading(true);
    setRankingFailures([]);
    setProcessingProgress(0);
    const resumesToAnalyze = hasExistingRanking ? Math.max(0, newUnrankedResumeCount) : resumes.length;
    const rankingWorkUnits = Math.max(1, resumesToAnalyze);
    const rankingSpeed = rankingWorkUnits <= 1 ? 3.4 : rankingWorkUnits <= 3 ? 1.6 : rankingWorkUnits <= 6 ? 1.0 : 0.7;
    if (processingIntervalRef.current) clearInterval(processingIntervalRef.current);
    processingIntervalRef.current = setInterval(() => {
      setProcessingProgress(prev => {
        const step = rankingSpeed;
        if (prev < 30) return Math.min(30, prev + 1.5 * step);
        if (prev < 60) return Math.min(60, prev + 0.6 * step);
        if (prev < 80) return Math.min(80, prev + 0.25 * step);
        if (prev < 93) return Math.min(93, prev + 0.1 * step);
        if (prev < 97) return Math.min(97, prev + 0.04 * step);
        return prev;
      });
    }, 400);
    try {
      console.log('Starting stack ranking...');
      console.log('Position ID:', positionId);
      console.log('Selected JD ID:', selectedJdId);
      console.log('Resumes count:', resumes.length);
      console.log('Resumes data:', resumes);

      // Prepare custom scoring data
      const customScoring = skillWeightages.map(skill => ({
        parameter: skill.skillName,
        weight: sanitizeWeightage(skill.weightage)
      }));

      console.log('Sending custom scoring:', customScoring);

      // Hard-coded per-request timeout to ensure it applies even in dev/prod builds
      const perRequestTimeout = 20 * 60 * 1000; // 20 minutes
      const response = await api.post(
        `/stack-ranking/position/${positionId}/jd/${selectedJdId}/analyze`,
        { customScoring: customScoring },
        { timeout: perRequestTimeout, signal: rankingController.signal }
      );
      console.log('New ranking response data:', response.data);

      // Normalize the response data to match the expected format
      // Normalize and enforce sequential ranks 1..N on new analysis
      const sourceList = response.data.candidates || response.data.rankings || [];
      let candidates = sourceList.map((candidate, index) => {
        // Parse AI analysis to extract detailed information
        const aiAnalysisData = parseAiAnalysis(candidate.ai_analysis || candidate.aiAnalysis);

        const rawEmail = candidate.email || candidate.candidate_email || candidate.candidateEmail || '';
        const safeEmail = normalizeCandidateEmailForDisplay(rawEmail, candidate);

        return {
          rank: candidate.rank || index + 1,
          name: candidate.name || candidate.candidateName || 'Unknown',
          email: safeEmail,
          mobile: candidate.mobile || candidate.phone || candidate.candidate_phone || 'Not provided',
          total_score: candidate.total_score || candidate.totalScore || candidate.match_score || 0,
          technical_score: candidate.technical_score || candidate.technicalScore,
          communication_score: candidate.communication_score || candidate.communicationScore,
          experience_score: candidate.experience_score || candidate.experienceScore,
          skills_score: candidate.skills_score || candidate.skillsScore,
          parameter_scores:
            candidate.parameter_scores ||
            candidate.parameterScores ||
            aiAnalysisData.parameter_scores ||
            null,
          ai_analysis: candidate.ai_analysis || candidate.aiAnalysis,
          resume_id: candidate.resume_id || candidate.resumeId || candidate.id,
          parameter_scores: candidate.parameterScores || candidate.parameter_scores || null,
          // Extract detailed fields from AI analysis or use direct fields as fallback
          education: aiAnalysisData.education || candidate.education || candidate.educationDetails || 'Not provided',
          skills: aiAnalysisData.skills || candidate.skills || candidate.skillSet || 'Not provided',
          matching_skills: aiAnalysisData.matching_skills || candidate.matching_skills || candidate.matchingSkills || 'Not provided',
          missing_skills: aiAnalysisData.missing_skills || candidate.missing_skills || candidate.missingSkills || 'Not provided',
          match_summary: aiAnalysisData.match_summary || candidate.match_summary || candidate.matchSummary || candidate.ai_analysis || 'Not provided',
          experience: candidate.experience || candidate.experienceYears || 'Not provided',
          current_position: candidate.current_position || candidate.currentPosition || 'Not provided',
          company: candidate.company || candidate.currentCompany || 'Not provided',
          relevant_projects: candidate.relevant_projects || candidate.relevantProjects || 'Not provided'
        };
      });
      candidates = dedupeRankingCandidatesByResumeId(candidates);
      candidates.sort((a, b) => Number(b.total_score ?? 0) - Number(a.total_score ?? 0));
      candidates = candidates.map((c, idx) => ({ ...c, rank: idx + 1 }));

      const normalizedResults = {
        job_role: position?.positionName || position?.title || position?.name || stackRankingResults?.job_title || 'Position',
        total_candidates: candidates.length,
        analysis_timestamp: new Date().toISOString(),
        candidates
      };

      setStackRankingResults(normalizedResults);
      const failedResumes = response?.data?.failed_resumes || response?.data?.failedResumes || [];
      // Nothing ranked: show everyone with basic details instead of an empty Results page.
      setAiRankingFailed(candidates.length === 0);
      setRankingFailures(Array.isArray(failedResumes) ? failedResumes : []);
      setCurrentPage(1);
      setWhatsAppStatusFilter('all');
      setSelectedRecruiters(new Set());
      goToStep(3);

      const successfulCount = Number(response?.data?.successful_count ?? candidates.length ?? 0);
      const failedCount = Number(response?.data?.failed_count ?? (Array.isArray(failedResumes) ? failedResumes.length : 0));
      if (successfulCount > 0 && failedCount > 0) {
        toast.warning(
          `Stack ranking completed for ${resumeCountLabel(successfulCount)}. ${resumeCountLabel(failedCount)} failed and were skipped.`
        );
      } else if (successfulCount > 0) {
        // Show this success toast only once per completed run, even if the user revisits/refreshes.
        let alreadyShown = false;
        try {
          alreadyShown = sessionStorage.getItem(STACK_RANKING_SUCCESS_TOAST_KEY) === '1';
        } catch (e) {
          alreadyShown = false;
        }

        if (!alreadyShown) {
          const newProcessed = Number(response?.data?.new_resumes_processed);
          const reused = Number(response?.data?.existing_resumes_reused);
          const incrementalMsg =
            response?.data?.incremental &&
            Number.isFinite(newProcessed) &&
            Number.isFinite(reused) &&
            reused > 0 &&
            newProcessed > 0
              ? `Stack ranking completed: ${resumeCountLabel(newProcessed)} newly ranked; ${resumeCountLabel(reused)} kept from the previous run.`
              : `Stack ranking completed successfully for ${resumeCountLabel(successfulCount)}. Status will be updated when you return to the Existing Positions list.`;
          toast.success(incrementalMsg, { toastId: STACK_RANKING_SUCCESS_TOAST_KEY });
          try {
            sessionStorage.setItem(STACK_RANKING_SUCCESS_TOAST_KEY, '1');
          } catch (e) {
            // ignore storage errors
          }
        }
      } else {
        const failedArr = Array.isArray(failedResumes) ? failedResumes : [];
        const joinedReasons = failedArr.map((f) => String(f?.reason || f?.message || '').toLowerCase()).join(' ');
        if (joinedReasons.includes('weightage') && joinedReasons.includes('100')) {
          toast.warning(
            'Stack ranking could not run because skill weightages do not total 100%. Go back to Skill Weightage, fix the total to 100%, then run stack ranking again.'
          );
        } else if (failedArr.length > 0) {
          const r0 = failedArr[0]?.reason || failedArr[0]?.message;
          toast.error(
            r0
              ? `No resumes could be ranked successfully. ${r0}`
              : 'No resumes could be ranked successfully. Please review failed resume details.'
          );
        } else {
          toast.error('No resumes could be ranked successfully. Please review failed resume details.');
        }
      }

    } catch (error) {
      const canceled =
        error?.code === 'ERR_CANCELED' ||
        error?.name === 'CanceledError' ||
        error?.name === 'AbortError';
      if (canceled) {
        return;
      }

      console.error('Error performing stack ranking:', error);

      // AI unavailable (expired tokens, timeout, server/network error): keep what was already ranked
      // and list the rest with basic details so outreach can continue. A 403 means Stack Ranking was
      // switched off meanwhile. 400s are input problems (e.g. weightage total) the recruiter has to
      // fix, so those stay on this step.
      const status = error.response?.status;
      if (status !== 400 && resumes.length > 0) {
        setAiRankingFailed(true);
        setCurrentPage(1);
        goToStep(3);
      }

      // Log detailed error information
      if (error.response) {
        console.error('Error response status:', error.response.status);
        console.error('Error response data:', error.response.data);
        console.error('Error response headers:', error.response.headers);

        // Show specific error message based on status code
        if (error.response.status === 400) {
          const errorMessage = error.response.data?.message || error.response.data?.reason || error.response.data?.error || 'Bad Request - Invalid parameters';
          toast.error(`Stack ranking failed: ${errorMessage}`);
        } else if (error.response.status === 408) {
          // Handle timeout errors specifically
          toast.error('Stack ranking timed out. This can happen with large numbers of resumes. Please try again or reduce the number of resumes.');
        } else if (error.response.status === 404) {
          toast.error('Stack ranking endpoint not found. Please check if the backend service is running.');
        } else if (error.response.status === 500) {
          // Prefer specific error details returned by backend/python api when available
          const serverMsg = error.response.data?.reason || error.response.data?.error || error.response.data?.detail || error.response.data?.body || error.response.data?.message;
          toast.error(serverMsg || 'Internal server error during stack ranking. Please try again later.');
        } else {
          toast.error(`Stack ranking failed with status ${error.response.status}`);
        }
      } else if (error.request) {
        console.error('Error request:', error.request);
        toast.error('Network error: Unable to connect to the server. Please check your connection.');
      } else {
        console.error('Error message:', error.message);
        toast.error(`Stack ranking failed: ${error.message}`);
      }
    } finally {
      if (processingIntervalRef.current) {
        clearInterval(processingIntervalRef.current);
        processingIntervalRef.current = null;
      }
      if (rankingAbortRef.current === rankingController) {
        rankingAbortRef.current = null;
      }
      setRankingLoading(false);
    }
  };

  const handleViewDetails = (candidate) => {
    setSelectedCandidate(candidate);
    setShowDetailsModal(true);
  };

  // Bulk WhatsApp is per recruiter: you can message only the candidates whose resume you attached
  // (the server enforces the same rule). Resumes with no recorded attacher belong to the position's creator.
  const currentUserEmail = String(user?.email || '').trim().toLowerCase();
  const candidateOwnerEmail = (candidate) => String(
    candidate?.attachedByEmail || candidate?.uploadedByEmail || candidate?.createdByEmail || position?.createdByEmail || ''
  ).trim().toLowerCase();
  const isOwnCandidate = (candidate) =>
    unrestricted || !currentUserEmail || candidateOwnerEmail(candidate) === currentUserEmail;
  const notOwnCandidateTitle = (candidate) =>
    `Attached by ${candidate?.attachedBy || candidate?.createdBy || candidate?.uploadedBy || 'another recruiter'} — only they can send WhatsApp to this candidate`;

  const handleSelectCandidate = (candidateId, candidate) => {
    if (candidate && isWhatsAppOutreachLocked(getWhatsAppStatus(candidate))) {
      toast.info('Candidate already contacted for this role.');
      return;
    }
    if (candidate && !isOwnCandidate(candidate)) {
      toast.info(notOwnCandidateTitle(candidate));
      return;
    }

    setSelectedCandidates(prev =>
      prev.includes(candidateId)
        ? prev.filter(id => id !== candidateId)
        : [...prev, candidateId]
    );
  };

  const handleSelectAll = () => {
    const eligibleCandidateIds = whatsAppFilteredCandidates
      .filter((candidate) => !isWhatsAppOutreachLocked(getWhatsAppStatus(candidate)) && isOwnCandidate(candidate))
      .map((c, index) => c.resume_id || c.rank || index);

    if (eligibleCandidateIds.length === 0) {
      toast.info('No candidates you attached are available for sending.');
      return;
    }

    if (selectedCandidates.length === eligibleCandidateIds.length) {
      setSelectedCandidates([]);
    } else {
      setSelectedCandidates(eligibleCandidateIds);
    }
  };

  // Skill editing functions
  const sanitizeWeightage = (value) => {
    const parsed = parseFloat(value);
    if (!Number.isFinite(parsed)) return 0;
    return Math.max(0, parsed);
  };

  const calculateSkillTotal = (skills) => {
    const total = skills.reduce((sum, skill) => sum + sanitizeWeightage(skill.weightage), 0);
    setSkillTotal(total);
    setIsValidTotal(Math.abs(total - 100) < 0.01);
    return total;
  };

  const hasInvalidSkillValues = Object.values(invalidSkillIndexes).some(Boolean);
  const hasNegativeSkillValues = skillWeightages.some((skill) => Number(skill.weightage) < 0);
  const hasAnyInvalidSkillValues = hasInvalidSkillValues || hasNegativeSkillValues;

  const handleSkillWeightageChange = (index, value) => {
    const updatedSkills = [...skillWeightages];

    if (String(value).includes('-')) {
      updatedSkills[index].weightage = 0;
      setInvalidSkillIndexes((prev) => ({ ...prev, [index]: true }));
      setSkillWeightages(updatedSkills);
      calculateSkillTotal(updatedSkills);
      return;
    }

    updatedSkills[index].weightage = sanitizeWeightage(value);
    setInvalidSkillIndexes((prev) => ({ ...prev, [index]: false }));
    setSkillWeightages(updatedSkills);
    calculateSkillTotal(updatedSkills);
  };

  const handleSkillNameChange = (index, value) => {
    const updatedSkills = [...skillWeightages];
    updatedSkills[index].skillName = value;
    setSkillWeightages(updatedSkills);
  };

  const handleAddSkill = () => {
    if (!newSkillName.trim() || sanitizeWeightage(newSkillWeightage) <= 0) {
      toast.error('Please enter valid skill name and weightage');
      return;
    }

    const skillExists = skillWeightages.some(
      (skill) => skill.skillName.toLowerCase() === newSkillName.toLowerCase()
    );

    if (skillExists) {
      toast.error('Skill already exists');
      return;
    }

    const newSkill = {
      skillName: newSkillName.trim(),
      weightage: sanitizeWeightage(newSkillWeightage),
    };

    const updatedSkills = [...skillWeightages, newSkill];
    setSkillWeightages(updatedSkills);
    calculateSkillTotal(updatedSkills);

    setNewSkillName('');
    setNewSkillWeightage(0);
    setShowAddSkillModal(false);
    toast.success('Skill added successfully!');
  };

  const handleDeleteSkill = async (skillName) => {
    if (!selectedJdId) {
      toast.error('No job description selected. Please add a job description before managing skills.');
      return;
    }

    if (window.confirm(`Are you sure you want to delete "${skillName}"?`)) {
      try {
        const response = await api.delete(
          `/stack-ranking/skill-weightage/jd/${selectedJdId}/skill/${encodeURIComponent(skillName)}`
        );

        if (response.data && response.data.scoring_parameters) {
          const updatedSkills = applyScoringParametersPreservingOrder(
            skillWeightages,
            response.data.scoring_parameters
          );
          setSkillWeightages(updatedSkills);
          setInvalidSkillIndexes({});
          calculateSkillTotal(updatedSkills);
          toast.success('Skill deleted successfully!');
        } else {
          const updatedSkills = skillWeightages.filter(
            (s) => s.skillName.toLowerCase() !== skillName.toLowerCase()
          );
          setSkillWeightages(updatedSkills);
          setInvalidSkillIndexes({});
          calculateSkillTotal(updatedSkills);
          toast.success('Skill deleted successfully!');
        }
      } catch (error) {
        console.error('Error deleting skill:', error);
        toast.error('Failed to delete skill');
      }
    }
  };

  const persistSkillWeightages = async ({ showToast = true } = {}) => {
    if (!selectedJdId) {
      if (showToast) toast.error('No job description selected. Please add a job description before saving skills.');
      return false;
    }

    if (hasAnyInvalidSkillValues) {
      if (showToast) toast.error('Skill weightage must be a positive value');
      return false;
    }

    if (!isValidTotal) {
      if (showToast) toast.error('Total weightage must equal 100%');
      return false;
    }

    try {
      const skillsData = skillWeightages.map((skill) => ({
        skillName: skill.skillName,
        weightage: sanitizeWeightage(skill.weightage),
      }));

      const response = await api.put(`/stack-ranking/skill-weightage/jd/${selectedJdId}`, skillsData);

      if (response.data && response.data.scoring_parameters) {
        const updatedSkills = applyScoringParametersPreservingOrder(
          skillWeightages,
          response.data.scoring_parameters
        );
        setSkillWeightages(updatedSkills);
        setInvalidSkillIndexes({});
      } else {
        setInvalidSkillIndexes({});
      }
      setEditingSkills(false);
      if (showToast) toast.success('Skills saved successfully!');
      return true;
    } catch (error) {
      console.error('Error saving skills:', error);
      if (showToast) toast.error('Failed to save skills');
      return false;
    }
  };

  const handleSaveSkills = async () => {
    await persistSkillWeightages();
  };

  // ATS (Workday / Greenhouse) import handler
  const handleAtsImport = (importedResumes) => {
    const mapped = importedResumes.map(r => ({
      resumeId:        r.resumeId,
      candidateName:   r.candidateName,
      email:           r.email,
      mobile:          r.mobile,
      fileName:        r.fileName,
      fileUrl:         r.fileUrl,
      fileSize:        r.fileSize || 0,
      atsJobId:        r.atsJobId,
      atsJobTitle:     r.atsJobTitle,
      atsSource:       r.atsSource,
      importedFromAts: true,
    }));
    setResumes(prev => {
      const existingIds = new Set(prev.map(r => r.resumeId));
      const newOnes = mapped.filter(r => !existingIds.has(r.resumeId));
      return [...prev, ...newOnes];
    });
    const count = mapped.length;
    toast.success(count ? `Added ${count} resume${count !== 1 ? 's' : ''} from Workday to the list.` : 'No resumes were added.');
  };

  const handleCancelEdit = async () => {
    setEditingSkills(false);
    setInvalidSkillIndexes({});
    if (selectedJdId) {
      const saved = await loadSavedJdSkillWeightages(api, selectedJdId);
      if (saved?.length) {
        setSkillWeightages(saved);
      } else {
        await fetchSkillWeightageForJd(selectedJdId, position, null, { forceJobFamily: true });
      }
    }
  };

  const handleContinueToUpload = async () => {
    if (editingSkills) {
      const saved = await persistSkillWeightages({ showToast: true });
      if (!saved) return;
    }
    goToStep(2);
  };

  const canProceedWithSkills = Boolean(
    selectedJdId && activeJobFamilyKey && skillWeightages.length > 0 && isValidTotal && !hasAnyInvalidSkillValues
  );

  const hasRankingResults = Boolean(
    stackRankingResults?.candidates && stackRankingResults.candidates.length > 0
  );
  // Without AI ranking (switched off or failed) the Results step is the basic candidate list.
  const showsBasicList = (!aiRankingOn || aiRankingFailed) && resultCandidates.length > 0;
  const hasResultsList = hasRankingResults || showsBasicList;

  const isStepAvailable = (stepNumber) => {
    if (stepNumber === 1) return aiRankingOn;
    if (stepNumber === 2) {
      return !aiRankingOn || canProceedWithSkills || currentStep >= 2 || resumes.length > 0 || hasRankingResults;
    }
    if (stepNumber === 3) return hasResultsList;
    return false;
  };

  const handleStepClick = (stepNumber) => {
    if (stepNumber === currentStep) return;
    if (!isStepAvailable(stepNumber)) {
      if (stepNumber === 2) {
        toast.info('Complete skill weightage (total 100%) before opening Upload Resumes.');
      } else if (stepNumber === 1) {
        toast.info(AI_RANKING_OFF_MESSAGE);
      } else if (stepNumber === 3) {
        toast.info(aiRankingOn ? 'Run stack ranking to open Results.' : 'Upload resumes to see the candidate list.');
      }
      return;
    }
    goToStep(stepNumber);
  };

  // Skill Weightage only feeds the AI ranking; with Stack Ranking off, start at Upload Resumes.
  useEffect(() => {
    if (!aiRankingOn && currentStep === 1) goToStep(2, { push: false });
  }, [aiRankingOn, currentStep, goToStep]);

  useEffect(() => {
    const fromUrl = parseStepQuery(searchParams.get('step'));
    if (fromUrl && fromUrl !== currentStep) {
      setCurrentStep(fromUrl);
      writeSavedWizardStep(positionId, fromUrl);
    }
  }, [searchParams, currentStep, positionId]);

  // CEIPAL import functions
  const handleCeipalImport = (importedResumes) => {
    const mappedResumes = importedResumes.map(resume => ({
      resumeId: resume.resume_id,
      candidateName: resume.candidate_name,
      email: resume.email,
      mobile: resume.mobile,
      fileName: resume.file_name, // Map snake_case to camelCase
      filePath: resume.file_path || '',
      fileType: resume.file_type || 'PDF',
      fileSize: resume.file_size || 0,
      ceipalApplicantId: resume.ceipal_applicant_id,
      ceipalJobId: resume.ceipal_job_id,
      importedFromCeipal: true,
      ceipalImportDate: new Date().toISOString()
    }));


    // Add imported resumes to the current resumes list
    setResumes(prevResumes => [...prevResumes, ...mappedResumes]);

    // Clear any existing stack ranking results since we have new resumes
    setStackRankingResults(null);

    const importedCount = importedResumes.length;
    const resumeLabel = importedCount === 1 ? 'resume' : 'resumes';
    const msg = importedCount
      ? `Added ${importedCount} ${resumeLabel} from CEIPAL to the list.`
      : 'No resumes were added (all selected may have failed or were already imported).';
    toast.success(msg);
  };

  const cleanText = (text) => {
    if (!text) return '';

    return text
      // Remove all asterisks from formatting
      .replace(/\*\*/g, '')
      .replace(/\*/g, '');
  };

  const formatMatchSummary = (text) => {
    if (!text) return 'Not provided';

    // Split by major sections
    const sections = text.split(/(STRENGTHS:|CONCERNS:|PARAMETER EVALUATION:)/);
    if (sections.length === 1) {
      // No special sections found, return as is
      return <span style={{ color: '#666' }}>{text}</span>;
    }

    const formattedSections = [];
    for (let i = 0; i < sections.length; i++) {
      const section = sections[i].trim();
      if (!section) continue;

      if (section === 'STRENGTHS:' || section === 'CONCERNS:' || section === 'PARAMETER EVALUATION:') {
        formattedSections.push(
          <div key={i} className="mb-2">
            <h6 className="mb-1 fw-bold" style={{ color: 'black' }}>{section}</h6>
          </div>
        );
      } else {
        // Check if this section contains parameter evaluations
        if (section.includes('(') && section.includes('%):')) {
          const lines = section.split('\n');
          const formattedLines = lines.map((line, lineIndex) => {
            if (line.includes('(') && line.includes('%):')) {
              // This is a parameter line
              const parts = line.split(':');
              if (parts.length >= 2) {
                const parameterName = parts[0].trim();
                const rest = parts.slice(1).join(':').trim();
                return (
                  <div key={lineIndex} className="mb-1">
                    <span style={{ color: 'black', fontWeight: '600' }}>{parameterName}:</span>
                    <span style={{ color: '#666' }}> {rest}</span>
                  </div>
                );
              }
            }
            return <div key={lineIndex} style={{ color: '#666' }}>{line}</div>;
          });
          formattedSections.push(<div key={i}>{formattedLines}</div>);
        } else {
          // Handle STRENGTHS and CONCERNS sections
          const lines = section.split('\n');
          const formattedLines = lines.map((line, lineIndex) => {
            const trimmedLine = line.trim();
            if (!trimmedLine) return <div key={lineIndex}><br /></div>;

            // Check if this line has a colon (indicating a heading followed by description)
            if (trimmedLine.includes(':')) {
              const colonIndex = trimmedLine.indexOf(':');
              const heading = trimmedLine.substring(0, colonIndex).trim();
              const description = trimmedLine.substring(colonIndex + 1).trim();

              return (
                <div key={lineIndex} className="mb-2">
                  <span style={{ color: 'black', fontWeight: '600' }}>{heading}:</span>
                  <span style={{ color: '#666' }}> {description}</span>
                </div>
              );
            }

            // Regular line without colon
            return <div key={lineIndex} style={{ color: '#666' }}>{line}</div>;
          });
          formattedSections.push(<div key={i}>{formattedLines}</div>);
        }
      }
    }

    return <div>{formattedSections}</div>;
  };

  const parseAiAnalysis = (aiAnalysis) => {
    if (!aiAnalysis) return {};

    try {
      // Try to parse as JSON first
      if (typeof aiAnalysis === 'string' && aiAnalysis.startsWith('{')) {
        return JSON.parse(aiAnalysis);
      }

      // If it's already an object, return it
      if (typeof aiAnalysis === 'object') {
        return aiAnalysis;
      }

      // If it's a string, try to extract structured data
      const result = {};

      // Look for common patterns in the text
      const educationMatch = aiAnalysis.match(/(?:Education|EDUCATION)[:\s]*([^]*?)(?=\n\n|\n[A-Z]|$)/i);
      if (educationMatch) result.education = educationMatch[1].trim();

      const skillsMatch = aiAnalysis.match(/(?:Skills|SKILLS)[:\s]*([^]*?)(?=\n\n|\n[A-Z]|$)/i);
      if (skillsMatch) result.skills = skillsMatch[1].trim();

      const matchingSkillsMatch = aiAnalysis.match(/(?:Matching Skills|MATCHING SKILLS)[:\s]*([^]*?)(?=\n\n|\n[A-Z]|$)/i);
      if (matchingSkillsMatch) result.matching_skills = matchingSkillsMatch[1].trim();

      const missingSkillsMatch = aiAnalysis.match(/(?:Missing Skills|MISSING SKILLS)[:\s]*([^]*?)(?=\n\n|\n[A-Z]|$)/i);
      if (missingSkillsMatch) result.missing_skills = missingSkillsMatch[1].trim();

      const matchSummaryMatch = aiAnalysis.match(/(?:Match Summary|MATCH SUMMARY|Summary|SUMMARY)[:\s]*([^]*?)(?=\n\n|\n[A-Z]|$)/i);
      if (matchSummaryMatch) result.match_summary = matchSummaryMatch[1].trim();

      return result;
    } catch (error) {
      console.error('Error parsing AI analysis:', error);
      return {};
    }
  };

  const handleJdPreview = () => {
    if (!selectedJdId) {
      toast.error('Please select a job description first');
      return;
    }

    if (selectedJd) {
      setJdPreviewContent(selectedJd);
      setShowJdPreviewModal(true);
    } else {
      console.log('Available JDs:', jdVersions.map(jd => ({ id: jd.jdId, type: typeof jd.jdId })));
      console.log('Looking for JD ID:', selectedJdId, 'Type:', typeof selectedJdId);
      toast.error('Job description not found. Please try selecting the JD again.');
    }
  };

  const handleJdEdit = () => {
    if (!selectedJdId) {
      toast.error('Please select a job description first');
      return;
    }
    setShowJdPreviewModal(false);
    setShowJdEditModal(true);
  };

  const refreshJdVersionsAfterEdit = async () => {
    try {
      const jdVersionsResponse = await api.get(`/job-descriptions/position/${positionId}/versions`);
      const versions = jdVersionsResponse.data || [];
      setJdVersions(versions);
      if (versions.length > 0) {
        const latestJd = versions[versions.length - 1];
        setSelectedJdId(latestJd.jdId);
        setJdPreviewContent(latestJd);
      }
    } catch (error) {
      console.error('Failed to refresh job descriptions after edit:', error);
    }
  };

  const steps = [
    { number: 1, title: 'Skill Weightage', description: 'Review and adjust skill weights' },
    { number: 2, title: 'Upload Resumes', description: 'Upload candidate resumes' },
    { number: 3, title: 'Results', description: 'Ranking and WhatsApp' }
  ];

  // Derived pagination metrics
  const candidatesWithCreatedBy = useMemo(
    () => attachResumeCreatedBy(resultCandidates, resumes, position),
    [resultCandidates, resumes, position]
  );

  const createdByRecruiters = useMemo(() => uniqueResumeAttachers(resumes), [resumes]);

  const createdByFilteredCandidates = useMemo(
    () => candidatesWithCreatedBy.filter((c) => matchesSsoRecruiterSet(c, selectedRecruiters)),
    [candidatesWithCreatedBy, selectedRecruiters]
  );

  const displayedResumes = useMemo(
    () => (resumes || []).filter((r) => matchesSsoRecruiterSet({
      uploadedBy: r.uploadedBy,
      uploadedByEmail: r.uploadedByEmail,
      createdBy: r.uploadedBy,
      createdByEmail: r.uploadedByEmail,
    }, selectedRecruiters)),
    [resumes, selectedRecruiters]
  );

  const whatsAppFilteredCandidates = useMemo(() => {
    const all = createdByFilteredCandidates;
    if (whatsAppStatusFilter === 'all') return all;
    return all.filter(c => {
      const s = getWhatsAppStatus(c);
      if (whatsAppStatusFilter === 'not_contacted') return ['not_sent', 'failed', 'missing-details'].includes(s);
      if (whatsAppStatusFilter === 'in_progress')   return ['processing', 'sent', 'delivered', 'interested', 'awaiting-details'].includes(s) || /^asking-q\d+$/.test(s);
      if (whatsAppStatusFilter === 'completed')     return s === 'completed';
      if (whatsAppStatusFilter === 'not_interested') return ['not-interested', 'not_interested'].includes(s);
      return true;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createdByFilteredCandidates, whatsAppStatusFilter, whatsappMessages, whatsAppStatusOverrides]);

  const totalItems = whatsAppFilteredCandidates.length;
  const totalPages = useMemo(() => {
    return totalItems === 0 ? 1 : Math.ceil(totalItems / recordsPerPage);
  }, [totalItems, recordsPerPage]);

  const paginatedCandidates = useMemo(() => {
    const start = (currentPage - 1) * recordsPerPage;
    const end = start + recordsPerPage;
    const candidates = whatsAppFilteredCandidates.slice(start, end);

    // Debug: Check for duplicate keys
    const keys = candidates.map((c, index) => c.resume_id || c.rank || index);
    const uniqueKeys = new Set(keys);
    if (keys.length !== uniqueKeys.size) {
      console.warn('Duplicate keys detected in candidates:', keys);
      console.warn('Candidates data:', candidates);
    }

    return candidates;
  }, [whatsAppFilteredCandidates, currentPage, recordsPerPage]);

  const startIndex = totalItems === 0 ? 0 : (currentPage - 1) * recordsPerPage + 1;
  const endIndex = Math.min(currentPage * recordsPerPage, totalItems);

  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  const handlePageSizeChange = (size) => {
    setRecordsPerPage(size);
    setCurrentPage(1);
  };

  // Early return after all hooks are declared
  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--ds-bg)', fontFamily: "'Plus Jakarta Sans','Inter',sans-serif" }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ width: 36, height: 36, border: '3px solid var(--ds-border)', borderTop: '3px solid #1A5FD4', borderRadius: '50%', animation: 'sr-spin 0.8s linear infinite', margin: '0 auto 12px' }} />
          <p style={{ color: 'var(--ds-muted)', fontSize: 14 }}>Loading stack ranking data...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: 'var(--ds-bg)',
      padding: '20px 0',
      fontFamily: "'Plus Jakarta Sans','Inter',sans-serif"
    }}>
      <style>{`@keyframes sr-spin{to{transform:rotate(360deg)}}`}</style>
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '0 16px' }}>
        {/* Header Section */}
        <div className="mb-4">
          <h1 style={{
            color: 'var(--ds-text)',
            fontWeight: 700,
            letterSpacing: '-0.5px',
            fontSize: '1.5rem',
            marginBottom: 12
          }}>
            Stack Ranking
          </h1>

          {/* Information Section — popups, not page navigation */}
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
            <button
              type="button"
              onClick={() => setHeaderInfoPopup('position')}
              {...infoCardHoverHandlers(true)}
              style={infoCardStyle(true)}
              title="View job position details"
            >
              <i className="fas fa-briefcase" style={{ fontSize: '1.5rem', color: '#1A5FD4', marginRight: 8 }}></i>
              <div>
                <small style={{ fontSize: '0.75rem', color: 'var(--ds-muted)', display: 'block' }}>Job Position</small>
                <span style={{ fontWeight: 700, fontSize: '0.9rem', color: 'var(--ds-text)' }}>{position?.positionName || position?.title || position?.name || 'N/A'}</span>
              </div>
            </button>
            <button
              type="button"
              onClick={() => setHeaderInfoPopup('resumes')}
              {...infoCardHoverHandlers(true)}
              style={infoCardStyle(true)}
              title="View uploaded resumes"
            >
              <i className="fas fa-file-pdf" style={{ fontSize: '1.5rem', color: '#28a745', marginRight: 8 }}></i>
              <div>
                <small style={{ fontSize: '0.75rem', color: 'var(--ds-muted)', display: 'block' }}>Resumes Received</small>
                <span style={{ fontWeight: 700, fontSize: '0.9rem', color: 'var(--ds-text)' }}>{resumes.length}</span>
              </div>
            </button>
            <button
              type="button"
              onClick={() => setHeaderInfoPopup('ranking')}
              {...infoCardHoverHandlers(true)}
              style={infoCardStyle(true)}
              title="View ranking summary"
            >
              <i className="fas fa-chart-line" style={{ fontSize: '1.5rem', color: '#ffc107', marginRight: 8 }}></i>
              <div>
                <small style={{ fontSize: '0.75rem', color: 'var(--ds-muted)', display: 'block' }}>Stack Ranking</small>
                <span style={{ fontWeight: 700, fontSize: '0.9rem', color: 'var(--ds-text)' }}>
                  {stackRankingResults ? 'Completed' :
                    (searchParams.get('step') === 'results' ? 'Completed' : 'Pending')}
                </span>
              </div>
            </button>
            <button
              type="button"
              onClick={handleJdPreview}
              disabled={!selectedJdId}
              {...infoCardHoverHandlers(!!selectedJdId)}
              style={{ ...infoCardStyle(!!selectedJdId), justifyContent: 'space-between' }}
              title="Preview job description"
            >
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <i className="fas fa-eye" style={{ fontSize: '1.5rem', color: '#17a2b8', marginRight: 8 }}></i>
                <div>
                  <small style={{ fontSize: '0.75rem', color: 'var(--ds-muted)', display: 'block' }}>JD View</small>
                  <span style={{ fontWeight: 700, fontSize: '0.9rem', color: 'var(--ds-text)' }}>Preview</span>
                </div>
              </div>
              <span
                style={{
                  borderRadius: 6, padding: '4px 8px', fontSize: '0.75rem',
                  border: `1px solid ${FEUJI_BLUE}`, color: FEUJI_BLUE,
                  background: 'transparent',
                  pointerEvents: 'none',
                }}
              >
                <i className="fas fa-eye"></i>
              </span>
            </button>
          </div>
        </div>

        {/* Step Progress Indicator */}
        <div style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            {(() => {
              const candidates = resultCandidates;
              const allWhatsAppCompleted =
                candidates.length > 0 &&
                candidates.every(c => getWhatsAppStatus(c) === 'completed');
              return steps.map((step, index) => {
              const isCompleted = step.number < currentStep
                || (step.number === 3 && allWhatsAppCompleted)
                || (step.number < 3 && hasResultsList);
              const isActive = currentStep === step.number && !isCompleted;
              const canClick = isStepAvailable(step.number);

              return (
                <React.Fragment key={step.number}>
                  <button
                    type="button"
                    onClick={() => handleStepClick(step.number)}
                    disabled={!canClick}
                    title={canClick
                      ? (currentStep === step.number ? `Current step: ${step.title}` : `Go to ${step.title}`)
                      : `${step.title} is not available yet`}
                    style={{
                      display: 'flex', alignItems: 'center',
                      background: 'transparent', border: 'none', padding: 0, textAlign: 'left',
                      cursor: canClick ? 'pointer' : 'not-allowed',
                      opacity: canClick ? 1 : 0.55,
                    }}
                    onMouseEnter={(e) => {
                      if (canClick) e.currentTarget.style.transform = 'translateY(-1px)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                    }}
                  >
                    <div style={{
                      width: 35, height: 35, borderRadius: '50%',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      color: 'white',
                      background: currentStep === step.number ? 'linear-gradient(135deg,#0041C2,#1A5FD4)' : isCompleted ? 'var(--feuji-green)' : 'var(--ds-surface)',
                      boxShadow: currentStep === step.number ? '0 4px 15px rgba(0,65,194,0.3)' : 'none',
                      border: currentStep !== step.number && !isCompleted && canClick ? `2px solid ${FEUJI_BLUE}` : '2px solid transparent',
                      transition: 'all 0.3s ease'
                    }}>
                      {isCompleted && currentStep !== step.number ? (
                        <i className="fas fa-check" style={{ fontSize: '0.9rem' }}></i>
                      ) : (
                        <span style={{ fontWeight: 700, fontSize: '0.9rem', color: isActive || currentStep === step.number ? 'white' : (canClick ? FEUJI_BLUE : 'var(--ds-muted)') }}>{step.number}</span>
                      )}
                    </div>
                    <div style={{ marginLeft: 8 }}>
                      <div style={{
                        fontWeight: 700, fontSize: '0.95rem',
                        color: isCompleted ? 'var(--feuji-green)' : (isActive || currentStep === step.number) ? 'rgb(89,144,225)' : (canClick ? 'var(--ds-text)' : 'var(--ds-muted)')
                      }}>
                        {step.title}
                      </div>
                      <div style={{ fontSize: '0.8rem', color: 'var(--ds-muted)' }}>{step.description}</div>
                    </div>
                  </button>

                  {index < steps.length - 1 && (
                    <div style={{ flex: 1, position: 'relative', margin: '0 12px' }}>
                      <div style={{
                        position: 'absolute', height: 4, width: '100%', top: '50%', left: 0,
                        transform: 'translateY(-50%)', borderRadius: 2, transition: 'all 0.3s ease',
                        background: currentStep > step.number ? 'var(--feuji-green)' : 'var(--ds-border)'
                      }} />
                    </div>
                  )}
                </React.Fragment>
              );
            });
            })()}
          </div>
        </div>

        {/* Step 1: Skill Weightage */}
        {currentStep === 1 && (
          <div style={{ borderRadius: 8, overflow: 'hidden', background: 'var(--ds-card)', boxShadow: 'var(--ds-shadow-md)' }}>
            <div style={{ background: 'linear-gradient(135deg,#0041C2,#1A5FD4)', border: 'none', padding: '1rem 0.75rem' }}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <div style={{ marginRight: 8 }}>
                  <div style={{ width: 35, height: 35, borderRadius: '50%', background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <i className="fas fa-cogs" style={{ fontSize: '1rem', color: 'white' }}></i>
                  </div>
                </div>
                <div>
                  <h6 style={{ margin: 0, color: 'white', fontWeight: 700, fontSize: 18 }}>Skill Weightage</h6>
                  <p style={{ margin: 0, color: 'rgba(255,255,255,0.75)', fontSize: 14 }}>Configure skill weights for candidate evaluation</p>
                </div>
              </div>
            </div>
            <div style={{ padding: '0.75rem' }}>
              {position && (
                <div style={{ marginBottom: 12 }}>
                  <JobFamilyTripleSelect
                    roleKey={activeJobFamilyKey}
                    readOnly={hasLinkedJobFamily}
                    idPrefix="sr-jf"
                    onRoleKeyChange={handleJobFamilyProfileChange}
                  />
                  {!activeJobFamilyKey && (
                    <p style={{ margin: '8px 0 0', fontSize: 12, color: 'var(--ds-muted)' }}>
                      Select sector, domain, and role to load skill weightages for this position.
                    </p>
                  )}
                </div>
              )}

              <div style={{ marginBottom: 8 }}>
                <h6 style={{ fontWeight: 700, color: 'var(--ds-text)', fontSize: 16, marginBottom: 4 }}>Job Description</h6>
                <p style={{ color: 'var(--ds-muted)', fontSize: 14, margin: 0 }}>
                  Using latest version: {selectedJdId ?
                    `Version ${selectedJd?.version || '1'} - ${getJdSourceTypeLabel(selectedJd)} (${formatAppDate(selectedJd?.createdAt)})`
                    : 'No JD selected'
                  }
                </p>
              </div>

              <div style={{ marginBottom: 8 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4, flexWrap: 'wrap', gap: 8 }}>
                  <h6 style={{ fontWeight: 700, fontSize: 16, margin: 0, color: 'var(--ds-text)', display: 'flex', alignItems: 'center', gap: 6 }}>
                    <i className="fas fa-balance-scale" style={{ fontSize: 14, color: '#1A5FD4' }}></i>
                    Skill Weightage (Total: {skillTotal}%)
                    {!isValidTotal && <span style={{ color: '#dc3545', fontSize: 12, marginLeft: 4 }}>⚠️ Must equal 100%</span>}
                  </h6>
                  <div>
                    {!editingSkills ? (
                      <button
                        type="button"
                        onClick={() => setEditingSkills(true)}
                        {...primaryBtnHoverHandlers(true)}
                        style={primaryBtnStyle({ borderRadius: 4, padding: '4px 8px', fontSize: '0.7rem', cursor: 'pointer' })}
                      >
                        <i className="fas fa-edit" style={{ fontSize: '0.6rem', marginRight: 4 }}></i>
                        Edit Skills
                      </button>
                    ) : (
                      <div style={{ display: 'flex', gap: 4 }}>
                        <button
                          type="button"
                          onClick={handleSaveSkills}
                          disabled={!isValidTotal || hasAnyInvalidSkillValues}
                          style={{ borderRadius: 4, padding: '4px 8px', fontWeight: 600, fontSize: '0.7rem', background: '#28a745', border: 'none', color: 'white', cursor: 'pointer', opacity: (!isValidTotal || hasAnyInvalidSkillValues) ? 0.6 : 1 }}
                        >
                          <i className="fas fa-save" style={{ fontSize: '0.6rem', marginRight: 4 }}></i>
                          Save
                        </button>
                        <button
                          type="button"
                          onClick={handleCancelEdit}
                          style={{ borderRadius: 4, padding: '4px 8px', fontWeight: 600, fontSize: '0.7rem', background: 'transparent', border: '1px solid var(--ds-border)', color: 'var(--ds-text)', cursor: 'pointer' }}
                        >
                          <i className="fas fa-times" style={{ fontSize: '0.6rem', marginRight: 4 }}></i>
                          Cancel
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                <div style={{ background: 'var(--ds-surface)', border: '1px solid var(--ds-border)', borderRadius: 8, padding: 8 }}>
                  {skillWeightages.length === 0 ? (
                    <p style={{ margin: 0, padding: 8, color: 'var(--ds-muted)', fontSize: 13 }}>
                      No skill weights found for this position.
                    </p>
                  ) : (
                    skillWeightages.map((skill, index) => (
                      <div key={index} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4, padding: 4, border: '1px solid var(--ds-border)', borderRadius: 8, background: 'var(--ds-card)', minHeight: 30, boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
                        <div style={{ flex: 1, display: 'flex', alignItems: 'center' }}>
                          {editingSkills ? (
                            <input
                              type="text"
                              style={{ minWidth: 150, borderRadius: 4, border: '1px solid var(--ds-border)', padding: '4px 8px', fontSize: '0.7rem', background: 'var(--ds-input-bg)', color: 'var(--ds-text)', marginRight: 8 }}
                              value={skill.skillName}
                              onChange={(e) => handleSkillNameChange(index, e.target.value)}
                            />
                          ) : (
                            <span style={{ fontSize: 14, color: 'var(--ds-text)', fontWeight: 500 }}>{skill.skillName}:</span>
                          )}
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          {editingSkills ? (
                            <input
                              type="number"
                              style={{ width: 50, textAlign: 'center', borderRadius: 4, border: invalidSkillIndexes[index] ? '1px solid #dc3545' : '1px solid var(--ds-border)', padding: '2px 4px', fontSize: '0.7rem', marginRight: 4, background: 'var(--ds-input-bg)', color: 'var(--ds-text)' }}
                              value={skill.weightage}
                              onChange={(e) => handleSkillWeightageChange(index, e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === '-' || e.key === 'Minus') {
                                  e.preventDefault();
                                  setInvalidSkillIndexes((prev) => ({ ...prev, [index]: true }));
                                }
                              }}
                              min="0"
                              max="100"
                            />
                          ) : (
                            <span style={{ fontSize: 14, minWidth: 30, textAlign: 'center', color: 'rgb(89,144,225)', fontWeight: 700, marginRight: 4 }}>
                              {skill.weightage}
                            </span>
                          )}
                          <span style={{ fontSize: 12, color: 'var(--ds-muted)', fontWeight: 500, marginRight: 4 }}>%</span>

                          {editingSkills && (
                            <button type="button" onClick={() => handleDeleteSkill(skill.skillName)} style={{ borderRadius: 4, padding: '2px 4px', fontSize: '0.7rem', border: '1px solid #dc3545', color: '#dc3545', background: 'transparent', cursor: 'pointer' }}>
                              <i className="fas fa-trash" style={{ fontSize: '0.6rem' }}></i>
                            </button>
                          )}
                          {editingSkills && invalidSkillIndexes[index] && (
                            <span style={{ fontSize: 10, color: '#dc3545', marginLeft: 4 }}>Must be positive</span>
                          )}
                        </div>
                      </div>
                    ))
                  )}

                  {editingSkills && hasAnyInvalidSkillValues && (
                    <div style={{ color: '#dc3545', fontSize: 12, marginTop: 4 }}>Skill weightage must be a positive value</div>
                  )}

                  {editingSkills && (
                    <div style={{ textAlign: 'center', marginTop: 8 }}>
                      <button type="button" onClick={() => setShowAddSkillModal(true)} style={{ borderRadius: 4, padding: '4px 8px', fontWeight: 600, fontSize: '0.7rem', background: '#28a745', border: 'none', color: 'white', cursor: 'pointer' }}>
                        <i className="fas fa-plus" style={{ fontSize: '0.6rem', marginRight: 4 }}></i>
                        Add New Skill
                      </button>
                    </div>
                  )}
                </div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginTop: 8 }}>
                <button
                  type="button"
                  onClick={handleBackToPositions}
                  style={{ borderRadius: 8, padding: '8px 16px', fontWeight: 600, border: '2px solid var(--ds-border)', color: 'var(--ds-text)', background: 'transparent', transition: 'all 0.3s ease', fontSize: '0.9rem', cursor: 'pointer' }}
                >
                  <i className="fas fa-arrow-left" style={{ marginRight: 8 }}></i>Back to Positions
                </button>
                <button
                  onClick={handleContinueToUpload}
                  disabled={!canProceedWithSkills}
                  {...primaryBtnHoverHandlers(canProceedWithSkills)}
                  style={primaryBtnStyle({
                    borderRadius: 8, padding: '8px 16px', fontSize: '0.9rem',
                    cursor: !canProceedWithSkills ? 'not-allowed' : 'pointer',
                    opacity: !canProceedWithSkills ? 0.55 : 1,
                  })}
                >
                  Continue to Upload Resumes
                  <i className="fas fa-arrow-right" style={{ fontSize: '0.7rem', marginLeft: 4 }}></i>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Step 2: Upload Resumes */}
        {currentStep === 2 && (
          <div style={{ borderRadius: 12, overflow: 'hidden', background: 'var(--ds-card)', boxShadow: 'var(--ds-shadow-md)' }}>
            <div style={{
              background: 'linear-gradient(135deg,#0041C2,#1A5FD4)',
              border: 'none',
              padding: '1rem'
            }}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <div style={{ marginRight: 8 }}>
                  <div style={{ width: 35, height: 35, borderRadius: '50%', background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <i className="fas fa-upload" style={{ fontSize: '1rem', color: 'white' }}></i>
                  </div>
                </div>
                <div>
                  <h5 style={{ margin: '0 0 4px', color: 'white', fontWeight: 700, fontSize: 16 }}>Upload Candidate Resumes</h5>
                  <p style={{ margin: 0, color: 'rgba(255,255,255,0.75)', fontSize: 14 }}>Upload candidate resumes for stack ranking</p>
                </div>
              </div>
            </div>
            <div style={{ padding: '1rem' }}>
              <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                <div style={{ flex: '1 1 300px', marginBottom: 12 }}>
                  <h6 style={{ fontWeight: 700, fontSize: 16, marginBottom: 8, color: 'var(--ds-text)', display: 'flex', alignItems: 'center', gap: 8 }}>
                    <i className="fas fa-cloud-upload-alt" style={{ color: '#28a745' }}></i>
                    Upload Resumes
                  </h6>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
                    <button
                      onClick={canUploadResumes ? handleFileSelect : undefined}
                      disabled={!canUploadResumes}
                      title={canUploadResumes ? undefined : (positionAccess.locked
                        ? 'This position is locked. Only managers and admins can add resumes.'
                        : 'You don\'t have permission to add resumes to this position')}
                      {...primaryBtnHoverHandlers(true)}
                      style={primaryBtnStyle({ borderRadius: 8, padding: '8px 16px', fontSize: '0.9rem', cursor: canUploadResumes ? 'pointer' : 'not-allowed', opacity: canUploadResumes ? 1 : 0.55 })}
                    >
                      <i className="fas fa-plus" style={{ marginRight: 8 }}></i>Add Resume Files
                    </button>
                    <button
                      onClick={() => setAtsImportType('workday')}
                      onMouseEnter={e => { e.currentTarget.style.background = FEUJI_GRAD_HOVER; }}
                      onMouseLeave={e => { e.currentTarget.style.background = FEUJI_GRAD; }}
                      style={primaryBtnStyle({ borderRadius: 8, padding: '8px 16px', fontSize: '0.9rem', cursor: 'pointer' })}
                    >
                      <i className="fas fa-cloud-download-alt" style={{ marginRight: 8 }}></i>Import from Workday
                    </button>
                    <button
                      onClick={() => setAtsImportType('greenhouse')}
                      onMouseEnter={e => { e.currentTarget.style.background = '#1a8a3c'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = '#24a84b'; }}
                      style={{ ...primaryBtnStyle({ borderRadius: 8, padding: '8px 16px', fontSize: '0.9rem', cursor: 'pointer' }), background: '#24a84b' }}
                    >
                      <i className="fas fa-leaf" style={{ marginRight: 8 }}></i>Import from Greenhouse
                    </button>
                    <button
                      onClick={() => setShowCeipalImport(true)}
                      onMouseEnter={(e) => {
                        if (!prefetchedJobPostings && !showCeipalImport) {
                          api.get('/ceipal/job-postings?page=1&pageSize=20').then((r) => { if (r.data?.success) setPrefetchedJobPostings(r.data); }).catch(() => {});
                        }
                        e.currentTarget.style.background = FEUJI_GRAD_HOVER;
                        e.currentTarget.style.boxShadow = '0 4px 14px rgba(0,65,194,0.4)';
                        e.currentTarget.style.transform = 'translateY(-1px)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = FEUJI_GRAD;
                        e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,65,194,0.25)';
                        e.currentTarget.style.transform = 'translateY(0)';
                      }}
                      style={primaryBtnStyle({ borderRadius: 8, padding: '8px 16px', fontSize: '0.9rem', cursor: 'pointer' })}
                    >
                      <i className="fas fa-download" style={{ marginRight: 8 }}></i>Import from CEIPAL
                    </button>
                  </div>
                  <input type="file" ref={fileInputRef} onChange={handleFileChange} accept=".pdf,.docx,.doc" multiple style={{ display: 'none' }} />
                  <p style={{ color: 'var(--ds-muted)', fontSize: '0.75rem' }}>Supported formats: PDF, DOC, DOCX | Import resumes directly from CEIPAL ATS or Workday</p>
                </div>
                <div style={{ flex: '1 1 300px', marginBottom: 12 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8, gap: 8, flexWrap: 'wrap' }}>
                    <h6 style={{ fontWeight: 700, fontSize: 16, margin: 0, color: 'var(--ds-text)', display: 'flex', alignItems: 'center', gap: 8 }}>
                      <i className="fas fa-list" style={{ color: '#1A5FD4' }}></i>
                      {uploadedResumesLabel(displayedResumes.length)}
                    </h6>
                    {selectedResumeIds.size > 0 && resumes.length > 0 && (
                      <button
                        onClick={handleBulkDeleteResumes}
                        disabled={bulkDeleteInProgress}
                        style={{ borderRadius: 6, padding: '4px 10px', fontSize: '0.78rem', border: '1px solid #dc3545', color: '#dc3545', background: 'transparent', cursor: 'pointer', fontWeight: 600 }}
                      >
                        <i className="fas fa-trash" style={{ marginRight: 4 }}></i>Delete Selected ({selectedResumeIds.size})
                      </button>
                    )}
                  </div>
                  {createdByRecruiters.length > 0 && (
                    <div style={{ marginBottom: 8 }}>
                      <RecruiterSsoDropdown
                        compact
                        recruiters={createdByRecruiters}
                        selectedEmails={selectedRecruiters}
                        onChange={(next) => setSelectedRecruiters(next)}
                      />
                    </div>
                  )}
                  {displayedResumes.length > 0 && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, paddingLeft: 2 }}>
                      <input
                        type="checkbox"
                        id="select-all-resumes"
                        checked={displayedResumes.filter(r => !String(r.resumeId).startsWith('temp-')).length > 0 &&
                          displayedResumes.filter(r => !String(r.resumeId).startsWith('temp-')).every(r => selectedResumeIds.has(r.resumeId))}
                        onChange={() => {
                          const validResumes = displayedResumes.filter(r => !String(r.resumeId).startsWith('temp-'));
                          const allSelected = validResumes.every(r => selectedResumeIds.has(r.resumeId));
                          setSelectedResumeIds(allSelected ? new Set() : new Set(validResumes.map(r => r.resumeId)));
                        }}
                        style={{ cursor: 'pointer', width: 14, height: 14 }}
                      />
                      <label htmlFor="select-all-resumes" style={{ fontSize: 12, color: 'var(--ds-muted)', margin: 0, cursor: 'pointer', userSelect: 'none' }}>Select All</label>
                    </div>
                  )}
                  <div style={{ maxHeight: 250, overflowY: 'auto', background: 'var(--ds-surface)', border: '1px solid var(--ds-border)', borderRadius: 8, padding: 8 }}>
                    {displayedResumes.length === 0 ? (
                      <div style={{ textAlign: 'center', padding: '16px 0' }}>
                        <div style={{ width: 45, height: 45, borderRadius: '50%', background: 'var(--ds-border)', margin: '0 auto 8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <i className="fas fa-file-pdf" style={{ fontSize: '1.2rem', color: 'var(--ds-muted)' }}></i>
                        </div>
                        <p style={{ color: 'var(--ds-muted)', margin: 0, fontSize: 14 }}>
                          {resumes.length === 0
                            ? 'No resumes uploaded yet'
                            : 'No resumes attached by the selected recruiter(s) for this position.'}
                        </p>
                      </div>
                    ) : (
                      <div>
                        {displayedResumes.map((resume) => {
                          const isTemp = String(resume.resumeId).startsWith('temp-');
                          const isChecked = selectedResumeIds.has(resume.resumeId);
                          const isRanked = rankedResumeIdSet.has(String(resume.resumeId));
                          const rowBackground = isChecked
                            ? 'rgba(220,53,69,0.12)'
                            : isRanked
                              ? 'rgba(0,65,194,0.10)'
                              : 'transparent';
                          return (
                            <div key={resume.resumeId} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '7px 8px', borderBottom: '1px solid var(--ds-border)', transition: 'all 0.2s ease', background: rowBackground, borderRadius: isRanked || isChecked ? 4 : 0 }} title={isRanked ? 'Already stack ranked' : 'Not ranked yet'}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 0 }}>
                                <input
                                  type="checkbox"
                                  checked={isChecked}
                                  disabled={isTemp}
                                  onChange={() => {
                                    const next = new Set(selectedResumeIds);
                                    if (next.has(resume.resumeId)) next.delete(resume.resumeId);
                                    else next.add(resume.resumeId);
                                    setSelectedResumeIds(next);
                                  }}
                                  style={{ cursor: isTemp ? 'not-allowed' : 'pointer', flexShrink: 0, width: 14, height: 14 }}
                                />
                                <i className="fas fa-file-pdf" style={{ color: '#dc3545', fontSize: '1rem', flexShrink: 0 }}></i>
                                <div style={{ minWidth: 0 }}>
                                  <span style={{ display: 'block', fontWeight: 500, color: isRanked ? '#1A365D' : 'var(--ds-text)', fontSize: 14, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{resume.fileName}</span>
                                  {(resume.uploadedBy || resume.uploadedByEmail) && (
                                    <span style={{ display: 'block', fontSize: 11, color: 'var(--ds-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                      Attached by {resume.uploadedBy || resume.uploadedByEmail}
                                    </span>
                                  )}
                                </div>
                              </div>
                              <button
                                onClick={() => handleDeleteResume(resume.resumeId)}
                                disabled={isTemp}
                                title={isTemp ? 'Upload in progress' : 'Delete'}
                                style={{ flexShrink: 0, marginLeft: 8, borderRadius: 6, padding: '4px 8px', fontSize: '0.8rem', border: '1px solid #dc3545', color: '#dc3545', background: 'transparent', cursor: isTemp ? 'not-allowed' : 'pointer', opacity: isTemp ? 0.5 : 1 }}
                              >
                                <i className="fas fa-trash"></i>
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 12 }}>
                {aiRankingOn ? (
                <button onClick={() => goToStep(1)} style={{ borderRadius: 8, padding: '8px 16px', fontWeight: 600, border: '2px solid var(--ds-border)', color: 'var(--ds-text)', background: 'transparent', transition: 'all 0.3s ease', fontSize: '0.9rem', cursor: 'pointer' }}>
                  <i className="fas fa-arrow-left" style={{ marginRight: 8 }}></i>Back to Skill Weightage
                </button>
                ) : <span />}
                {!aiRankingOn ? (
                <button
                  onClick={() => { setCurrentPage(1); goToStep(3); }}
                  disabled={resumes.length === 0 || uploadInProgress}
                  {...primaryBtnHoverHandlers(!(resumes.length === 0 || uploadInProgress))}
                  style={primaryBtnStyle({
                    borderRadius: 8, padding: '8px 16px', fontSize: '0.9rem',
                    cursor: (resumes.length === 0 || uploadInProgress) ? 'not-allowed' : 'pointer',
                    opacity: (resumes.length === 0 || uploadInProgress) ? 0.55 : 1,
                  })}
                  title={AI_RANKING_OFF_MESSAGE}
                >
                  {uploadInProgress ? 'Uploading resumes…' : (<><i className="fas fa-users" style={{ marginRight: 8 }}></i>View Candidates</>)}
                </button>
                ) : (
                <button
                  onClick={handlePerformRanking}
                  disabled={resumes.length === 0 || rankingLoading || uploadInProgress || !canProceedWithSkills}
                  {...primaryBtnHoverHandlers(!(resumes.length === 0 || rankingLoading || uploadInProgress || !canProceedWithSkills))}
                  style={primaryBtnStyle({
                    borderRadius: 8, padding: '8px 16px', fontSize: '0.9rem',
                    cursor: (resumes.length === 0 || rankingLoading || uploadInProgress) ? 'not-allowed' : 'pointer',
                    opacity: (resumes.length === 0 || rankingLoading || uploadInProgress) ? 0.55 : 1,
                  })}
                >
                  {uploadInProgress ? (
                    <><div style={{ width: 14, height: 14, border: '2px solid rgba(255,255,255,0.4)', borderTop: '2px solid white', borderRadius: '50%', animation: 'sr-spin 0.8s linear infinite', display: 'inline-block', marginRight: 8 }} />Uploading resumes…</>
                  ) : rankingLoading ? (
                    <><div style={{ width: 14, height: 14, border: '2px solid rgba(255,255,255,0.4)', borderTop: '2px solid white', borderRadius: '50%', animation: 'sr-spin 0.8s linear infinite', display: 'inline-block', marginRight: 8 }} />Analyzing…</>
                  ) : hasExistingRanking && newUnrankedResumeCount > 0 ? (
                    <><i className="fas fa-plus" style={{ marginRight: 8 }}></i>Rank {resumeCountLabel(newUnrankedResumeCount)} only</>
                  ) : (
                    <><i className="fas fa-chart-line" style={{ marginRight: 8 }}></i>Stack Rank Resumes</>
                  )}
                </button>
                )}
              </div>

              {rankingLoading && (
                <div style={{ marginTop: 16, padding: '12px 4px 4px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <span style={{ fontSize: 13, color: 'var(--ds-text)', fontWeight: 600 }}>
                      {hasExistingRanking && newUnrankedResumeCount >= 0 && rankedResumeIdSet.size > 0
                        ? (newUnrankedResumeCount > 0
                          ? `Analyzing ${resumeCountLabel(newUnrankedResumeCount)} (${resumeCountLabel(rankedResumeIdSet.size)} already ranked)…`
                          : `Refreshing ranking from ${resumeCountLabel(rankedResumeIdSet.size)} already scored…`)
                        : `Analyzing ${resumeCountLabel(resumes.length)}…`}
                    </span>
                    <span style={{ fontSize: 13, color: 'rgb(89,144,225)', fontWeight: 700 }}>
                      {Math.round(processingProgress)}%
                    </span>
                  </div>
                  <div style={{ height: 10, borderRadius: 5, background: 'var(--ds-border)', overflow: 'hidden', boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.1)' }}>
                    <div style={{
                      height: '100%',
                      borderRadius: 5,
                      background: 'linear-gradient(90deg, #0041C2, rgb(89,144,225))',
                      width: `${processingProgress}%`,
                      transition: 'width 0.4s ease',
                      boxShadow: '0 0 8px rgba(0,65,194,0.4)'
                    }} />
                  </div>
                  <p style={{ fontSize: 12, color: 'var(--ds-muted)', marginTop: 8, marginBottom: 0, textAlign: 'center' }}>
                    AI is evaluating {hasExistingRanking && newUnrankedResumeCount > 0 ? 'only the newly added resume(s)' : 'candidates'} against the job description. Already ranked resumes are kept.
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Step 3: Results */}
        {currentStep === 3 && hasResultsList && (
          <div style={{ borderRadius: 12, overflow: 'hidden', background: 'var(--ds-card)', boxShadow: 'var(--ds-shadow-md)' }}>
            <div style={{ background: 'linear-gradient(135deg,#0041C2,#1A5FD4)', border: 'none', padding: '1rem' }}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <div style={{ marginRight: 8 }}>
                  <div style={{ width: 35, height: 35, borderRadius: '50%', background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <i className="fas fa-trophy" style={{ fontSize: '1rem', color: 'white' }}></i>
                  </div>
                </div>
                <div>
                  <h5 style={{ margin: '0 0 4px', color: 'white', fontWeight: 700, fontSize: 16 }}>{hasRankingResults ? 'Stack Ranking Results' : 'Candidates'}</h5>
                  <p style={{ margin: 0, color: 'rgba(255,255,255,0.75)', fontSize: 14 }}>{hasRankingResults ? 'AI-powered resume scoring and stack ranking' : 'Basic details from the uploaded resumes'}</p>
                </div>
              </div>
            </div>
            <div style={{ padding: '1rem' }}>
              <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap', marginBottom: 8 }}>
                {[
                  { icon: 'fa-briefcase', label: 'Job Role', value: position?.positionName || stackRankingResults?.job_role || 'Position' },
                  { icon: 'fa-users', label: 'Total Candidates', value: createdByFilteredCandidates.length },
                  { icon: 'fa-calendar', label: 'Ranking Date', value: hasRankingResults ? formatAppDate(stackRankingResults?.analysis_timestamp) : 'Not ranked' }
                ].map((stat) => (
                  <div key={stat.label} style={{ flex: '1 1 150px', textAlign: 'center', padding: 8, background: 'var(--ds-surface)', border: '1px solid var(--ds-border)', borderRadius: 8, minHeight: 60, transition: 'all 0.2s ease' }}>
                    <div style={{ fontSize: '0.7rem', color: 'var(--ds-muted)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, marginBottom: 4 }}>
                      <i className={`fas ${stat.icon}`} style={{ fontSize: '0.8rem' }}></i>{stat.label}
                    </div>
                    <div style={{ fontWeight: 700, fontSize: '0.8rem', color: 'rgb(89,144,225)' }}>{stat.value}</div>
                  </div>
                ))}
              </div>

              {basicCandidateCount > 0 && (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 12, padding: '10px 12px', borderRadius: 10, border: '1px solid #F5C26B', background: 'rgba(245,194,107,0.12)' }}>
                  <span style={{ fontSize: 13, color: 'var(--ds-text)' }}>
                    <i className="fas fa-info-circle" style={{ marginRight: 8, color: '#B7791F' }} />
                    {!aiRankingOn
                      ? AI_RANKING_OFF_MESSAGE
                      : `${resumeCountLabel(basicCandidateCount)} not ranked${aiRankingFailed ? ' — AI ranking is unavailable right now' : ''}. Listed with basic details (name, email, phone); you can still send outreach.`}
                  </span>
                  {aiRankingOn && (
                    <button
                      type="button"
                      onClick={() => { goToStep(2); handlePerformRanking(); }}
                      disabled={rankingLoading || !canProceedWithSkills}
                      title={canProceedWithSkills ? 'Rank the remaining resumes' : 'Skill weightages must total 100% first'}
                      style={{ borderRadius: 6, padding: '6px 12px', border: '1px solid #1A5FD4', background: 'transparent', color: '#1A5FD4', fontWeight: 600, fontSize: 12, cursor: (rankingLoading || !canProceedWithSkills) ? 'not-allowed' : 'pointer', opacity: (rankingLoading || !canProceedWithSkills) ? 0.55 : 1 }}
                    >
                      <i className="fas fa-redo" style={{ marginRight: 6 }} />Retry ranking
                    </button>
                  )}
                </div>
              )}

              <div style={{ margin: '12px 0', padding: 12, border: '1px solid var(--ds-border)', borderRadius: 8, background: 'var(--ds-surface)' }}>
                <style>{`.msq-del{width:28px;height:28px;min-width:28px;border-radius:7px;display:flex;align-items:center;justify-content:center;cursor:pointer;transition:all 0.18s;border:1px solid var(--ds-border);background:transparent;padding:0;flex-shrink:0;}
.msq-del svg{width:13px;height:13px;stroke:var(--ds-muted);stroke-width:1.7;fill:none;}
.msq-del:hover:not(:disabled){border-color:#EF4444;background:rgba(239,68,68,0.08);}
.msq-del:hover:not(:disabled) svg{stroke:#EF4444;}
.msq-del:disabled{opacity:0.4;cursor:not-allowed;}`}</style>
                <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4 }}>Manual screening questions</div>
                <div style={{ fontSize: 12, color: 'var(--ds-muted)', marginBottom: 8 }}>
                  Asked on WhatsApp after the six standard questions, in this order. Add and Delete save immediately. Change the checkbox or answer type, then click Save. Disabled questions are skipped.
                </div>
                {manualQuestions.map((question, index) => (
                  <div key={`${question.sortOrder}-${question.text}-${index}`} style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6 }}>
                    <input
                      type="checkbox"
                      checked={question.enabled !== false}
                      disabled={manualQuestionsSaving}
                      onChange={(event) => updateManualQuestion(index, { enabled: event.target.checked })}
                      aria-label="Ask this question"
                    />
                    <span style={{ flex: 1, fontSize: 13, color: question.enabled === false ? 'var(--ds-muted)' : 'var(--ds-text)' }}>
                      {question.text}
                    </span>
                    <select
                      value={question.answerType || 'TEXT'}
                      disabled={manualQuestionsSaving}
                      onChange={(event) => updateManualQuestion(index, { answerType: event.target.value })}
                      aria-label="Answer type"
                      style={{ border: '1px solid var(--ds-border)', borderRadius: 4, padding: '2px 6px', fontSize: 12, background: 'var(--ds-card)' }}
                    >
                      <option value="YES_NO">Yes / No</option>
                      <option value="NUMBER">Number</option>
                      <option value="TEXT">Text</option>
                    </select>
                    <button
                      type="button"
                      className="msq-del"
                      title="Delete"
                      aria-label="Delete question"
                      disabled={manualQuestionsSaving}
                      onClick={() => deleteManualQuestion(index)}
                    >
                      <svg viewBox="0 0 24 24" strokeLinecap="round">
                        <polyline points="3 6 5 6 21 6" />
                        <path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6" />
                        <path d="M10 11v6M14 11v6M9 6V4h6v2" />
                      </svg>
                    </button>
                  </div>
                ))}
                <div style={{ display: 'flex', gap: 8, marginTop: 8, alignItems: 'center' }}>
                  <input
                    type="text"
                    value={manualQuestionDraft}
                    maxLength={200}
                    disabled={manualQuestionsSaving}
                    placeholder="Add a question (one line, 200 characters)"
                    onChange={(event) => setManualQuestionDraft(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') {
                        event.preventDefault();
                        addManualQuestion();
                      }
                    }}
                    style={{ flex: 1, border: '1px solid var(--ds-border)', borderRadius: 6, padding: '6px 8px', fontSize: 13 }}
                  />
                  <select
                    value={manualQuestionType}
                    disabled={manualQuestionsSaving}
                    onChange={(event) => setManualQuestionType(event.target.value)}
                    aria-label="Answer type for the new question"
                    style={{ border: '1px solid var(--ds-border)', borderRadius: 6, padding: '6px 8px', fontSize: 13, background: 'var(--ds-card)' }}
                  >
                    <option value="YES_NO">Yes / No</option>
                    <option value="NUMBER">Number</option>
                    <option value="TEXT">Text</option>
                  </select>
                  <button type="button" disabled={manualQuestionsSaving || !manualQuestionDraft.trim()} onClick={addManualQuestion} style={{ border: '1px solid var(--ds-border)', borderRadius: 6, padding: '6px 12px', background: 'var(--ds-card)', color: 'var(--ds-text)', cursor: manualQuestionsSaving || !manualQuestionDraft.trim() ? 'not-allowed' : 'pointer', fontSize: 13, opacity: manualQuestionsSaving || !manualQuestionDraft.trim() ? 0.6 : 1 }}>
                    Add
                  </button>
                  <button
                    type="button"
                    disabled={manualQuestionsSaving || !manualQuestionsDirty}
                    onClick={saveManualQuestions}
                    style={{ border: 'none', borderRadius: 6, padding: '6px 12px', background: manualQuestionsDirty ? '#0041C2' : '#9aa6c2', color: 'white', cursor: manualQuestionsDirty ? 'pointer' : 'not-allowed', fontSize: 13 }}
                  >
                    {manualQuestionsSaving ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </div>

              {rankingFailures.length > 0 && (
                <div className="mb-2 p-2 rounded" style={{ backgroundColor: '#fff3cd', border: '1px solid #ffe69c' }}>
                  <div className="fw-semibold small text-dark mb-1">
                    <i className="fas fa-exclamation-triangle me-1"></i>
                    {skippedResumesBannerTitle(rankingFailures.length)}
                  </div>
                  <div className="small text-muted">
                    {rankingFailures
                      .slice(0, 3)
                      .map(item => `${item.candidate_name || item.candidateName || 'Unknown'}: ${item.reason || 'Failed to process'}`)
                      .join(' | ')}
                    {rankingFailures.length > 3 ? ' | ...see logs for full details' : ''}
                  </div>
                </div>
              )}

              {(() => {
                const eligibleCount = whatsAppFilteredCandidates.filter((candidate) =>
                  !isWhatsAppOutreachLocked(getWhatsAppStatus(candidate)) && isOwnCandidate(candidate)
                ).length || 0;
                const allEligibleSelected = eligibleCount > 0 && selectedCandidates.length === eligibleCount;

                const thStyle = { padding: '6px 4px', fontSize: 14, fontWeight: 700, color: 'var(--ds-text)', background: 'var(--ds-surface)', borderBottom: '2px solid var(--ds-border)', whiteSpace: 'nowrap' };
                const allCands = createdByFilteredCandidates;
                const filterCounts = {
                  all:            allCands.length,
                  not_contacted:  allCands.filter(c => ['not_sent','failed','missing-details'].includes(getWhatsAppStatus(c))).length,
                  in_progress:    allCands.filter(c => {
                    const s = getWhatsAppStatus(c);
                    return ['processing','sent','delivered','interested','awaiting-details'].includes(s) || /^asking-q\d+$/.test(s);
                  }).length,
                  completed:      allCands.filter(c => getWhatsAppStatus(c) === 'completed').length,
                  not_interested: allCands.filter(c => ['not-interested','not_interested'].includes(getWhatsAppStatus(c))).length,
                };
                const filterBtns = [
                  { key: 'all',            label: 'All',           color: '#6B7280' },
                  { key: 'not_contacted',  label: 'Not Contacted', color: '#0041C2' },
                  { key: 'in_progress',    label: 'In Progress',   color: '#D97706' },
                  { key: 'completed',      label: 'Completed',     color: '#059669' },
                  { key: 'not_interested', label: 'Not Interested',color: '#DC2626' },
                ];
                return (
              <div>
                {/* WhatsApp Status filter pills + User (SSO) — this position only */}
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10, alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {filterBtns.map(fb => {
                    const active = whatsAppStatusFilter === fb.key;
                    return (
                      <button
                        key={fb.key}
                        onClick={() => { setWhatsAppStatusFilter(fb.key); setCurrentPage(1); }}
                        style={{
                          padding: '4px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600,
                          cursor: 'pointer', border: `1px solid ${active ? fb.color : 'var(--ds-border)'}`,
                          background: active ? fb.color : 'transparent',
                          color: active ? '#fff' : 'var(--ds-muted)',
                          transition: 'all 0.15s',
                        }}
                      >
                        {fb.label} <span style={{ opacity: 0.75 }}>({filterCounts[fb.key]})</span>
                      </button>
                    );
                  })}
                  </div>
                  <RecruiterSsoDropdown
                    compact
                    recruiters={createdByRecruiters}
                    selectedEmails={selectedRecruiters}
                    onChange={(next) => { setSelectedRecruiters(next); setCurrentPage(1); }}
                  />
                </div>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 0, fontSize: 12 }}>
                  <thead>
                    <tr>
                      <th style={{ ...thStyle, width: 30, textAlign: 'center' }}>
                        <input type="checkbox" checked={allEligibleSelected} onChange={handleSelectAll} disabled={eligibleCount === 0} title={eligibleCount === 0 ? 'All candidates are already contacted' : 'Select eligible candidates'} />
                      </th>
                      <th style={{ ...thStyle, width: 50, textAlign: 'center' }}>Rank</th>
                      <th style={{ ...thStyle, width: 150 }}>Name</th>
                      <th style={{ ...thStyle, width: 120 }}>Email</th>
                      <th style={{ ...thStyle, width: 100 }}>Phone</th>
                      <th style={{ ...thStyle, width: 120 }}>Attached By</th>
                      <th style={{ ...thStyle, width: 60, textAlign: 'center' }}>Score</th>
                      <th style={{ ...thStyle, width: 100, textAlign: 'center' }}>WhatsApp Status</th>
                      <th style={{ ...thStyle, width: 50, textAlign: 'center' }}>View</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedCandidates.map((candidate, index) => (
                      (() => {
                        const candidateStatus = getWhatsAppStatus(candidate);
                        const isAlreadyContacted = isWhatsAppOutreachLocked(candidateStatus);
                        const isOwn = isOwnCandidate(candidate);
                        const selectDisabled = isAlreadyContacted || !isOwn;
                        return (
                      <tr key={`candidate-${candidate.positionId || positionId}-${candidate.resume_id || candidate.rank || index}-${index}`} style={{ borderBottom: '1px solid var(--ds-border)', transition: 'all 0.2s ease', opacity: isAlreadyContacted ? 0.78 : 1, verticalAlign: 'middle' }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--ds-hover)'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}>
                        <td style={{ textAlign: 'center', padding: '4px' }}>
                          <input type="checkbox"
                            checked={selectedCandidates.includes(candidate.resume_id || candidate.rank || index)}
                            onChange={() => handleSelectCandidate(candidate.resume_id || candidate.rank || index, candidate)}
                            disabled={selectDisabled}
                            title={isAlreadyContacted ? 'Candidate already contacted for this role' : (!isOwn ? notOwnCandidateTitle(candidate) : 'Select candidate')}
                          />
                        </td>
                        <td style={{ textAlign: 'center', padding: '4px' }}>
                          {(() => {
                            const redFlag = getCriteriaRedFlag(candidate);
                            const rankBg = redFlag ? '#DC2626' : (candidate.rank <= 3 ? '#28a745' : 'rgb(89,144,225)');
                            const title = redFlag
                              ? `Criteria red flag: ≥50% NO answers (${(getLatestWhatsappMessage(candidate.mobile) || {}).criteriaNoCount ?? '?'}/${(getLatestWhatsappMessage(candidate.mobile) || {}).criteriaTotalCount ?? '?'}). Recruiter decides shortlist.`
                              : undefined;
                            if (candidate.basic_only) {
                              return <span title="Not ranked" style={{ fontSize: '0.75rem', color: 'var(--ds-muted)' }}>—</span>;
                            }
                            return (
                              <span title={title} style={{ borderRadius: 4, padding: '1px 6px', fontSize: '0.65rem', fontWeight: 700, background: rankBg, color: 'white', display: 'inline-block' }}>
                                {candidate.rank}
                              </span>
                            );
                          })()}
                        </td>
                        <td style={{ padding: '4px' }}>
                          <div style={{ fontSize: 12, color: 'var(--ds-text)' }}>{candidate.name}</div>
                        </td>
                        <td style={{ padding: '4px' }}>
                          <div style={{ fontSize: 12, color: 'var(--ds-text2)' }}>{candidate.email}</div>
                        </td>
                        <td style={{ padding: '4px' }}>
                          <div style={{ fontSize: 12, color: 'var(--ds-text2)' }}>{formatPhoneCompact(candidate.mobile) || candidate.mobile || 'N/A'}</div>
                        </td>
                        <td style={{ padding: '4px' }}>
                          <div style={{ fontSize: 12, color: 'var(--ds-text)', fontWeight: 600 }} title={candidate.attachedByEmail || candidate.createdByEmail || candidate.attachedBy || candidate.createdBy || ''}>
                            {candidate.attachedBy || candidate.createdBy || '—'}
                          </div>
                        </td>
                        <td style={{ textAlign: 'center', padding: '4px' }}>
                          {candidate.basic_only ? (
                            <div title="No AI score — basic details only" style={{ borderRadius: 6, padding: '4px 8px', fontSize: '0.7rem', background: 'var(--ds-surface)', border: '1px solid var(--ds-border)', color: 'var(--ds-muted)', fontWeight: 600, display: 'inline-block', whiteSpace: 'nowrap' }}>
                              Not ranked
                            </div>
                          ) : (
                          <div style={{ borderRadius: 6, padding: '4px 8px', fontSize: '0.8rem', background: 'rgb(89,144,225)', color: 'white', fontWeight: 600, display: 'inline-block', minWidth: 40, textAlign: 'center' }}>
                            {candidate.total_score}%
                          </div>
                          )}
                        </td>
                        <td style={{ textAlign: 'center', padding: '4px' }}>
                          {(() => {
                            const status = getWhatsAppStatus(candidate);
                            const message = getLatestWhatsappMessage(candidate.mobile);
                            const candidateAttachedMessage = candidate.whatsappMessage || null;
                            const statusDisplay = getWhatsAppStatusDisplay(status);
                            const isClickable = status !== 'not_sent';
                            return (
                              <div
                                key={`${candidate.name}-${status}`}
                                onClick={() => handleWhatsAppBadgeClick(candidate, status, message, candidateAttachedMessage)}
                                style={{ cursor: isClickable ? 'pointer' : 'default', display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 8px', borderRadius: 12, background: statusDisplay.color, color: 'white', fontSize: '0.65rem', fontWeight: 600, boxShadow: '0 1px 3px rgba(0,0,0,0.1)', transition: 'all 0.2s ease', opacity: isClickable ? 1 : 0.7, minWidth: 'fit-content', maxWidth: 120 }}
                                title={isClickable ? 'Click to view WhatsApp response details' : 'No WhatsApp message sent yet'}
                              >
                                <span style={{ fontSize: '0.8rem' }}>{statusDisplay.icon}</span>
                                <span style={{ fontSize: '0.65rem' }}>{statusDisplay.text}</span>
                              </div>
                            );
                          })()}
                        </td>

                        <td style={{ textAlign: 'center', padding: '4px' }}>
                          {!candidate.basic_only && (
                          <button onClick={() => handleViewDetails(candidate)} title="View score breakdown" style={{ width: 22, height: 22, borderRadius: '50%', border: '1px solid rgb(89,144,225)', color: 'rgb(89,144,225)', background: 'transparent', cursor: 'pointer', fontSize: '0.6rem', padding: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                            <i className="fas fa-eye"></i>
                          </button>
                          )}
                        </td>
                      </tr>
                        );
                      })()
                    ))}
                    {selectedRecruiters.size > 0 && paginatedCandidates.length === 0 && (
                      <tr>
                        <td colSpan={9} style={{ padding: '16px', textAlign: 'center', color: 'var(--ds-muted)', fontSize: 12 }}>
                          No resumes attached by the selected recruiter(s) for this position.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              </div>
                );
              })()}

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12 }}>
                <div style={{ color: 'var(--ds-muted)', fontSize: 13 }}>Selected: {selectedCandidates.length}</div>
                <div>
                  <button
                    onClick={refreshWhatsAppMessages}
                    disabled={!canRefreshWhatsAppStatus}
                    title={canRefreshWhatsAppStatus ? 'Update delivery and reply status from WhatsApp' : 'Send at least one WhatsApp message before status refresh is available'}
                    {...outlineBlueBtnHoverHandlers(canRefreshWhatsAppStatus)}
                    style={{ borderRadius: 6, padding: '4px 12px', fontSize: '0.8rem', background: 'transparent', border: `1px solid ${FEUJI_BLUE}`, color: FEUJI_BLUE, cursor: canRefreshWhatsAppStatus ? 'pointer' : 'not-allowed', opacity: canRefreshWhatsAppStatus ? 1 : 0.5, transition: 'all 0.18s ease' }}
                  >
                    <i className="fas fa-sync-alt" style={{ marginRight: 4 }}></i>Refresh Status
                  </button>
                </div>
              </div>


              {/* Pagination Controls */}
              <div className="mt-2">
                <GlobalPagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                  recordsPerPage={recordsPerPage}
                  onPageSizeChange={handlePageSizeChange}
                  totalItems={totalItems}
                  startIndex={startIndex}
                  endIndex={endIndex}
                  label="candidates"
                  size="small"
                />
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 12 }}>
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                  <button onClick={() => goToStep(2)} style={{ borderRadius: 8, padding: '8px 16px', fontWeight: 600, border: '2px solid var(--ds-border)', color: 'var(--ds-text)', background: 'transparent', transition: 'all 0.3s ease', fontSize: '0.9rem', cursor: 'pointer' }}>
                    <i className="fas fa-arrow-left" style={{ marginRight: 8 }}></i>Back to Upload
                  </button>
                  <button
                    type="button"
                    onClick={handleAddResumeToExistingRanking}
                    disabled={rankingLoading || uploadInProgress}
                    {...primaryBtnHoverHandlers(!(rankingLoading || uploadInProgress))}
                    style={primaryBtnStyle({
                      borderRadius: 8, padding: '8px 16px', fontSize: '0.9rem',
                      cursor: (rankingLoading || uploadInProgress) ? 'not-allowed' : 'pointer',
                      opacity: (rankingLoading || uploadInProgress) ? 0.55 : 1,
                    })}
                    title="Upload one or more resumes and rank only the new files"
                  >
                    <i className="fas fa-plus" style={{ marginRight: 8 }}></i>Add Resume to Ranking
                  </button>
                </div>
                <div style={{ display: 'flex', gap: 12 }}>
                  <button
                    title={canSendWhatsApp ? undefined : whatsAppBlockedReason}
                    disabled={!canSendWhatsApp || selectedCandidates.length === 0 || whatsAppSending}
                    onClick={async () => {
                      if (whatsAppSending) return;
                      setWhatsAppSending(true);
                      try {
                        const allCandidates = resultCandidates;
                        const selectedCandidateRecords = selectedCandidates
                          .map(candidateId => allCandidates.find(c => c.resume_id === candidateId || c.rank === candidateId))
                          .filter(Boolean);

                        const jobTitle = position?.title || stackRankingResults?.job_title || 'Software Engineer';
                        const firstCandidateName = selectedCandidateRecords[0]?.name || 'Candidate';
                        const selectedJdFileUrl = selectedJd?.fileUrl || '';
                        const selectedJdFilename = selectedJdFileUrl
                          ? selectedJdFileUrl.split('?')[0].split('/').pop() || 'job-description.pdf'
                          : 'job-description.pdf';

                        const validCandidates = [];
                        const invalidCandidates = [];
                        const alreadyContactedCandidates = [];

                        selectedCandidateRecords.forEach((candidate) => {
                          const currentWhatsAppStatus = getWhatsAppStatus(candidate);
                          if (isWhatsAppOutreachLocked(currentWhatsAppStatus)) {
                            alreadyContactedCandidates.push({
                              candidateKey: getCandidateKey(candidate),
                              candidateName: candidate?.name || 'Candidate',
                              status: currentWhatsAppStatus,
                              reason: 'Candidate already contacted for this role'
                            });
                            return;
                          }

                          const missing = getMissingCandidateDetails(candidate);
                          const phone = candidate?.phone || candidate?.mobile || candidate?.candidate_phone || candidate?.candidatePhone;
                          const candidateKey = getCandidateKey(candidate);

                          if (missing.length > 0) {
                            invalidCandidates.push({
                              candidateKey,
                              candidateName: candidate?.name || 'Candidate',
                              phoneNumber: phone || '',
                              reason: `Missing ${missing.join(', ')}`
                            });
                            return;
                          }

                          validCandidates.push({
                            candidateKey,
                            phoneNumber: phone,
                            candidateName: candidate?.name || 'Candidate',
                            jobTitle: jobTitle,
                            positionId: parseInt(positionId),
                            jdId: selectedJd?.jdId ? Number(selectedJd.jdId) : null
                          });
                        });

                        if (!validCandidates.length) {
                          if (alreadyContactedCandidates.length) {
                            const nextOverrides = {};
                            alreadyContactedCandidates.forEach((entry) => {
                              if (entry.candidateKey) {
                                nextOverrides[entry.candidateKey] = { status: 'already-contacted', reason: entry.reason };
                              }
                            });
                            setWhatsAppStatusOverrides((prev) => ({ ...prev, ...nextOverrides }));
                          }
                          if (invalidCandidates.length) {
                            const nextOverrides = {};
                            invalidCandidates.forEach((entry) => {
                              if (entry.candidateKey) {
                                nextOverrides[entry.candidateKey] = { status: 'missing-details', reason: entry.reason };
                              }
                            });
                            setWhatsAppStatusOverrides((prev) => ({ ...prev, ...nextOverrides }));
                          }
                          toast.error(
                            alreadyContactedCandidates.length
                              ? 'Selected candidate(s) already contacted for this role.'
                              : 'No valid candidates available for WhatsApp sending.'
                          );
                          return;
                        }

                        const to = validCandidates
                          .map(c => normalizeToE16491(c.phoneNumber))
                          .filter(Boolean);

                        const payload = {
                          integrated_number: "917396524433",
                          content_type: 'template',
                          payload: {
                            messaging_product: 'whatsapp',
                            type: 'template',
                            template: {
                              name: 'temp',
                              language: { code: 'en', policy: 'deterministic' },
                              namespace: '2f1dea0e_fd99_4fd8_98af_7fe6be2c56c3',
                              to_and_components: [
                                {
                                  to,
                                  components: {
                                    header_1: {
                                      filename: selectedJdFilename,
                                      type: "document",
                                      value: selectedJdFileUrl || "resolved-by-backend"
                                    },
                                    body_1: { type: 'text', value: firstCandidateName },
                                    body_2: { type: 'text', value: jobTitle },
                                    body_3: { type: 'text', value: 'Feuji' }
                                  }
                                }
                              ]
                            }
                          },
                          candidates: validCandidates.map(({ phoneNumber, candidateName, positionId, jdId }) => ({
                            phoneNumber,
                            candidateName,
                            jobTitle,
                            positionId,
                            jdId
                          }))
                        };

                        const res = await api.post('/whatsapp/send-bulk-template', payload);
                        const data = res?.data || {};

                        const responseResults = Array.isArray(data.results) ? data.results : [];
                        const skippedByBackend = Array.isArray(data.skippedCandidates) ? data.skippedCandidates : [];
                        const totalSent = Number(data.totalSent || 0);
                        const totalFailed = Number(data.totalFailed || 0);
                        const totalSkipped = Number(data.totalSkipped || 0);
                        const totalAlreadyContacted = Number(data.totalAlreadyContacted || 0);

                        const nextOverrides = {};
                        alreadyContactedCandidates.forEach((entry) => {
                          if (entry.candidateKey) {
                            nextOverrides[entry.candidateKey] = { status: 'already-contacted', reason: entry.reason };
                          }
                        });
                        invalidCandidates.forEach((entry) => {
                          if (entry.candidateKey) {
                            nextOverrides[entry.candidateKey] = { status: 'missing-details', reason: entry.reason };
                          }
                        });

                        validCandidates.forEach((entry) => {
                          if (!entry.candidateKey) return;
                          const result = responseResults.find(r =>
                            r?.phoneNumber === entry.phoneNumber && r?.candidateName === entry.candidateName
                          );
                          if (!result) {
                            nextOverrides[entry.candidateKey] = { status: 'failed', reason: 'No delivery status returned' };
                            return;
                          }
                          nextOverrides[entry.candidateKey] = result.success
                            ? { status: 'sent' }
                            : result.status === 'already-contacted'
                              ? { status: 'already-contacted', reason: result.reason || 'Candidate already contacted for this role' }
                              : { status: 'failed', reason: result.error || result.reason || 'Send failed' };
                        });

                        skippedByBackend.forEach((entry) => {
                          const matchedCandidate = selectedCandidateRecords.find((candidate) => {
                            const phone = candidate?.phone || candidate?.mobile || candidate?.candidate_phone || candidate?.candidatePhone;
                            return candidate?.name === entry?.candidateName || phone === entry?.phoneNumber;
                          });
                          const candidateKey = matchedCandidate ? getCandidateKey(matchedCandidate) : null;
                          if (candidateKey) {
                            nextOverrides[candidateKey] = {
                              status: 'missing-details',
                              reason: entry?.reason || 'Missing required candidate details'
                            };
                          }
                        });

                        setWhatsAppStatusOverrides((prev) => ({ ...prev, ...nextOverrides }));
                        await fetchWhatsAppMessages();

                        const totalMissing = invalidCandidates.length + totalSkipped;
                        const totalContacted = alreadyContactedCandidates.length + totalAlreadyContacted;
                        if (totalSent > 0 && totalFailed === 0 && totalMissing === 0) {
                          toast.success(`WhatsApp sent successfully to ${totalSent} candidate${totalSent > 1 ? 's' : ''}.`);
                        } else if (totalSent > 0) {
                          const detailParts = [];
                          if (totalMissing > 0) detailParts.push(`${totalMissing} candidate${totalMissing > 1 ? 's' : ''} skipped due to missing details`);
                          if (totalContacted > 0) detailParts.push(`${totalContacted} candidate${totalContacted > 1 ? 's' : ''} already contacted`);
                          if (totalFailed > 0) detailParts.push(`${totalFailed} candidate${totalFailed > 1 ? 's' : ''} failed to send`);
                          toast.warning(`WhatsApp sent to ${totalSent} candidate${totalSent > 1 ? 's' : ''}. ${detailParts.join('. ')}.`);
                        } else if (totalContacted > 0) {
                          toast.info(`${totalContacted} candidate${totalContacted > 1 ? 's are' : ' is'} already contacted for this role.`);
                        } else {
                          toast.error('No valid candidates available for WhatsApp sending.');
                        }
                      } catch (err) {
                        console.error(err);
                        const nextOverrides = {};
                        (stackRankingResults?.candidates || [])
                          .filter(c => selectedCandidates.includes(c.resume_id ?? c.rank))
                          .forEach((candidate) => {
                            const candidateKey = getCandidateKey(candidate);
                            if (candidateKey) nextOverrides[candidateKey] = { status: 'failed', reason: 'API failure' };
                          });
                        setWhatsAppStatusOverrides((prev) => ({ ...prev, ...nextOverrides }));
                        toast.error('Failed to send WhatsApp messages. Please try again.');
                      } finally {
                        setWhatsAppSending(false);
                      }
                    }}
                    style={{ borderRadius: 8, padding: '8px 16px', fontWeight: 600, background: 'var(--feuji-green)', border: 'none', color: 'white', transition: 'all 0.3s ease', fontSize: '0.9rem', cursor: (selectedCandidates.length === 0 || whatsAppSending || !canSendWhatsApp) ? 'not-allowed' : 'pointer', opacity: (selectedCandidates.length === 0 || whatsAppSending || !canSendWhatsApp) ? 0.6 : 1 }}
                  >
                    <i className="fab fa-whatsapp" style={{ marginRight: 8 }}></i>
                    {whatsAppSending ? 'Sending...' : `Send WhatsApp (${selectedCandidates.length})`}
                  </button>
                  <button
                    onClick={() => { setStackRankingResults(null); goToStep(1); }}
                    {...primaryBtnHoverHandlers(true)}
                    style={primaryBtnStyle({ borderRadius: 8, padding: '8px 16px', fontSize: '0.9rem', cursor: 'pointer' })}
                  >
                    <i className="fas fa-redo" style={{ marginRight: 8 }}></i>Start New Ranking
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* File Upload Confirmation Modal */}
        {showFileConfirmation && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1050, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
            <div style={{ background: 'var(--ds-card)', borderRadius: 12, width: '90%', maxWidth: 480, maxHeight: '90vh', boxShadow: 'var(--ds-shadow-md)', display: 'flex', flexDirection: 'column' }}>
              <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--ds-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
                <h6 style={{ margin: 0, fontWeight: 700, color: 'var(--ds-text)', fontSize: 14 }}>Confirm Resume Upload</h6>
                <button onClick={handleCancelUpload} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ds-muted)', fontSize: 18, lineHeight: 1 }}>×</button>
              </div>
              <div style={{ padding: '16px 20px', overflowY: 'auto', flex: 1, minHeight: 0 }}>
                <p style={{ color: 'var(--ds-text)', fontSize: 13, marginBottom: 12 }}>Do you want to upload the following {selectedFiles.length === 1 ? 'resume' : 'resumes'}?</p>
                <div style={{ border: '1px solid var(--ds-border)', borderRadius: 8, maxHeight: 'min(50vh, 400px)', overflowY: 'auto' }}>
                  {selectedFiles.map((file, index) => (
                    <div key={index} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', borderBottom: index < selectedFiles.length - 1 ? '1px solid var(--ds-border)' : 'none' }}>
                      <span style={{ fontSize: 13, color: 'var(--ds-text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0, flex: 1, marginRight: 8 }} title={file.name}>{file.name}</span>
                      <span style={{ background: 'rgb(89,144,225)', color: 'white', borderRadius: 4, padding: '2px 8px', fontSize: '0.75rem', fontWeight: 600, flexShrink: 0 }}>{getShortFileTypeLabel(file)}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div style={{ padding: '12px 20px', borderTop: '1px solid var(--ds-border)', display: 'flex', justifyContent: 'flex-end', gap: 8, flexShrink: 0 }}>
                <button onClick={handleCancelUpload} style={{ borderRadius: 6, padding: '6px 14px', border: '1px solid var(--ds-border)', background: 'transparent', color: 'var(--ds-text)', cursor: 'pointer', fontSize: 13 }}>Cancel</button>
                <button
                  type="button"
                  onClick={handleConfirmUpload}
                  {...primaryBtnHoverHandlers(true)}
                  style={primaryBtnStyle({ borderRadius: 6, padding: '6px 14px', cursor: 'pointer', fontSize: 13 })}
                >
                  Upload {selectedFiles.length} Resume{selectedFiles.length > 1 ? 's' : ''}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteConfirmation && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1050, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ background: 'var(--ds-card)', borderRadius: 12, width: '90%', maxWidth: 400, boxShadow: 'var(--ds-shadow-md)' }}>
              <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--ds-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h6 style={{ margin: 0, fontWeight: 700, color: 'var(--ds-text)', fontSize: 14 }}>Confirm Delete</h6>
                <button onClick={cancelDeleteResume} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ds-muted)', fontSize: 18, lineHeight: 1 }}>×</button>
              </div>
              <div style={{ padding: '20px', textAlign: 'center' }}>
                <i className="fas fa-exclamation-triangle" style={{ color: '#ffc107', fontSize: '2rem', marginBottom: 12, display: 'block' }}></i>
                <h6 style={{ color: 'var(--ds-text)' }}>Are you sure you want to delete this resume?</h6>
                <p style={{ color: 'var(--ds-muted)', fontSize: 13 }}>This action cannot be undone.</p>
              </div>
              <div style={{ padding: '12px 20px', borderTop: '1px solid var(--ds-border)', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                <button onClick={cancelDeleteResume} style={{ borderRadius: 6, padding: '6px 14px', border: '1px solid var(--ds-border)', background: 'transparent', color: 'var(--ds-text)', cursor: 'pointer', fontSize: 13 }}>Cancel</button>
                <button onClick={confirmDeleteResume} style={{ borderRadius: 6, padding: '6px 14px', background: '#dc3545', border: 'none', color: 'white', cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>
                  <i className="fas fa-trash" style={{ marginRight: 8 }}></i>Delete Resume
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Bulk Delete Confirmation Modal */}
        {showBulkDeleteConfirmation && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1050, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ background: 'var(--ds-card)', borderRadius: 12, width: '90%', maxWidth: 420, boxShadow: 'var(--ds-shadow-md)' }}>
              <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--ds-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h6 style={{ margin: 0, fontWeight: 700, color: 'var(--ds-text)', fontSize: 14 }}>Confirm Bulk Delete</h6>
                <button onClick={cancelBulkDeleteResumes} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ds-muted)', fontSize: 18, lineHeight: 1 }}>×</button>
              </div>
              <div style={{ padding: '20px', textAlign: 'center' }}>
                <i className="fas fa-exclamation-triangle" style={{ color: '#ffc107', fontSize: '2rem', marginBottom: 12, display: 'block' }}></i>
                <h6 style={{ color: 'var(--ds-text)' }}>Delete {selectedResumeIds.size} selected resume{selectedResumeIds.size !== 1 ? 's' : ''}?</h6>
                <p style={{ color: 'var(--ds-muted)', fontSize: 13 }}>This action cannot be undone.</p>
              </div>
              <div style={{ padding: '12px 20px', borderTop: '1px solid var(--ds-border)', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                <button onClick={cancelBulkDeleteResumes} style={{ borderRadius: 6, padding: '6px 14px', border: '1px solid var(--ds-border)', background: 'transparent', color: 'var(--ds-text)', cursor: 'pointer', fontSize: 13 }}>Cancel</button>
                <button onClick={confirmBulkDeleteResumes} style={{ borderRadius: 6, padding: '6px 14px', background: '#dc3545', border: 'none', color: 'white', cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>
                  <i className="fas fa-trash" style={{ marginRight: 8 }}></i>Delete {selectedResumeIds.size} Resume{selectedResumeIds.size !== 1 ? 's' : ''}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Stack ranking score breakdown (same as Score Analysis sidebar) */}
        {showDetailsModal && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1050, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
            <div style={{ background: 'var(--ds-card)', borderRadius: 12, width: '100%', maxWidth: 520, boxShadow: 'var(--ds-shadow-md)', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
              <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--ds-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
                <h6 style={{ margin: 0, fontWeight: 700, color: 'var(--ds-text)', fontSize: 14 }}>Stack Ranking Score</h6>
                <button onClick={() => setShowDetailsModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ds-muted)', fontSize: 20, lineHeight: 1 }}>×</button>
              </div>
              <div style={{ padding: '16px 20px', overflowY: 'auto', flex: 1 }}>
                {selectedCandidate && (() => {
                  const totalScore = Math.round(Number(selectedCandidate.total_score ?? 0));
                  const breakdown = buildSkillScoreBreakdown(selectedCandidate, skillWeightages);
                  const infoBox = (label, value, accent) => (
                    <div style={{ padding: 8, background: 'var(--ds-surface)', borderRadius: 6, marginBottom: 0 }}>
                      <div style={{ fontSize: 11, color: 'var(--ds-muted)', marginBottom: 2 }}>{label}</div>
                      <div style={{ fontSize: 14, fontWeight: 600, color: accent || 'var(--ds-text)' }}>{value}</div>
                    </div>
                  );
                  return (
                    <div>
                      <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--ds-text)', marginBottom: 10 }}>{selectedCandidate.name}</div>
                      <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
                        <div style={{ flex: 1 }}>{infoBox('Rank', `#${selectedCandidate.rank}`, '#1A5FD4')}</div>
                        <div style={{ flex: 1 }}>{infoBox('Total Score', `${totalScore}%`, scoreColor(totalScore))}</div>
                      </div>
                      <div style={{ border: '1px solid var(--ds-border)', borderRadius: 10, padding: '12px 14px', marginBottom: 14 }}>
                        <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--ds-muted)', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 10 }}>
                          Skill Score Breakdown
                        </div>
                        {breakdown.length === 0 ? (
                          <div style={{ fontSize: 12, color: 'var(--ds-muted)', textAlign: 'center', padding: '12px 0' }}>
                            No skill score data available. Re-run stack ranking to refresh scores.
                          </div>
                        ) : (
                          breakdown.map((sk, i) => (
                            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: i < breakdown.length - 1 ? 10 : 0 }}>
                              <span style={{ fontSize: 11, color: 'var(--ds-text2)', width: 130, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', flexShrink: 0 }} title={sk.name}>
                                {sk.name}{sk.weight > 0 ? ` (${sk.weight}%)` : ''}
                              </span>
                              <div style={{ flex: 1, height: 6, background: 'rgba(0,65,194,.08)', borderRadius: 3, overflow: 'hidden' }}>
                                <div style={{ height: '100%', borderRadius: 3, background: scoreColor(sk.score), width: `${Math.min(100, Math.max(0, sk.score))}%`, transition: 'width .6s ease' }} />
                              </div>
                              <span style={{ fontSize: 11, fontWeight: 700, color: scoreColor(sk.score), minWidth: 30, textAlign: 'right' }}>{sk.score}%</span>
                            </div>
                          ))
                        )}
                      </div>
                      {selectedCandidate.match_summary && selectedCandidate.match_summary !== 'Not provided' && (
                        <div>
                          <h6 style={{ fontWeight: 700, color: 'var(--ds-text)', marginBottom: 4, fontSize: 12 }}>Match Summary</h6>
                          <div style={{ color: 'var(--ds-text2)', fontSize: 13, whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
                            {formatMatchSummary(cleanText(selectedCandidate.match_summary))}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>
              <div style={{ padding: '12px 20px', borderTop: '1px solid var(--ds-border)', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                <button onClick={cancelBulkDeleteResumes} style={{ borderRadius: 6, padding: '6px 14px', border: '1px solid var(--ds-border)', background: 'transparent', color: 'var(--ds-text)', cursor: 'pointer', fontSize: 13 }}>Cancel</button>
                <button onClick={confirmBulkDeleteResumes} style={{ borderRadius: 6, padding: '6px 14px', background: '#dc3545', border: 'none', color: 'white', cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>
                  <i className="fas fa-trash" style={{ marginRight: 8 }}></i>Delete {selectedResumeIds.size} Resume{selectedResumeIds.size !== 1 ? 's' : ''}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Candidate Details Modal */}
        {showDetailsModal && selectedCandidate && (
          <CandidateReportModal
            candidate={selectedCandidate}
            skillWeightages={skillWeightages}
            rankingDate={stackRankingResults?.analysis_timestamp}
            totalCandidates={stackRankingResults?.total_candidates}
            onClose={() => setShowDetailsModal(false)}
          />
        )}

        {showJdEditModal && (
          <JdPreview
            asModal
            onClose={() => setShowJdEditModal(false)}
            onSaved={refreshJdVersionsAfterEdit}
            positionName={position?.positionName || position?.title || position?.name}
          />
        )}

        {headerInfoPopup === 'position' && (
          <HeaderInfoPopup title="Job Position" icon="fa-briefcase" onClose={() => setHeaderInfoPopup(null)}>
            {popupDetailRow('Job Role', position?.positionName || position?.title || position?.name)}
            {popupDetailRow('Client', position?.clientName)}
            {popupDetailRow('Job Code', position?.jobCode)}
            {popupDetailRow('Status', formatPositionStatusLabel(position || {}))}
            {popupDetailRow('Created On', formatAppDate(position?.createdAt))}
            {popupDetailRow('Created By', position?.createdBy || position?.createdByName || position?.createdByEmail)}
            {popupDetailRow('JD Version', selectedJd ? `Version ${selectedJd.version || '1'} — ${getJdSourceTypeLabel(selectedJd)}` : '—')}
            {popupDetailRow('Resumes', String(resumes.length))}
          </HeaderInfoPopup>
        )}

        {headerInfoPopup === 'resumes' && (
          <HeaderInfoPopup title={`Resumes Received (${resumes.length})`} icon="fa-file-pdf" onClose={() => setHeaderInfoPopup(null)}>
            {resumes.length === 0 ? (
              <p style={{ margin: 0, color: 'var(--ds-muted)', fontSize: 13, textAlign: 'center', padding: '12px 0' }}>No resumes uploaded yet.</p>
            ) : (
              <div style={{ border: '1px solid var(--ds-border)', borderRadius: 8, overflow: 'hidden' }}>
                {resumes.map((resume, index) => (
                  <div
                    key={resume.resumeId || index}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      gap: 10,
                      padding: '8px 12px',
                      borderBottom: index < resumes.length - 1 ? '1px solid var(--ds-border)' : 'none',
                      background: index % 2 ? 'var(--ds-surface)' : 'transparent',
                    }}
                  >
                    <span style={{ fontSize: 13, color: 'var(--ds-text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={resume.fileName || resume.candidateName}>
                      {resume.candidateName || resume.fileName || `Resume ${index + 1}`}
                    </span>
                    <span style={{ fontSize: 11, color: 'var(--ds-muted)', flexShrink: 0 }}>
                      {rankedResumeIdSet.has(String(resume.resumeId)) ? 'Ranked' : 'Uploaded'}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </HeaderInfoPopup>
        )}

        {headerInfoPopup === 'ranking' && (
          <HeaderInfoPopup title="Stack Ranking" icon="fa-chart-line" onClose={() => setHeaderInfoPopup(null)}>
            {popupDetailRow('Status', stackRankingResults ? 'Completed' : 'Pending')}
            {popupDetailRow('Candidates ranked', String(stackRankingResults?.candidates?.length || stackRankingResults?.total_candidates || 0))}
            {popupDetailRow('Ranking date', formatAppDate(stackRankingResults?.analysis_timestamp))}
            {stackRankingResults?.candidates?.length > 0 ? (
              <div style={{ marginTop: 12 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--ds-muted)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '.06em' }}>Top candidates</div>
                <div style={{ border: '1px solid var(--ds-border)', borderRadius: 8, overflow: 'hidden' }}>
                  {stackRankingResults.candidates.slice(0, 8).map((c, i) => (
                    <div
                      key={c.resume_id || c.rank || i}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '8px 12px',
                        borderBottom: i < Math.min(stackRankingResults.candidates.length, 8) - 1 ? '1px solid var(--ds-border)' : 'none',
                      }}
                    >
                      <span style={{ fontSize: 13, color: 'var(--ds-text)' }}>
                        #{c.rank || i + 1} {c.name || c.candidateName || 'Candidate'}
                      </span>
                      <span style={{ fontSize: 13, fontWeight: 700, color: scoreColor(Number(c.total_score ?? 0)) }}>
                        {Math.round(Number(c.total_score ?? 0))}%
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <p style={{ margin: '12px 0 0', color: 'var(--ds-muted)', fontSize: 13 }}>Ranking is not completed yet.</p>
            )}
          </HeaderInfoPopup>
        )}

        {/* JD Preview Modal */}
        {showJdPreviewModal && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1050, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
            <div style={{ background: 'var(--ds-card)', borderRadius: 12, width: '100%', maxWidth: 720, boxShadow: 'var(--ds-shadow-md)', display: 'flex', flexDirection: 'column', maxHeight: '90vh' }}>
              <div style={{ background: 'linear-gradient(135deg,#0041C2,#1A5FD4)', borderRadius: '12px 12px 0 0', padding: '14px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <i className="fas fa-file-alt" style={{ color: 'white' }}></i>
                  <span style={{ fontWeight: 700, color: 'white', fontSize: 14 }}>Job Description Preview</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
                  {jdPreviewContent && (
                    <span style={{ color: 'rgba(255,255,255,0.85)', fontSize: 12, fontWeight: 600 }}>
                      Version {jdPreviewContent.version} - {getJdSourceTypeLabel(jdPreviewContent)} ({formatAppDate(jdPreviewContent.createdAt)})
                    </span>
                  )}
                  <button
                    onClick={handleJdEdit}
                    style={{
                      borderRadius: 6, padding: '5px 12px', fontWeight: 700, fontSize: 12,
                      border: '1px solid rgba(255,255,255,0.45)', background: 'rgba(255,255,255,0.16)',
                      color: 'white', cursor: 'pointer', display: 'inline-flex', alignItems: 'center',
                    }}
                    title="Edit Job Description"
                  >
                    <i className="fas fa-edit" style={{ marginRight: 6 }}></i>Edit
                  </button>
                  <button onClick={() => setShowJdPreviewModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.8)', fontSize: 20, lineHeight: 1 }}>×</button>
                </div>
              </div>
              <div style={{ padding: '16px 20px', overflowY: 'auto', flex: 1 }}>
                {jdPreviewContent ? (
                  <div>
                    <div style={{ marginBottom: 12 }}>
                      <h6 style={{ fontWeight: 700, fontSize: 13, color: 'var(--ds-text)', display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                        <i className="fas fa-briefcase" style={{ color: '#1A5FD4' }}></i>Job Description Content
                      </h6>
                      <div style={{ background: 'var(--ds-surface)', border: '1px solid var(--ds-border)', borderRadius: 6, minHeight: 200, padding: 12 }}>
                        {jdPreviewContent.jdText ? (
                          <>
                            <style>{JD_PREVIEW_INNER_CSS}</style>
                            <div
                              className="jd-preview-content"
                              style={JD_PREVIEW_CONTENT_STYLE}
                              dangerouslySetInnerHTML={{ __html: formatJdPreviewHtml(jdPreviewContent.jdText) }}
                            />
                          </>
                        ) : (
                          <div style={{ textAlign: 'center', padding: '24px 0' }}>
                            <i className="fas fa-file-alt" style={{ color: 'var(--ds-muted)', fontSize: '2rem', display: 'block', marginBottom: 8 }}></i>
                            <p style={{ color: 'var(--ds-muted)', margin: 0, fontSize: 13 }}>No content available for this job description</p>
                          </div>
                        )}
                      </div>
                    </div>
                    {jdPreviewContent.skills && jdPreviewContent.skills.length > 0 && (
                      <div style={{ marginBottom: 12 }}>
                        <h6 style={{ fontWeight: 700, fontSize: 13, color: 'var(--ds-text)', marginBottom: 8 }}>
                          <i className="fas fa-cogs" style={{ color: '#28a745', marginRight: 8 }}></i>Required Skills
                        </h6>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                          {jdPreviewContent.skills.map((skill, index) => (
                            <span key={index} style={{ borderRadius: 6, padding: '4px 12px', fontSize: '0.8rem', background: 'var(--ds-surface)', border: '1px solid var(--ds-border)', color: 'var(--ds-text)' }}>{skill}</span>
                          ))}
                        </div>
                      </div>
                    )}
                    {jdPreviewContent.experience && (
                      <div style={{ marginBottom: 12 }}>
                        <h6 style={{ fontWeight: 700, fontSize: 13, color: 'var(--ds-text)', marginBottom: 8 }}><i className="fas fa-clock" style={{ color: '#ffc107', marginRight: 8 }}></i>Experience Required</h6>
                        <div style={{ padding: 8, background: 'rgba(255,193,7,0.1)', border: '1px solid rgba(255,193,7,0.3)', borderRadius: 6 }}>
                          <span style={{ color: '#856404', fontWeight: 500, fontSize: 13 }}>{jdPreviewContent.experience}</span>
                        </div>
                      </div>
                    )}
                    {jdPreviewContent.location && (
                      <div style={{ marginBottom: 12 }}>
                        <h6 style={{ fontWeight: 700, fontSize: 13, color: 'var(--ds-text)', marginBottom: 8 }}><i className="fas fa-map-marker-alt" style={{ color: '#dc3545', marginRight: 8 }}></i>Location</h6>
                        <div style={{ padding: 8, background: 'rgba(220,53,69,0.1)', border: '1px solid rgba(220,53,69,0.3)', borderRadius: 6 }}>
                          <span style={{ color: '#721c24', fontWeight: 500, fontSize: 13 }}>{jdPreviewContent.location}</span>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div style={{ textAlign: 'center', padding: '24px 0' }}>
                    <i className="fas fa-exclamation-triangle" style={{ color: '#ffc107', fontSize: '2rem', display: 'block', marginBottom: 8 }}></i>
                    <p style={{ color: 'var(--ds-muted)', margin: 0, fontSize: 13 }}>No job description content available</p>
                  </div>
                )}
              </div>
              <div style={{ padding: '12px 20px', borderTop: '1px solid var(--ds-border)', display: 'flex', justifyContent: 'flex-end', flexShrink: 0 }}>
                <button
                  onClick={() => setShowJdPreviewModal(false)}
                  {...primaryBtnHoverHandlers(true)}
                  style={primaryBtnStyle({ borderRadius: 6, padding: '6px 14px', cursor: 'pointer', fontSize: 13 })}
                >
                  <i className="fas fa-times" style={{ marginRight: 8 }}></i>Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* WhatsApp Message Details Modal */}
        {showMessageModal && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1050, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
            <div style={{ background: 'var(--ds-card)', borderRadius: 12, width: '100%', maxWidth: 680, boxShadow: 'var(--ds-shadow-md)', display: 'flex', flexDirection: 'column', maxHeight: '90vh' }}>
              <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--ds-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{ width: 25, height: 25, borderRadius: '50%', background: 'var(--ds-surface)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <i className="fab fa-whatsapp" style={{ color: '#25d366', fontSize: '0.8rem' }}></i>
                  </div>
                  <span style={{ fontWeight: 700, color: 'var(--ds-text)', fontSize: 14 }}>WhatsApp Response Details</span>
                </div>
                <button onClick={() => setShowMessageModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ds-muted)', fontSize: 20, lineHeight: 1 }}>×</button>
              </div>
              <div style={{ padding: '16px 20px', overflowY: 'auto', flex: 1 }}>
                {selectedMessage && (() => {
                  const infoBox = (label, value) => (
                    <div style={{ padding: 8, background: 'var(--ds-surface)', borderRadius: 6 }}>
                      <div style={{ fontSize: 11, color: 'var(--ds-muted)', marginBottom: 2 }}>{label}</div>
                      <div style={{ fontWeight: 600, color: 'var(--ds-text)', fontSize: 14 }}>{value}</div>
                    </div>
                  );
                  const qaBox = (label, val) => (
                    <div style={{ padding: 8, borderRadius: 6, background: val ? 'rgba(40,167,69,0.08)' : 'var(--ds-surface)', border: '1px solid var(--ds-border)' }}>
                      <div style={{ fontSize: 11, color: 'var(--ds-muted)', marginBottom: 2 }}>{label}</div>
                      <div style={{ fontWeight: 600, color: val ? '#155724' : 'var(--ds-muted)', fontSize: 13 }}>
                        {normalizeCandidateDetail(val) || <em style={{ fontSize: '0.8rem' }}>N/A</em>}
                      </div>
                    </div>
                  );
                  const statusLabel = selectedMessage.status === 'completed' ? '🎉 COMPLETED' : selectedMessage.status === 'interested' ? '🎯 INTERESTED' : selectedMessage.status === 'awaiting-details' ? '⏳ AWAITING DETAILS' : selectedMessage.status === 'not-interested' ? '🚫 NOT INTERESTED' : selectedMessage.status === 'delivered' ? '✅ DELIVERED' : selectedMessage.status === 'sent' ? '📤 SENT' : selectedMessage.status === 'processing' ? '⏳ PROCESSING' : selectedMessage.status === 'failed' ? '❌ FAILED' : selectedMessage.status.replace('_', ' ').toUpperCase();
                  return (
                    <div>
                      <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                        <div style={{ flex: 1 }}>{infoBox('Candidate Name', selectedMessage.candidateName || 'N/A')}</div>
                        <div style={{ flex: 1 }}>{infoBox('Phone Number', selectedMessage.phoneNumber)}</div>
                      </div>
                      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                        <div style={{ flex: 1 }}>{infoBox('Job Title', selectedMessage.jobTitle || 'Software Engineer')}</div>
                        <div style={{ flex: 1, padding: 8, background: 'var(--ds-surface)', borderRadius: 6 }}>
                          <div style={{ fontSize: 11, color: 'var(--ds-muted)', marginBottom: 4 }}>Status</div>
                          <span style={{ borderRadius: 8, padding: '4px 12px', fontSize: '0.8rem', fontWeight: 700, background: `var(--bs-${getWhatsAppStatusColor(selectedMessage.status)}, #6c757d)`, color: 'white', display: 'inline-block' }}>{statusLabel}</span>
                        </div>
                      </div>
                      <hr style={{ border: 'none', borderTop: '1px solid var(--ds-border)', margin: '12px 0' }} />
                      <h6 style={{ fontWeight: 700, color: 'var(--ds-text)', marginBottom: 12 }}>
                        <i className="fas fa-list-ul" style={{ marginRight: 8 }}></i>
                        {selectedMessage.status === 'completed' || selectedMessage.status === 'not-interested' || selectedMessage.status === 'not_interested' ? 'Candidate Response Details' : selectedMessage.status?.startsWith('asking-') ? `Questionnaire In Progress — ${selectedMessage.status.replace('asking-', 'Q').toUpperCase()} Pending` : 'Candidate Response Details'}
                      </h6>
                      <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                        <div style={{ flex: 1 }}>{qaBox('Q1 · Total Years of Experience', selectedMessage.experienceYears)}</div>
                        <div style={{ flex: 1 }}>{qaBox('Q2 · Current Location', selectedMessage.currentLocation)}</div>
                      </div>
                      <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                        <div style={{ flex: 1 }}>{qaBox('Q3 · Willing to Relocate', selectedMessage.willingToRelocate)}</div>
                        <div style={{ flex: 1 }}>{qaBox('Q4 · Current CTC (LPA)', selectedMessage.currentCtc)}</div>
                      </div>
                      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                        <div style={{ flex: 1 }}>{qaBox('Q5 · Expected CTC (LPA)', selectedMessage.expectedCtc)}</div>
                        <div style={{ flex: 1 }}>{qaBox('Q6 · Notice Period', selectedMessage.noticePeriod)}</div>
                      </div>
                      {(selectedMessage.criteriaRedFlag || selectedMessage.criteria_red_flag) && (
                        <div style={{ marginTop: 10, marginBottom: 8, padding: '8px 10px', borderRadius: 8, background: 'rgba(220,38,38,.08)', border: '1px solid rgba(220,38,38,.35)', color: '#DC2626', fontSize: 12, fontWeight: 600 }}>
                          Criteria red flag — {selectedMessage.criteriaNoCount ?? selectedMessage.criteria_no_count ?? '?'} NO of {selectedMessage.criteriaTotalCount ?? selectedMessage.criteria_total_count ?? '?'} Yes/No criteria (≥50%). Shortlist remains a recruiter decision.
                        </div>
                      )}
                      {(() => {
                        let manualAnswers = [];
                        try {
                          const raw = selectedMessage.manualAnswers;
                          manualAnswers = typeof raw === 'string' ? JSON.parse(raw) : (Array.isArray(raw) ? raw : []);
                        } catch (parseError) {
                          manualAnswers = [];
                        }
                        if (!Array.isArray(manualAnswers) || manualAnswers.length === 0) return null;
                        return manualAnswers.map((item, index) => (
                          <div key={`manual-answer-${index}`} style={{ marginBottom: 8 }}>
                            {qaBox(`Q${7 + index} · ${item.question || 'Recruiter question'}`, item.answer)}
                          </div>
                        ));
                      })()}
                      {selectedMessage.status === 'not-interested' && (
                        <div style={{ padding: '10px 14px', background: 'rgba(255,193,7,0.1)', border: '1px solid rgba(255,193,7,0.4)', borderRadius: 8, marginBottom: 8, color: '#856404', fontSize: 13 }}>
                          <i className="fas fa-info-circle" style={{ marginRight: 8 }}></i>Candidate declined the opportunity.
                        </div>
                      )}
                      {selectedMessage.status === 'awaiting-details' && (
                        <div style={{ padding: '10px 14px', background: 'rgba(23,162,184,0.1)', border: '1px solid rgba(23,162,184,0.4)', borderRadius: 8, marginBottom: 8, color: '#0c5460', fontSize: 13 }}>
                          <i className="fas fa-clock" style={{ marginRight: 8 }}></i>Candidate is interested — waiting for their detailed response.
                        </div>
                      )}
                      <hr style={{ border: 'none', borderTop: '1px solid var(--ds-border)', margin: '12px 0' }} />
                      <div style={{ display: 'flex', gap: 8 }}>
                        <div style={{ flex: 1, padding: 8, background: 'var(--ds-surface)', borderRadius: 6 }}>
                          <div style={{ fontSize: 11, color: 'var(--ds-muted)', marginBottom: 2 }}>Sent At</div>
                          <div style={{ fontWeight: 700, color: 'var(--ds-text)', fontSize: 13 }}>{formatIndiaDateTime(selectedMessage.sentAt || selectedMessage.createdAt)}</div>
                        </div>
                        <div style={{ flex: 1, padding: 8, background: 'var(--ds-surface)', borderRadius: 6 }}>
                          <div style={{ fontSize: 11, color: 'var(--ds-muted)', marginBottom: 2 }}>Details Received</div>
                          <div style={{ fontWeight: 700, color: 'var(--ds-text)', fontSize: 13 }}>{formatIndiaDateTime(selectedMessage.respondedAt)}</div>
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </div>
              <div style={{ padding: '12px 20px', borderTop: '1px solid var(--ds-border)', display: 'flex', justifyContent: 'flex-end', flexShrink: 0 }}>
                <button onClick={() => setShowMessageModal(false)} style={{ borderRadius: 6, padding: '6px 14px', border: '1px solid var(--ds-border)', background: 'transparent', color: 'var(--ds-text)', cursor: 'pointer', fontSize: 13 }}>Close</button>
              </div>
            </div>
          </div>
        )}



      </div>

      {/* ATS Import Modal (Workday + Greenhouse) */}
      <ATSImportModal
        show={atsImportType !== null}
        onHide={() => setAtsImportType(null)}
        onImportResumes={handleAtsImport}
        positionId={positionId}
        atsType={atsImportType || 'workday'}
      />

      {/* CEIPAL Import Modal */}
      <CeipalImportModal
        show={showCeipalImport}
        onHide={() => setShowCeipalImport(false)}
        onImportResumes={handleCeipalImport}
        positionId={positionId}
        prefetchedJobPostings={prefetchedJobPostings}
        onPrefetchConsumed={() => setPrefetchedJobPostings(null)}
      />

      {showAddSkillModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1050, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: 'var(--ds-card)', borderRadius: 12, width: '90%', maxWidth: 400, boxShadow: 'var(--ds-shadow-md)' }}>
            <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--ds-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h6 style={{ margin: 0, fontWeight: 700, color: 'var(--ds-text)', fontSize: 14 }}>Add New Skill</h6>
              <button type="button" onClick={() => setShowAddSkillModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ds-muted)', fontSize: 20, lineHeight: 1 }}>×</button>
            </div>
            <div style={{ padding: '16px 20px' }}>
              <div style={{ marginBottom: 12 }}>
                <label style={{ fontSize: 13, color: 'var(--ds-text)', display: 'block', marginBottom: 4 }}>Skill Name</label>
                <input type="text" placeholder="Enter skill name" value={newSkillName} onChange={(e) => setNewSkillName(e.target.value)} style={{ width: '100%', padding: '6px 10px', borderRadius: 6, border: '1px solid var(--ds-border)', background: 'var(--ds-input-bg)', color: 'var(--ds-text)', fontSize: 13, boxSizing: 'border-box' }} />
              </div>
              <div>
                <label style={{ fontSize: 13, color: 'var(--ds-text)', display: 'block', marginBottom: 4 }}>Weightage (%)</label>
                <input type="number" placeholder="Enter weightage" value={newSkillWeightage} onChange={(e) => setNewSkillWeightage(sanitizeWeightage(e.target.value))} onKeyDown={(e) => { if (e.key === '-' || e.key === 'Minus') e.preventDefault(); }} min="0" max="100" style={{ width: '100%', padding: '6px 10px', borderRadius: 6, border: '1px solid var(--ds-border)', background: 'var(--ds-input-bg)', color: 'var(--ds-text)', fontSize: 13, boxSizing: 'border-box' }} />
              </div>
            </div>
            <div style={{ padding: '12px 20px', borderTop: '1px solid var(--ds-border)', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button type="button" onClick={() => setShowAddSkillModal(false)} style={{ borderRadius: 6, padding: '6px 14px', border: '1px solid var(--ds-border)', background: 'transparent', color: 'var(--ds-text)', cursor: 'pointer', fontSize: 13 }}>Cancel</button>
              <button
                type="button"
                onClick={handleAddSkill}
                {...primaryBtnHoverHandlers(true)}
                style={primaryBtnStyle({ borderRadius: 6, padding: '6px 14px', cursor: 'pointer', fontSize: 13 })}
              >
                Add Skill
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StackRanking;
